import { supabase } from './supabaseClient';
import { applyCustomerOverride, hasCustomerOverride } from './customerOverride';
import {
    AdvanceDeduction,
    Customer,
    CustomerOverride,
    DocumentType,
    ExternalAdvance,
    Invoice,
    InvoiceConfig,
    InvoiceLine,
    InvoiceProjectRef,
    InvoiceRelationType,
    InvoiceStatus,
    InvoiceSection,
    LinkedProjectConfig,
    ProjectCheckResult,
    Report,
    ReportMetadata,
    InvoiceTotals,
    PriorRetention,
    ServiceItem,
    ServiceTransaction,
} from '../types';
import {
    applyAncillaryCosts,
    applyDocumentDiscount,
    applyInvoiceSections,
    applyStickyAddressExtras,
    applyStickyCustomerOverride,
    applyStickyCostCenter,
    applyStickyProjectName,
    canIssueFrom,
    canTransitionInvoice,
    componentLineIssues,
    computeCumulativePositions,
    computeDueDate,
    computeOpenPositions,
    cumulativeDeliveryStart,
    flatRateIssues,
    DEFAULT_COST_CENTER_LABEL,
    DocumentDiscount,
    documentDiscountOf,
    effectiveExcludedServiceIds,
    effectiveRetentionPercent,
    RetentionTotals,
    findCompetingDrafts,
    hasBlockingErrors,
    invoiceProjectRefs,
    LastUsedInvoiceDefaults,
    missingUsedIssues,
    nextInvoicePosNumbers,
    performanceProofProjectName,
    performedSpanFromLog,
    printedServicePeriod,
    manualCumulativeStart,
    ProofFilterSource,
    proofExcludedServiceIds,
    proofFilterRange,
    recalculateLines,
    reservedQtyOfLine,
    resolveSeries,
    skontoPaymentTermsText,
    typeDigitFromProjectNumber,
    undatedOpenSummary,
    UndatedSummary,
    validateInvoice,
    ValidationMessage,
    wantsStickyAddressExtras,
    wantsStickyCustomerOverride,
    wantsStickyCostCenter,
    wantsStickyProjectName,
} from './invoiceLogic';
import { fetchLastUsedInvoiceDefault } from './invoiceCostCenters';
import type { DeductionSource } from './datev/datevExport';
import { nextDocumentNumber } from './documentNumberService';
import { formatCents } from './money';
import { sha256Hex, assertCryptoAvailable } from './hash';
import { fetchReportHistoryWithChildren, loadProjectById, saveProjectToCloud } from './cloudService';
import { getNormalizedServices } from './projectLv';
import { ladeAlleSeiten } from './seitenweise';
import { ProjectLock, projectWriteBlockedMessage } from './projectLockLogic';
import { fetchProjectLock } from './projectLockService';
import { buchungstag, isAccountingEntry, isBackfilledAltEntry } from './serviceLogUtils';
import { loadJournal, mirrorEntriesToJournal, type JournalMirrorRequest, type ServiceJournalRow } from './serviceJournal';
import { collectRemovedEntryIds, removeServiceLogEntries } from './serviceLogRemoval';
import { allocatedByEntry, billingPeriodOf, ohneVerzichteteEintraege, planAllocations } from './allocationLogic';
import { STANDARD_TOR_DEPS, schattenAmTor, type SchattenAmTorEingang } from './allocationGateShadow';
import { nurDurchgereicht, torAmTor } from './allocationGate';
import { fetchTorNotschalter } from './maintenanceService';
import type { TorModus, TorNotschalterStand } from './torNotschalter';
import { STANDARD_ENTWURF_DEPS, entwurfAusDemJournal, type EntwurfDeps } from './entwurfAusJournal';
import { ProjektInBearbeitungError, meldeAusstellSperre, type ProjektcheckSperrGrund } from './ausstellSperren';
import { isFlatRateService } from './flatRateLogic';
import { flatLineDescription, flatLineMonthKeys, flatMonthProofSummary } from './flatProofSummary';
import {
    AdvanceRequest,
    buildAdvanceLine,
    deductionsFromAdvances,
    deductionsFromPriorInvoices,
    DEDUCTIBLE_ADVANCE_STATUSES,
    advanceNetCents,
    invoicedQuantityByService,
    isDeductibleAdvance,
    isDeductiblePriorInvoice,
    priorRetentionsFromDeductions,
    priorRetentionTotal,
    validateAdvanceChain,
} from './advanceInvoiceLogic';
import { fetchRetentionBackfill } from './retentionBackfill';
import {
    altBookedServiceIds,
    altCoverageByService,
    deductionsFromExternalAdvances,
    isExternalAdvanceId,
} from './externalAdvances';
import {
    bookedByReference,
    isAlreadyBooked,
    planWriteBack,
    planWriteBackRevert,
} from './invoiceWriteBackLogic';
import {
    aufteilungsAufnahme,
    notiere,
    riegelListe,
    starteAufteilungsSchatten,
    starteTorLeserSchatten,
    torLeserAufnahme,
    type TorLeserAufnahme,
} from './torLeserSchatten';
import {
    SHARE_FULL_BP,
    SPLIT_EPSILON,
    altCoveredByParty,
    splitCents,
    groupCoverageIssues,
    groupShareIssue,
    unassignedBilledIssues,
    billedByParty,
    billedFor,
    formatShare,
    hasSplit,
    splitLines,
    legacyPartyByCustomer,
    participantLabel,
    partyScopeFor,
    scopeToParty,
    validateSplit,
    resolveParty,
    type PartyScope,
    type ProjectSplit,
    type ProjectSplitParticipant,
} from './projectSplit';
import { fetchProjectSplit } from './projectSplitService';
import {
    buildCorrectionLines,
    copiedRetention,
    invoiceTotalsWithRetention,
    mirrorDeductions,
    mirrorPriorRetentions,
    mirrorRetentionTotals,
    buildCreditLines,
    mirrorLines,
    NEGATIVE_DOCUMENT_TYPES,
    onlyReleasesProjectQuantity,
    validateCancellation,
    validateCorrection,
} from './creditNoteLogic';

/**
 * Datenzugriff des Rechnungsmoduls.
 *
 * Stil wie crmService: async, throw bei Fehler, snake_case<->camelCase in den
 * Mappern, damit der Rest der Anwendung nur camelCase sieht.
 *
 * Bewusst NICHT an die Offline-Sync-Maschinerie der Projekte angeschlossen —
 * Rechnungen folgen dem CRM-Vorbild mit direktem CRUD. Beruehrt wird das
 * Projekt nur an einer Stelle: der Rueckschreibung des Leistungsstands beim
 * Ausstellen, und die geht ueber saveProjectToCloud.
 */

const BUCKET = 'invoices';

// ─── Row-Mapper ─────────────────────────────────────────────────────────────

/**
 * Geldspalten sind BIGINT und kommen von PostgREST als String. Number() ist
 * hier die einzige Konvertierung; alles danach rechnet in ganzen Cent.
 */
const cents = (v: unknown): number => Number(v ?? 0);

/**
 * Die Vorbelegliste des Sicherheitseinbehalts (Invoice.priorRetentions).
 *
 * Die Datenbankspalte heisst weiterhin `retention_releases` (Nr. 104): im
 * ersten Bau trug nur die Schlussrechnung eine Liste, naemlich die
 * Auszahlungen. Im kumulativen Modell traegt JEDER Beleg mit Abzuegen die
 * Einbehalte seiner Vorbelege, und die Schlussrechnung gibt sie ueber den
 * Satz 0 frei — die Spalte wurde nicht umbenannt, weil sie in PROD bereits
 * gefuellt ist und eine Umbenennung nichts am Inhalt aenderte.
 *
 * Bewusst fehlertolerant: fehlt die Spalte, weil Nr. 104 in der Umgebung
 * noch nicht gelaufen ist, laedt der Beleg trotzdem und traegt eine leere
 * Liste. Nur das SCHREIBEN braucht die Spalte zwingend.
 *
 * Altbestand aus dem ersten Bau nannte die Quelle `sourceInvoiceId`; sie wird
 * beim Lesen auf `sourceId` gehoben, damit kein Leser zwei Schreibweisen
 * kennen muss. Ausgestellte Belege lassen sich nicht umschreiben (Nr. 105),
 * die Uebersetzung bleibt also dauerhaft hier.
 */
const mapPriorRetentions = (raw: unknown): PriorRetention[] => {
    if (!Array.isArray(raw)) return [];
    return raw.map((r: any) => {
        const { sourceInvoiceId, ...rest } = r ?? {};
        return {
            ...rest,
            sourceId: r?.sourceId ?? sourceInvoiceId ?? '',
            retentionCents: Number(r?.retentionCents ?? 0),
        } as PriorRetention;
    });
};

/**
 * Die Summen eines Belegs aus der Zeile.
 *
 * Nur ZWEI der fuenf Einbehalt-Felder stehen in der Tabelle (retention_cents,
 * retention_release_cents, Nr. 104) — die uebrigen drei folgen aus ihnen und
 * der Vorbelegliste, und eine Spalte fuer etwas Ableitbares waere eine zweite
 * Wahrheit, die auseinanderlaufen kann:
 *
 *   prior      = Summe der Vorbelegliste
 *   cumulative = prior + retention - release      (Umkehrung von computeRetention)
 *   paidPrior  = prepaid - prior
 *
 * Fehlertolerant wie bisher: cents(undefined) ist 0, und 0 ist genau die
 * Wahrheit fuer jeden Beleg aus der Zeit vor dem Einbehalt. Ein Storno traegt
 * alles negativ, und die Ableitung bleibt dabei vorzeichenrichtig.
 */
const mapTotals = (row: any): InvoiceTotals => {
    const prepaidCents = cents(row.prepaid_cents);
    const retentionCents = cents(row.retention_cents);
    const retentionReleaseCents = cents(row.retention_release_cents);
    const priorRetentionCents = priorRetentionTotal(mapPriorRetentions(row.retention_releases));
    return {
        netTotalCents: cents(row.net_total_cents),
        taxTotalCents: cents(row.tax_total_cents),
        grossTotalCents: cents(row.gross_total_cents),
        prepaidCents,
        cumulativeRetentionCents: priorRetentionCents + retentionCents - retentionReleaseCents,
        priorRetentionCents,
        retentionCents,
        retentionReleaseCents,
        paidPriorCents: prepaidCents - priorRetentionCents,
        payableCents: cents(row.payable_cents),
        taxGroups: Array.isArray(row.tax_groups) ? row.tax_groups : [],
    };
};

const mapInvoiceRow = (row: any): Invoice => ({
    id: row.id,
    invoiceNumber: row.invoice_number ?? undefined,
    documentType: row.document_type,
    series: row.series,
    status: row.status,

    customerId: row.customer_id,
    customerName: row.customers?.name ?? undefined,
    primaryProjectId: row.primary_project_id ?? undefined,
    quoteId: row.quote_id ?? undefined,
    billingRunId: row.billing_run_id ?? undefined,
    splitGroupId: row.split_group_id ?? undefined,
    splitPartyId: row.split_party_id ?? undefined,
    splitShareBp: row.split_share_bp ?? undefined,

    invoiceDate: row.invoice_date,
    deliveryDateFrom: row.delivery_date_from ?? undefined,
    deliveryDateTo: row.delivery_date_to ?? undefined,
    dueDate: row.due_date,
    paymentTermsDays: Number(row.payment_terms_days ?? 14),

    lines: Array.isArray(row.lines) ? (row.lines as InvoiceLine[]) : [],
    advanceDeductions: Array.isArray(row.advance_deductions) ? row.advance_deductions : [],
    advanceDeductionMode: row.advance_deduction_mode ?? 'INDIVIDUAL',
    priorRetentions: mapPriorRetentions(row.retention_releases),
    totals: mapTotals(row),

    paidCents: cents(row.paid_cents),
    creditedCents: cents(row.credited_cents),
    openCents: cents(row.open_cents),
    dunningLevel: Number(row.dunning_level ?? 0),
    dunningLastAt: row.dunning_last_at ?? undefined,

    customerSnapshot: row.customer_snapshot ?? undefined,
    companySnapshot: row.company_snapshot ?? undefined,

    pdfUrl: row.pdf_url ?? undefined,
    snapshotId: row.snapshot_id ?? undefined,
    sentAt: row.sent_at ?? undefined,
    sentTo: row.sent_to ?? undefined,
    issuedAt: row.issued_at ?? undefined,
    issuedBy: row.issued_by ?? undefined,
    approvedAt: row.approved_at ?? undefined,
    approvedBy: row.approved_by ?? undefined,
    exportedAt: row.exported_at ?? undefined,

    details: row.details ?? {},
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
});

/** Nur definierte Felder schreiben — sonst wuerde ein Teil-Update Spalten leeren. */
const toInvoiceRow = (inv: Partial<Invoice>): Record<string, any> => {
    const row: Record<string, any> = {};
    if (inv.invoiceNumber !== undefined) row.invoice_number = inv.invoiceNumber;
    if (inv.documentType !== undefined) row.document_type = inv.documentType;
    if (inv.series !== undefined) row.series = inv.series;
    if (inv.status !== undefined) row.status = inv.status;
    if (inv.customerId !== undefined) row.customer_id = inv.customerId;
    if (inv.primaryProjectId !== undefined) row.primary_project_id = inv.primaryProjectId;
    if (inv.quoteId !== undefined) row.quote_id = inv.quoteId;
    if (inv.billingRunId !== undefined) row.billing_run_id = inv.billingRunId;
    if (inv.invoiceDate !== undefined) row.invoice_date = inv.invoiceDate;
    // Leer heisst NULL, nicht "leerer Text": die Oberflaeche liefert '' fuer ein
    // geraeumtes Datumsfeld, und bei kumulativer Abrechnung liefert die
    // Ableitung des Leistungsbeginns nichts, wenn kein datierter Eintrag
    // existiert. Beides muss die Spalte tatsaechlich leeren — sonst bliebe ein
    // Von-Datum stehen, das zum Beleg nicht mehr passt.
    if (inv.deliveryDateFrom !== undefined) row.delivery_date_from = inv.deliveryDateFrom || null;
    if (inv.deliveryDateTo !== undefined) row.delivery_date_to = inv.deliveryDateTo || null;
    if (inv.dueDate !== undefined) row.due_date = inv.dueDate;
    if (inv.paymentTermsDays !== undefined) row.payment_terms_days = inv.paymentTermsDays;
    if (inv.lines !== undefined) row.lines = inv.lines;
    if (inv.advanceDeductions !== undefined) row.advance_deductions = inv.advanceDeductions;
    if (inv.advanceDeductionMode !== undefined) {
        row.advance_deduction_mode = inv.advanceDeductionMode;
    }
    if (inv.totals !== undefined) {
        row.net_total_cents = inv.totals.netTotalCents;
        row.tax_total_cents = inv.totals.taxTotalCents;
        row.gross_total_cents = inv.totals.grossTotalCents;
        row.prepaid_cents = inv.totals.prepaidCents;
        // Der Einbehalt steckt bereits in payable_cents. Er wird TROTZDEM
        // eigens gespeichert: der naechste Beleg liest ihn je Vorbeleg
        // (priorRetentionsFromDeductions, netto retention - release), und aus
        // der Differenz zweier Bruttobetraege liesse er sich nicht
        // zurueckrechnen. cumulative, prior und paidPrior werden NICHT
        // gespeichert — mapTotals leitet sie ab (siehe dort).
        //
        // ?? 0, weil beide Felder in InvoiceTotals optional sind: ein Aufrufer
        // mit von Hand gebauten Summen darf die Spalten nicht auf NULL setzen
        // (NOT NULL in Migration Nr. 104).
        row.retention_cents = inv.totals.retentionCents ?? 0;
        row.retention_release_cents = inv.totals.retentionReleaseCents ?? 0;
        row.payable_cents = inv.totals.payableCents;
        row.tax_groups = inv.totals.taxGroups;
    }
    // Feld priorRetentions, Spalte retention_releases — siehe mapPriorRetentions.
    if (inv.priorRetentions !== undefined) row.retention_releases = inv.priorRetentions;
    if (inv.customerSnapshot !== undefined) row.customer_snapshot = inv.customerSnapshot;
    if (inv.companySnapshot !== undefined) row.company_snapshot = inv.companySnapshot;
    if (inv.pdfUrl !== undefined) row.pdf_url = inv.pdfUrl;
    if (inv.snapshotId !== undefined) row.snapshot_id = inv.snapshotId;
    if (inv.sentAt !== undefined) row.sent_at = inv.sentAt;
    if (inv.sentTo !== undefined) row.sent_to = inv.sentTo;
    if (inv.details !== undefined) row.details = inv.details;
    // Die Teilrechnungs-Gruppe wird bei der Anlage gesetzt und danach nie
    // wieder — sie muss den Festschreibungs-Trigger also nicht passieren.
    if (inv.splitGroupId !== undefined) row.split_group_id = inv.splitGroupId;
    if (inv.splitPartyId !== undefined) row.split_party_id = inv.splitPartyId;
    if (inv.splitShareBp !== undefined) row.split_share_bp = inv.splitShareBp;
    return row;
};

const SELECT = '*, customers(name)';

/**
 * Derselbe Beleg OHNE die Positionen.
 *
 * `lines` ist die mit Abstand groesste Spalte der Tabelle — ein jsonb-Feld mit
 * allen Positionen samt Texten, Mengen und LV-Bezuegen. In einer Liste ueber
 * den gesamten Bestand wird davon nichts angezeigt: die Tabelle zeigt Nummer,
 * Kunde, Projekt, Datum, Betraege und Status. Sie mitzuladen kostet bei
 * mehreren hundert Belegen ein Vielfaches der uebrigen Daten und ist der
 * groesste Hebel auf die Ladezeit der Rechnungsansicht.
 *
 * Bewusst eine ausgeschriebene Spaltenliste statt `*` mit Ausschluss: PostgREST
 * kennt kein "alles ausser". Mit `*` kaeme `lines` bei der naechsten neuen
 * Spalte still wieder mit, und niemand bemerkte den Rueckschritt.
 *
 * Die Liste muss dem Row-Mapper folgen. Was hier fehlt, faellt NICHT auf:
 * mapInvoiceRow setzt fuer fehlende Spalten stille Vorgaben ([], {}, 0). Ein
 * vergessenes advance_deductions wiese den Umsatz zu hoch aus, ein vergessenes
 * details naehme der Liste den Projektbezug — beides ohne Fehlermeldung.
 */
const SELECT_WITHOUT_LINES = [
    'id',
    'invoice_number',
    'document_type',
    'series',
    'status',
    'customer_id',
    'primary_project_id',
    'quote_id',
    'billing_run_id',
    'invoice_date',
    'delivery_date_from',
    'delivery_date_to',
    'due_date',
    'payment_terms_days',
    'advance_deductions',
    'advance_deduction_mode',
    'net_total_cents',
    'tax_total_cents',
    'gross_total_cents',
    'prepaid_cents',
    'retention_cents',
    'retention_release_cents',
    'retention_releases',
    'payable_cents',
    'tax_groups',
    'paid_cents',
    'credited_cents',
    'open_cents',
    'dunning_level',
    'dunning_last_at',
    'customer_snapshot',
    'company_snapshot',
    'pdf_url',
    'snapshot_id',
    'sent_at',
    'sent_to',
    'issued_at',
    'issued_by',
    'approved_at',
    'approved_by',
    'exported_at',
    'details',
    'created_by',
    'created_at',
    'updated_at',
    'customers(name)',
].join(', ');

// ─── Lesen ──────────────────────────────────────────────────────────────────

export interface FetchInvoicesOptions {
    /**
     * Positionen weglassen. Nur fuer Aufrufer, die ausschliesslich Kopfdaten
     * anzeigen — die zurueckgegebenen Belege tragen dann `lines: []`, und das
     * ist von "hat keine Positionen" nicht zu unterscheiden. Wer einen so
     * geladenen Beleg bearbeiten oder speichern will, muss ihn vorher ueber
     * fetchInvoice vollstaendig nachladen.
     */
    withoutLines?: boolean;
}

/**
 * Alle Belege.
 *
 * Ohne Optionen unveraendert vollstaendig — die uebrigen Aufrufer rechnen mit
 * den Positionen.
 *
 * Seitenweise: PostgREST liefert je Anfrage hoechstens 1000 Zeilen und
 * schweigt darueber. Der DATEV-Export verloere ab dem tausendsten Beleg still
 * Buchungen, der Zahlungsabgleich faende Rechnungen nicht (Stand 23.10.2026:
 * 438 Belege). `id` zuletzt macht die Sortierung eindeutig — mehrere Belege
 * desselben Tages und Anlagezeitpunkts (Rechnungslauf) blaetterten sonst
 * doppelt oder gar nicht.
 */
export const fetchInvoices = async (options?: FetchInvoicesOptions): Promise<Invoice[]> => {
    const select = options?.withoutLines ? SELECT_WITHOUT_LINES : SELECT;
    const rows = await ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
        supabase
            .from('invoices')
            .select(select)
            .order('invoice_date', { ascending: false })
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .range(von, bis),
    );
    return rows.map(mapInvoiceRow);
};

/**
 * Eine Rechnung laden. null heisst "gibt es nicht" — nicht "konnte nicht
 * geladen werden".
 *
 * Der Unterschied ist wesentlich: der Aufrufer laedt nach einer Buchung neu, um
 * den vom Datenbank-Trigger gesetzten Status zu sehen. Wuerde ein Netzfehler
 * hier zu null, zeigte die Oberflaeche weiter den alten Status und meldete
 * trotzdem Erfolg. Deshalb wird alles ausser "kein Treffer" (PGRST116)
 * durchgereicht.
 */
export const fetchInvoice = async (id: string): Promise<Invoice | null> => {
    const { data, error } = await supabase.from('invoices').select(SELECT).eq('id', id).single();
    if (error) {
        if ((error as { code?: string }).code === 'PGRST116') return null;
        throw error;
    }
    return data ? mapInvoiceRow(data) : null;
};

export const fetchInvoicesForProject = async (projectId: string): Promise<Invoice[]> => {
    const { data, error } = await supabase
        .from('invoices')
        .select(SELECT)
        .eq('primary_project_id', projectId)
        .order('invoice_date', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapInvoiceRow);
};

/**
 * Offene Belege, deren Mengen nach dem Ausstellen dieses Belegs nicht mehr
 * offen sind — Kandidaten fuers Aufraeumen.
 *
 * Nach dem Ausstellen aufgerufen: solche Konkurrenten sind ab jetzt nicht mehr
 * ausstellbar (der Projektcheck meldet "Menge doppelt beansprucht") und blieben
 * sonst kommentarlos in der Liste liegen. Geladen werden ALLE reservierenden
 * Belege, nicht nur die des Kunden: ein Entwurf zum selben Projekt kann unter
 * einem anderen Kunden angelegt worden sein, und die Zeilen liegen als jsonb
 * vor — ein Server-Filter auf projectId:serviceId ist nicht moeglich. Ob ein
 * Beleg wirklich konkurriert, entscheidet findCompetingDrafts anhand der
 * Mengen: der Projektstand ist hier frisch geladen und traegt die
 * Rueckschreibung der Ausstellung bereits, die Reservierungssicht den eben
 * ausgestellten Beleg nicht mehr. Ein nicht ladbares Projekt fuehrt zu "keine
 * Kandidaten dieser Positionen" — konservativ, nie zu einem Loeschangebot.
 */
/**
 * Statuswerte, in denen ein Beleg seinen Anteil an der Aufteilung NICHT mehr
 * traegt.
 *
 * Ein storniertes Geschwister fordert nichts mehr — sein Anteil steht wieder
 * offen. Zaehlte er weiter mit, kaeme die Gruppe rechnerisch auf 100 %, waehrend
 * tatsaechlich die Haelfte fehlt, und die uebrigen Belege liessen sich
 * ausstellen. Das Gegenteil dessen, wofuer der Riegel gebaut ist.
 *
 * SUPERSEDED_BY_FINAL_INVOICE bleibt bewusst DRIN: ein abgeloester Abschlag hat
 * gefordert und ist bezahlt worden, er ist in der Schlussrechnung aufgegangen.
 */
const SPLIT_INACTIVE_STATUSES: ReadonlySet<InvoiceStatus> = new Set<InvoiceStatus>([
    'CANCELLED',
]);

/**
 * Die noch tragenden Belege EINER Teilrechnungs-Gruppe.
 *
 * Der eigene Beleg ist dabei, und das ist Absicht: geprueft wird, ob die
 * Gruppe zusammen 100 % traegt, und dazu gehoert der eigene Anteil.
 */
export const fetchSplitGroupInvoices = async (splitGroupId: string): Promise<Invoice[]> => {
    const { data, error } = await supabase
        .from('invoices')
        .select(SELECT)
        .eq('split_group_id', splitGroupId);
    if (error) throw error;
    return (data || []).map(mapInvoiceRow).filter((i) => !SPLIT_INACTIVE_STATUSES.has(i.status));
};

export const fetchCompetingDrafts = async (issued: Invoice): Promise<Invoice[]> => {
    const projectIds = [
        ...new Set(
            issued.lines
                .filter((l) => l.kind === 'SERVICE' && l.projectId && l.serviceId)
                .map((l) => l.projectId as string),
        ),
    ];
    if (!projectIds.length) return [];

    // Seitenweise: der Statusfilter grenzt auf offene Belege ein, nicht auf
    // eine feste Menge — ueber 1000 offene Entwuerfe schnitte PostgREST still
    // ab (siehe fetchInvoices).
    const data = await ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
        supabase
            .from('invoices')
            .select(SELECT)
            .in('status', [...RESERVING_STATUSES])
            .order('id', { ascending: true })
            .range(von, bis),
    );

    const quantities = new Map<string, { used: number; billed: number }>();
    for (const projectId of projectIds) {
        const project = await loadProjectById(projectId);
        if (!project) continue;
        for (const s of getNormalizedServices(project)) {
            quantities.set(reservationKey(projectId, s.id), {
                used: s.quantityUsed || 0,
                billed: s.quantityBilled || 0,
            });
        }
    }
    const reservations = await fetchReservations(projectIds);

    return findCompetingDrafts(issued, data.map(mapInvoiceRow), {
        quantities,
        reservations,
    });
};

/**
 * Mengen, die bereits von einem offenen Entwurf beansprucht werden.
 *
 * Erste der drei Idempotenz-Schichten: ohne diese Abfrage zieht ein zweiter
 * Rechnungslauf dieselben offenen Positionen erneut in einen Entwurf.
 */
/**
 * Groesse eines ID-Blocks fuer `.in(...)`-Filter.
 *
 * Der Filter steht in der URL und ist laengenbegrenzt. Ein zu langer Filter
 * wird abgeschnitten und findet ZU WENIG — ohne Fehler. Bei Reservierungen
 * heisst zu wenig: eine bereits von einem Entwurf beanspruchte Menge gilt
 * wieder als offen, und der naechste Rechnungslauf stellt sie ein zweites Mal
 * in Rechnung.
 */
const ID_CHUNK = 100;

const chunked = <T,>(items: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
};

/**
 * Status, in denen ein Beleg Mengen reserviert. Muss der WHERE-Bedingung von
 * v_invoice_line_reservations entsprechen — sonst zieht der Abzug unten
 * Mengen ab, die in der Sicht gar nicht stehen.
 *
 * Der Gleichlauf gilt nicht nur fuer den STATUS, sondern ebenso fuer die
 * RECHNUNG: der Selbst-Ausschluss unten muss je Zeile GENAU den Ausdruck
 * abziehen, den die Sicht je Zeile addiert hat. Das ist reservedQtyOfLine
 * (invoiceLogic.ts) — greatest(quantity - previouslyBilledQty, 0), Zeilen mit
 * quantityInformational tragen nichts bei. Die rohe quantity abzuziehen war
 * genau der Fehler: eine Storno-Zeile traegt eine NEGATIVE Menge, und
 * 0 - (-1) = +1 erfand eine Reservierung, die in der Sicht nie stand.
 *
 * Wird die Sicht geaendert, sind BEIDE Konstanten hier und reservedQtyOfLine
 * nachzuziehen — sonst rechnet der Client wieder etwas anderes als die
 * Datenbank.
 */
const RESERVING_STATUSES: ReadonlySet<string> = new Set([
    'DRAFT',
    'READY_FOR_REVIEW',
    'APPROVED',
]);

/**
 * Reservierte Mengen je projectId:serviceId.
 *
 * `excludeInvoiceId` blendet den eigenen Beleg aus. Ohne das rechnet sich jeder
 * geoeffnete Entwurf seine eigenen Zeilen als fremde Reservierung an: die offene
 * Menge faellt auf null, und der Projektcheck meldet "Keine offene Position —
 * bereits abgerechnet oder von einem anderen Entwurf beansprucht" ausgerechnet
 * fuer den Entwurf, der die Menge haelt. Ausstellen ist dann unmoeglich.
 *
 * Die Sicht aggregiert ueber alle Belege, der eigene Anteil laesst sich daher
 * nicht wegfiltern — er wird abgezogen.
 */
export const fetchReservations = async (
    projectIds?: string[],
    excludeInvoiceId?: string,
): Promise<Map<string, number>> => {
    const select = 'project_id, service_id, reserved_qty';
    type Row = { project_id: string; service_id: string; reserved_qty: number | string };

    // Seitenweise, in BEIDEN Zweigen: PostgREST liefert je Anfrage hoechstens
    // 1000 Zeilen und meldet das nicht. Eine abgeschnittene Reservierung gilt
    // als offen, und der naechste Rechnungslauf stellt sie ein zweites Mal in
    // Rechnung. Auch ein Block von 100 Projekten kann ueber 1000 Zeilen tragen
    // — die Sicht hat eine Zeile je projectId:serviceId, ein grosses LV in
    // einem Entwurf allein schon Hunderte. Die Sicht gruppiert nach genau
    // diesen beiden Spalten; nach ihnen sortiert ist die Reihenfolge eindeutig.
    let data: Row[] = [];
    if (projectIds?.length) {
        for (const block of chunked(projectIds, ID_CHUNK)) {
            const zeilen = await ladeAlleSeiten<Row>((von, bis) =>
                supabase
                    .from('v_invoice_line_reservations')
                    .select(select)
                    .in('project_id', block)
                    .order('project_id', { ascending: true })
                    .order('service_id', { ascending: true })
                    .range(von, bis),
            );
            data = data.concat(zeilen);
        }
    } else {
        data = await ladeAlleSeiten<Row>((von, bis) =>
            supabase
                .from('v_invoice_line_reservations')
                .select(select)
                .order('project_id', { ascending: true })
                .order('service_id', { ascending: true })
                .range(von, bis),
        );
    }
    // Schluessel ist projectId:serviceId, nicht die serviceId allein. Die Sicht
    // gruppiert bereits nach beidem; wuerde man die Projekt-Zuordnung hier
    // wegwerfen, haenge die Richtigkeit daran, dass ServiceItem.id
    // projektuebergreifend eindeutig ist — eine Annahme, die keine
    // Datenbank-Bedingung durchsetzt. Ein dupliziertes Projekt liesse dann die
    // Reservierung eines fremden Entwurfs in ein anderes Projekt lecken.
    const map = new Map<string, number>();
    for (const row of data || []) {
        map.set(reservationKey(row.project_id, row.service_id), Number(row.reserved_qty) || 0);
    }

    if (excludeInvoiceId) {
        const { data: own, error } = await supabase
            .from('invoices')
            .select('status, lines')
            .eq('id', excludeInvoiceId)
            .maybeSingle();
        if (error) throw error;
        // Ein ausgestellter Beleg steht nicht in der Sicht. Wuerde man seine
        // Zeilen trotzdem abziehen, kappte der Abzug die Reservierungen
        // fremder Entwuerfe und gaebe deren Mengen ein zweites Mal frei.
        if (own && RESERVING_STATUSES.has(own.status)) {
            for (const line of (own.lines as InvoiceLine[] | null) ?? []) {
                if (line?.kind !== 'SERVICE' || !line.projectId || !line.serviceId) continue;
                const key = reservationKey(line.projectId, line.serviceId);
                // Derselbe Ausdruck, den die Sicht je Zeile addiert hat — nicht
                // die rohe Menge. Sonst erzeugt eine negative Storno-Zeile eine
                // positive Reservierung (0 - (-1) = +1).
                const rest = (map.get(key) ?? 0) - reservedQtyOfLine(line);
                // Nie unter null: die Sicht und die Zeilen koennen zwischen den
                // beiden Abfragen auseinanderlaufen, und eine negative
                // Reservierung wuerde die offene Menge kuenstlich erhoehen.
                map.set(key, Math.max(0, rest));
            }
        }
    }

    return map;
};

export const reservationKey = (projectId: string, serviceId: string): string =>
    `${projectId}:${serviceId}`;

/** Sicht auf die Reservierungen EINES Projekts, nach serviceId geschluesselt. */
export const reservationsForProject = (
    all: Map<string, number>,
    projectId: string,
): Map<string, number> => {
    const prefix = `${projectId}:`;
    const out = new Map<string, number>();
    for (const [key, qty] of all) {
        if (key.startsWith(prefix)) out.set(key.slice(prefix.length), qty);
    }
    return out;
};

// ─── Positionen aus dem Projekt-LV ──────────────────────────────────────────

export interface ProjectServiceLinesOptions {
    /** Beleg, dessen eigene Reservierung nicht zaehlt (der Entwurf selbst). */
    excludeInvoiceId?: string;
    /** Leistungszeitraum; leer heisst "alles Offene". */
    periodFrom?: string;
    periodTo?: string;
    /** Mengen ohne Protokolleintrag dem Zeitraum zuschlagen. */
    includeUndated?: boolean;
    /**
     * Kumulativ: den GESAMTEN Leistungsstand bis zum Stichtag zeigen, statt
     * nur die noch nicht abgerechneten Mengen des Zeitraums.
     */
    cumulative?: boolean;
    /**
     * Leistungen, die dieser Beleg nicht abrechnet (ServiceItem.id) — der am
     * Beleg vermerkte Ausschluss (details.excludedServiceIds).
     *
     * Wird VOR dem Zeilenbau angewandt, nicht danach: alles Weitere haengt an
     * den enthaltenen Zeilen — Zuordnungsplanung, vorhergesagter
     * Abrechnungszeitraum, Summen. Ein Abzug erst am Ergebnis liesse die
     * Vorhersage einen Zeitraum nennen, den der Beleg gar nicht abrechnet.
     */
    excludedServiceIds?: string[];
    taxRateBp: number;
}

export interface ProjectServiceLinesResult {
    lines: InvoiceLine[];
    projectName: string;
    orderReference?: string;
    /** Frühestes und spätestes Protokolldatum — Vorschlag fuer den Zeitraum. */
    loggedFrom?: string;
    loggedTo?: string;
    /**
     * Leistungsbeginn zum Stichtag — NUR bei kumulativer Bildung gesetzt.
     *
     * Der Beleg rechnet dort alles bis zum Stichtag ab; sein Zeitraum muss
     * genau das abdecken. Der Wert gehoert deshalb an jeden Aufrufer, der einen
     * kumulativen Beleg anlegt oder neu aufbaut, und ersetzt dessen
     * deliveryDateFrom. Undefined heisst "kein datierter Eintrag" — dann bleibt
     * das Von-Datum leer und der Beleg weist nur den Stichtag aus.
     */
    cumulativeFrom?: string;
    /**
     * Zeitraum, den ein kumulativer Beleg NEU abrechnet — NUR bei kumulativer
     * Bildung gesetzt.
     *
     * Vorhergesagt, nicht festgestellt: die endgueltige Zuordnung entsteht erst
     * beim Ausstellen (planProjectIssue), wo Zeitraum und Mengen feststehen. Hier
     * laufen dieselben puren Bausteine ueber denselben Stand — solange sich am
     * Protokoll und an den Vorbelegen nichts aendert, ist die Vorhersage die
     * spaetere Tatsache. Ausschliesslich fuer die Anzeige im Entwurf; der Andruck
     * liest die eingefrorene Zuordnung und nie diesen Wert.
     */
    cumulativeBillingPeriod?: { from: string; to: string };
    /**
     * Anzahl Leistungen, die insgesamt offen sind, im gewaehlten Zeitraum aber
     * nicht protokolliert wurden. Sichtbar zu machen, statt sie stumm
     * wegzulassen: sonst haelt der Nutzer eine zu kurze Rechnung fuer richtig.
     */
    outsidePeriodCount: number;
    /**
     * Erster und letzter Tag, an dem im Projekt bis zum Stichtag tatsaechlich
     * geleistet wurde (invoiceLogic: performedSpanFromLog).
     *
     * Gehoert an jeden Aufrufer, der einen Beleg anlegt oder neu aufbaut: er
     * schreibt die Spanne nach details.performedFrom/-To, und von dort lesen
     * Andruck, E-Rechnung, DATEV und Archiv. Aus dem fertigen Beleg allein
     * laesst sie sich nicht mehr gewinnen.
     */
    performed: { from?: string; to?: string };
    /**
     * Offene Mengen, zu denen es gar keinen Protokolleintrag gibt. Sie gehoeren
     * zu keinem Monat und fallen ohne includeUndated durch jedes Zeitfenster.
     */
    undated: UndatedSummary;
    /**
     * Die Wahl „ohne Datum mitnehmen", mit der GENAU diese Zeilen gebaut
     * wurden — nie undefined.
     *
     * Gehoert an jeden Aufrufer, der einen Beleg anlegt oder neu aufbaut: er
     * schreibt sie nach details.includeUndated (Begruendung dort). Aus dem
     * Ergebnis und nicht aus dem Zustand der Maske, damit am Beleg steht, was
     * in den Aufbau ging, und nicht, was ein Haken spaeter zeigt.
     */
    includeUndated: boolean;
}

/**
 * Was der Entwurfsaufbau von aussen liest (S5a Stufe 3c): der Tor-Notschalter
 * — derselbe Schalter fuer Zuordnung UND Entwurf, kein zweiter — und alles,
 * was entwurfAusJournal.ts braucht (Journal, Meldung, Uhr, Schlaf).
 */
export interface EntwurfAufbauDeps extends EntwurfDeps {
    liesNotschalter: () => Promise<Pick<TorNotschalterStand, 'modus'>>;
}

const STANDARD_ENTWURF_AUFBAU_DEPS: EntwurfAufbauDeps = {
    ...STANDARD_ENTWURF_DEPS,
    // Erst beim Aufruf aufgeloest, nicht beim Laden des Moduls.
    liesNotschalter: () => fetchTorNotschalter(),
};

/**
 * Baut die Positionszeilen eines Projekts aus dem Leistungsverzeichnis.
 *
 * Eine Stelle fuer beide Wege — Neuanlage aus der Liste und Neuaufbau im
 * Editor nach einer Zeitraumaenderung. Vorher stand die Zeilenbildung nur im
 * Anlagepfad; ein spaeter gesetzter Leistungszeitraum aenderte deshalb nur die
 * Kopfzeile des Belegs, nicht seine Positionen.
 *
 * ─── S5a Stufe 3c: das Protokoll kommt aus dem Buchungsjournal ─────────────
 * Im Journal-Modus (Tor-Notschalter AUS, der Normalbetrieb) rechnet der
 * Aufbau aus dem Journal — `journalToServiceTransactions(loadJournal(…))`
 * statt `metadata.globalData.serviceLog`. Alles andere (LV, Reservierungen,
 * Vorbelege, Zeitraum, Optionen) bleibt, wie es war, und geht unveraendert in
 * dieselbe reine Kernrechnung (`projectServiceLinesFromLog`). Ein
 * Rueckschatten rechnet sie ein zweites Mal ueber das JSON und meldet nur; ein
 * nicht lesbares Journal und ein Journal, das einer frisch gebuchten Leistung
 * nachlaeuft, halten den Aufbau mit Klartext an. Regeln, Klassen und Texte:
 * entwurfAusJournal.ts. Beim Ausstellen bleibt das Tor (allocationGate.ts)
 * das harte Netz.
 *
 * Im JSON-Modus (Notschalter AN) laeuft alles exakt wie bis Stufe 3b: kein
 * Journal-Aufruf, kein Schatten, kein Riegel.
 *
 * `deps` nur fuer Tests (Schalter, Journal, Meldung, Uhr, Schlaf).
 */
export const buildProjectServiceLines = async (
    projectId: string,
    opts: ProjectServiceLinesOptions,
    deps: Partial<EntwurfAufbauDeps> = {},
): Promise<ProjectServiceLinesResult> => {
    const d: EntwurfAufbauDeps = { ...STANDARD_ENTWURF_AUFBAU_DEPS, ...deps };

    // S5a Stufe 3c: der Tor-Notschalter, FRISCH je Aufbau, und nur im
    // Journal-Modus danach das Journal — als EINE Kette, sofort angestossen
    // und damit parallel zu Projekt, Reservierungen und Belegen statt danach.
    // Beide Glieder lehnen nie ab: der Schalter faellt bei jedem Zweifel
    // streng auf den Journal-Modus (torNotschalter.ts), `loadJournal` liefert
    // bei jedem Fehler null. Die `catch` halten das auch fuer ein Doppel, und
    // scheitert gleich das Projekt, bleibt nichts unbehandelt liegen.
    const quelle: Promise<{ modus: TorModus; journal: ServiceJournalRow[] | null }> = d
        .liesNotschalter()
        .catch(() => ({ modus: 'journal' as const }))
        .then(async (stand) =>
            stand.modus === 'json'
                ? { modus: 'json' as const, journal: null }
                : { modus: 'journal' as const, journal: await d.ladeJournal(projectId).catch(() => null) },
        )
        .catch(() => ({ modus: 'journal' as const, journal: null }));

    const project = await loadProjectById(projectId);
    if (!project) {
        throw new Error('Das Projekt konnte nicht geladen werden.');
    }
    const meta = project.data?.metadata ?? {};
    const global = meta.globalData ?? {};
    const serviceLog: ServiceTransaction[] = Array.isArray(global.serviceLog)
        ? global.serviceLog
        : [];
    const services = getNormalizedServices(project);

    // Die Abfragen gehen an dieselbe Datenbank und wissen nichts voneinander;
    // nacheinander ausgefuehrt wartete der Aufbau mehrmals auf die Leitung. Das
    // Fehlerverhalten bleibt je Zweig unveraendert — der Fehler der
    // Reservierungen traegt (ohne sie waere der offene Bestand falsch), der der
    // Vorhersage nicht.
    //
    // Kumulativ werden die Belege des Projekts fuer zweierlei gebraucht: die
    // Vorhersage des Abrechnungszeitraums (eine Anzeige im Entwurf) und die
    // bereits belegte Menge je Position (previouslyInvoicedQty, siehe unten).
    // Scheitert die Abfrage, entfaellt beides; den ganzen Aufbau daran
    // scheitern zu lassen waere ein Missverhaeltnis. Ein leeres Ergebnis
    // vorzutaeuschen waere schlimmer als keines: die Vorhersage nennte dann
    // einen zu weiten Zeitraum, und die Deckungspruefung saehe jede
    // Vorabrechnung als ungedeckt — beide Male saehe niemand, dass die Aussage
    // auf nichts beruht.
    //
    // Fortschreibend dieselbe Abfrage fuer den Zeitraumfilter UND die
    // Monatsangabe der Pauschalzeile — ihr Fehler traegt, wie bisher. Bis
    // Stufe 3b lief sie erst nach den beiden anderen; seit 3c steht sie mit
    // ihnen und dem Journal in einem Schritt (Latenz des Journals).
    const [reservationRows, projektBelege, { modus, journal }] = await Promise.all([
        fetchReservations([projectId], opts.excludeInvoiceId),
        opts.cumulative ? fetchProjectInvoices(projectId).catch(() => null) : fetchProjectInvoices(projectId),
        quelle,
    ]);

    const basis: ProjectServiceLinesBasis = {
        projectId,
        projectNumber: project.project_number,
        projectName: project.name,
        metaProjectName: meta.projectName,
        orderReference: meta.orderNumber,
        services,
        reservations: reservationsForProject(reservationRows, projectId),
        priorForPrediction: opts.cumulative ? projektBelege : null,
        periodInvoices: opts.cumulative ? null : projektBelege,
        newLineId: () => crypto.randomUUID(),
    };
    const baue = (log: ServiceTransaction[]): ProjectServiceLinesResult =>
        projectServiceLinesFromLog(log, basis, opts);

    // Notschalter AN: exakt der Weg bis Stufe 3b.
    if (modus === 'json') return baue(serviceLog);

    return entwurfAusDemJournal(
        {
            kopf: {
                projectId,
                projectNumber: project.project_number ? String(project.project_number) : null,
                invoiceId: opts.excludeInvoiceId ?? null,
                kumulativ: Boolean(opts.cumulative),
                zeitraum: { from: opts.periodFrom, to: opts.periodTo },
            },
            jsonLog: serviceLog,
            journal,
            baue,
            leistungen: services,
        },
        d,
    );
};

/**
 * Was der Entwurfsaufbau ausser dem Protokoll braucht — alles geladen, bevor
 * gerechnet wird (S5a Stufe 3c). Die Kernrechnung liest NUR das hier und das
 * Protokoll; so rechnet sie im Journal-Modus zweimal mit identischen
 * Eingaben, und es gibt keine zweite Fassung der Regeln.
 */
interface ProjectServiceLinesBasis {
    projectId: string;
    projectNumber?: string;
    projectName?: string;
    /** metadata.projectName — der Rueckfall fuer den Namen im Ergebnis. */
    metaProjectName?: string;
    orderReference?: string;
    /** Das LV, gestempelt wie in getNormalizedServices. */
    services: ServiceItem[];
    /** Reservierungen der anderen Entwuerfe, nach serviceId. */
    reservations: Map<string, number>;
    /** Kumulativ: die Belege des Projekts — null, wenn die Abfrage weggebrochen ist. */
    priorForPrediction: Invoice[] | null;
    /** Fortschreibend: die Belege des Projekts. */
    periodInvoices: Invoice[] | null;
    /** Die id einer neuen Zeile — zufaellig; verglichen wird je Leistung. */
    newLineId: () => string;
}

/**
 * Die reine Kernrechnung des Entwurfsaufbaus: Protokoll und Basis ergeben das
 * Ergebnis, ohne Netz, ohne Uhr. Bis Stufe 3b stand sie im Rumpf von
 * buildProjectServiceLines; herausgeloest, damit der Rueckschatten DIESELBE
 * Rechnung ueber das JSON fuehrt (S5a Stufe 3c).
 *
 * Liest ueber `isPostingEntry` und Verwandte das Buchungsart-Register — fuer
 * JSON-Zeilen also jetzt, im Moment des Aufrufs. Journalzeilen tragen ihre
 * Art selbst (`kind`) und fragen das Register nie.
 */
const projectServiceLinesFromLog = (
    serviceLog: ServiceTransaction[],
    basis: ProjectServiceLinesBasis,
    opts: ProjectServiceLinesOptions,
): ProjectServiceLinesResult => {
    const { projectId, services, reservations, priorForPrediction, periodInvoices } = basis;

    const all = computeOpenPositions(services, reservations);
    const inPeriod = opts.cumulative
        ? // Kumulativ zaehlt der Stand BIS zum Stichtag, nicht der Zeitraum:
          // "alles bisher Geleistete" ist eine Summe von Anfang an. Reservierungen
          // und bereits abgerechnete Mengen bleiben aussen vor — der Abzug der
          // Vorrechnungen erfolgt im Summenblock.
          computeCumulativePositions(services, {
              to: opts.periodTo,
              serviceLog,
              includeUndated: opts.includeUndated,
          })
        : computeOpenPositions(services, reservations, {
              from: opts.periodFrom,
              to: opts.periodTo,
              serviceLog,
              includeUndated: opts.includeUndated,
              // Die Belege des Projekts sagen, welche Protokolleintraege
              // bereits abgerechnet sind. Ohne sie boete ein Maifilter einen
              // vollstaendig abgerechneten Mai erneut an, solange im Projekt
              // ueberhaupt noch etwas offen ist.
              invoices: periodInvoices ?? undefined,
          });

    // Der Ausschluss greift VOR dem Zeilenbau. Alles, was danach kommt, rechnet
    // ueber die Zeilen — Zuordnungsplanung und der vorhergesagte
    // Abrechnungszeitraum eingeschlossen. Eine herausgenommene Leistung darf den
    // Zeitraum nicht mehr aufspannen, den sie selbst nicht mehr abrechnet.
    // Bindend geprueft, nicht bloss in der Maske: eine Leistung mit
    // ALT-Buchung wurde von der Alt-Rechnung gefordert, deren Abschlag dieser
    // Beleg abzieht. Bliebe sie draussen, zoege er mehr ab, als er ausweist.
    // Der Ausschluss steht am Beleg und ueberdauert — ein Entwurf aus der Zeit
    // vor dem Nachtrag traegt ihn sonst durch jeden Neuaufbau.
    const excluded = new Set(
        effectiveExcludedServiceIds(
            opts.excludedServiceIds,
            Boolean(opts.cumulative),
            altBookedServiceIds(serviceLog),
        ),
    );
    const positions = excluded.size
        ? inPeriod.filter((pos) => !excluded.has(pos.service.id))
        : inPeriod;

    // Auf den Kalendertag kuerzen: loggedFrom/loggedTo werden als
    // Leistungszeitraum vorgeschlagen und gegen Tagesgrenzen geprueft. Ein
    // Zeitstempel als Obergrenze schliesst genau den letzten Tag wieder aus.
    // Der BERLINER Tag (Betreiber-Entscheid 1 vom 02.10.2026), derselbe, nach
    // dem die Positionen ihren Zeitraum zaehlen (loggedQuantityInPeriod).
    const dates = serviceLog
        .filter((t) => !isAccountingEntry(t) && t?.date)
        .map((t) => buchungstag(t.date))
        .filter((d): d is string => !!d)
        .sort();

    // Wieviel der bereits abgerechneten Menge durch einen BELEG gedeckt ist.
    // Zwei Quellen, beide in Menge — Betraege taugen dafuer nicht (siehe
    // invoicedQuantityByService):
    //   Vorbelege des Projekts — was sie beansprucht haben, vorzeichenrichtig
    //   ALT-Protokollzeilen    — nachgetragene Alt-Rechnungen (Bestandsprojekte)
    // Der eigene Beleg bleibt draussen: seine Zeilen sind genau das, was hier
    // geprueft werden soll.
    //
    // NUR wenn priorForPrediction wirklich geladen wurde. Ist die Abfrage
    // weggebrochen (.catch(() => null) oben), bleibt das Feld WEG statt auf 0
    // zu stehen — 0 hiesse "nichts gedeckt" und sperrte einen einwandfreien
    // Beleg. Fehlend heisst "nicht ermittelt" und warnt nur.
    const coveredByService = priorForPrediction
        ? invoicedQuantityByService(priorForPrediction, opts.excludeInvoiceId)
        : null;
    const altCovered = coveredByService ? altCoverageByService(serviceLog) : null;

    const lines: InvoiceLine[] = positions.map((pos) => ({
        id: basis.newLineId(),
        kind: 'SERVICE' as const,
        projectId,
        projectNumber: basis.projectNumber,
        projectName: basis.projectName,
        serviceId: pos.service.id,
        lvPosition: pos.service.lvPosition,
        clientLvPosition: pos.service.clientLvPosition,
        title: pos.service.title,
        unit: pos.service.unit,
        quantity: pos.openQty,
        unitPriceCents: pos.unitPriceCents,
        taxRateBp: opts.taxRateBp,
        netCents: pos.netCents,
        // Gedeckt durch eine Monatspauschale: der Andruck darf solche Zeilen
        // zusammenfassen (details.flatRateDisplay). Der Stempel kommt von
        // getNormalizedServices (stampFlatCoverage) und steht nur dort.
        ...(pos.service.flatCoveredBy ? { flatCovered: true } : {}),
        // Nur kumulativ: dort steht die Gesamtmenge in der Zeile, und wie
        // viel davon neu ist, sieht man ihr nicht an. Fortschreibend ist
        // die Zeile per Definition neu — der Wert waere dort immer 0 und
        // nur Rauschen.
        ...(opts.cumulative
            ? {
                  previouslyBilledQty: pos.service.quantityBilled || 0,
                  // Bewusst auch dann gesetzt, wenn er 0 ist: gesetzt-und-0
                  // heisst "geprueft, nichts gedeckt" und ist der Befund;
                  // fehlend heisst "nicht ermittelt". Nur so laesst sich ein
                  // Altentwurf von einem echten Loch unterscheiden.
                  ...(coveredByService
                      ? {
                            previouslyInvoicedQty:
                                (coveredByService.get(pos.service.id) ?? 0) +
                                (altCovered?.get(pos.service.id)?.quantity ?? 0),
                        }
                      : {}),
              }
            : {}),
    }));

    // Nur kumulativ: fortschreibend nennt der Beleg seinen Zeitraum selbst,
    // und der Wert waere dort eine Angabe, die niemand verwenden darf.
    const cumulativeFrom = opts.cumulative
        ? cumulativeDeliveryStart(serviceLog, opts.periodTo)
        : undefined;

    // MONATSPAUSCHALE: die Pauschalzeile nennt, welche Monate sie abrechnet
    // und wie viele Begehungen in jedem stecken (Buchhaltung 5633: „Juli und
    // August mit je 2 Begehungen"). Die Monate kommen aus derselben Zuordnung,
    // die das Ausstellen treffen wird (planProjectIssue: Vorbelege ohne den
    // eigenen, Zeitraum des Belegs, kumulativ nur der Zuwachs) — nicht aus dem
    // Zeitraum: ein schon abgerechneter Juli laege im Zeitraum, stuende aber
    // nicht auf dieser Zeile. Ohne geladene Vorbelege keine Angabe; geraten
    // waere schlimmer. Menge, Preis und Summe bleiben unberuehrt, gefuellt
    // wird nur eine leere Beschreibung einer nicht von Hand betitelten Zeile.
    const flatPrior = opts.cumulative ? priorForPrediction : periodInvoices;
    const flatAllocated = flatPrior
        ? allocatedByEntry(flatPrior.filter((i) => i.id !== opts.excludeInvoiceId))
        : null;
    // Ohne ALT-Zeilen: eine Alt-Rechnung auf der Pauschale (5633: ALT-5633/12,
    // 25,5 Monate, datiert 03.07.) ist bereits abgerechnet und hat keinen
    // Monatsschluessel. Als aelteste Zeile im Zeitraum naehme sie sonst den
    // Zuwachs auf, und die Zeile bekaeme gar keine Monatsangabe. Nur fuer diese
    // Beschreibung — die Zuordnung beim Ausstellen bleibt, wie sie ist.
    // Ohne verzichtete Eintraege, wie die Zuordnung beim Ausstellen.
    const flatLog = ohneVerzichteteEintraege(
        flatAllocated ? serviceLog.filter((e) => !isBackfilledAltEntry(e)) : serviceLog,
        services,
    );
    const describedLines = flatAllocated
        ? lines.map((line) => {
              const flat = positions.find((p) => p.service.id === line.serviceId)?.service;
              if (!flat || !isFlatRateService(flat) || line.description || line.titleManual) return line;
              const toAllocate = opts.cumulative
                  ? Math.max(0, (line.quantity ?? 0) - (line.previouslyBilledQty ?? 0))
                  : (line.quantity ?? 0);
              const [planned] = planAllocations(
                  [line],
                  flatLog,
                  flatAllocated,
                  opts.cumulative
                      ? { from: cumulativeFrom, to: opts.periodTo }
                      : { from: opts.periodFrom, to: opts.periodTo },
                  () => toAllocate,
              );
              const monthKeys = flatLineMonthKeys(planned?.allocations, flatLog, toAllocate);
              if (!monthKeys?.length) return line;
              const description = flatLineDescription(flatMonthProofSummary(serviceLog, flat, services), monthKeys);
              return description ? { ...line, description } : line;
          })
        : lines;

    return {
        projectName: basis.projectName ?? basis.metaProjectName ?? '',
        orderReference: basis.orderReference,
        loggedFrom: dates[0],
        loggedTo: dates[dates.length - 1],
        cumulativeFrom,
        // Genau der Lauf, den planProjectIssue beim Ausstellen fuehren wird:
        // dieselbe Zuwachsmenge, derselbe Zeitraum, dieselben Vorbelege ohne
        // den eigenen. Nachgebaut statt geschaetzt — eine zweite, ungefaehre
        // Regel wuerde frueher oder spaeter etwas anderes sagen als der Andruck.
        cumulativeBillingPeriod: priorForPrediction
            ? billingPeriodOf(
                  planAllocations(
                      lines,
                      // Ohne verzichtete Eintraege, wie beim Ausstellen.
                      ohneVerzichteteEintraege(serviceLog, services),
                      // Ohne den eigenen Beleg: seine Zuordnung wuerde sich
                      // sonst selbst blockieren — dieselbe Einschraenkung, die
                      // planProjectIssue beim Ausstellen trifft.
                      allocatedByEntry(
                          priorForPrediction.filter((i) => i.id !== opts.excludeInvoiceId),
                      ),
                      { from: cumulativeFrom, to: opts.periodTo },
                      // Zugeordnet wird der ZUWACHS, nicht der Gesamtstand der
                      // Zeile — sonst verbrauchte ein Junibeleg die
                      // Julieintraege gleich mit.
                      (l) => Math.max(0, (l.quantity ?? 0) - (l.previouslyBilledQty ?? 0)),
                  ),
              )
            : undefined,
        // Gegen die Positionen VOR dem Ausschluss gezaehlt: die Meldung spricht
        // von Leistungen ausserhalb des Zeitraums. Herausgenommene Positionen
        // liegen im Zeitraum und sind bewusst weggelassen — sie hier
        // mitzuzaehlen machte aus einer Entscheidung eine Zeitraumluecke.
        outsidePeriodCount: all.length - inPeriod.length,
        undated: undatedOpenSummary(services, reservations, serviceLog),
        // Der LEISTUNGSZEITRAUM des Belegs, aus dem Protokoll selbst und
        // begrenzt auf den Stichtag. Bewusst hier gebildet und an den Beleg
        // gehaengt: dem fertigen Beleg liegt das Protokoll nicht mehr bei, und
        // aus seiner Zuordnung laesst sich die Frage nicht beantworten — die
        // verbraucht kumulativ die aeltesten offenen Eintraege zuerst.
        // Fortschreibend ab "Leistung von": die Positionen nehmen nur die
        // Mengen des Zeitraums (computeOpenPositions), der Zeitraum der
        // Leistung beginnt also nicht vorher.
        performed: performedSpanFromLog(
            serviceLog,
            opts.periodTo,
            opts.cumulative ? undefined : opts.periodFrom,
        ),
        // Dieselbe Wahl, die oben in computeOpenPositions bzw.
        // computeCumulativePositions ging — als echter Wahrheitswert, damit
        // am Beleg nie „unbekannt" steht, wo gebaut wurde.
        includeUndated: opts.includeUndated === true,
        lines: describedLines,
    };
};

// ─── Leistungsnachweis als Anlage ───────────────────────────────────────────

/**
 * Rendert den Leistungsnachweis zum Projekt des Belegs.
 *
 * Der Zeitraum kommt aus dem Beleg selbst: ein Nachweis, der mehr abdeckt als
 * die Rechnung, ist keine Grundlage — er belegt Leistungen, die hier gar nicht
 * abgerechnet werden.
 *
 * Liefert null, wenn kein Projektbezug besteht (Abschlag, Storno, kaufmaennische
 * Gutschrift). Das ist kein Fehler, sondern die Aussage "es gibt nichts
 * nachzuweisen".
 *
 * Der Generator wird nachgeladen: er zieht jsPDF samt Tabellen-Erweiterung und
 * wird nur gebraucht, wenn jemand den Nachweis tatsaechlich anfordert.
 */
/**
 * Laedt einen eigenen Leistungsnachweis (PDF) in den Beleg-Bucket hoch —
 * z. B. die unterschriebene Fassung, die der Auftraggeber zurueckschickt.
 *
 * Pfad mit UUID statt fester Dateiname: ein erneuter Upload ersetzt den Beleg-
 * Verweis (details.customProofPath), ueberschreibt aber nie eine Datei, auf die
 * ein anderer Stand noch zeigt. Die Vorgaengerdatei bleibt bewusst liegen —
 * Aufraeumen waere ein Loeschen im Archiv-Bucket fuer eine Handvoll Kilobyte.
 */
export const uploadCustomPerformanceProof = async (
    invoiceId: string,
    file: Blob,
    fileName: string,
): Promise<{ path: string; fileName: string }> => {
    const path = `custom-proofs/${invoiceId}/${crypto.randomUUID()}.pdf`;
    const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: false, contentType: 'application/pdf' });
    if (error) throw error;
    return { path, fileName };
};

export const renderPerformanceProofForInvoice = async (
    invoice: Pick<Invoice, 'primaryProjectId' | 'deliveryDateFrom' | 'deliveryDateTo' | 'lines' | 'details'>,
    userProfile: unknown,
): Promise<Uint8Array | null> => {
    // Ein hochgeladener Nachweis schlaegt den generierten: er ist die
    // unterschriebene bzw. abgestimmte Fassung. Bewusst VOR der
    // Projektbezug-Pruefung — die Datei existiert unabhaengig davon.
    const customPath = invoice.details?.customProofPath?.trim();
    if (customPath) {
        const { data, error } = await supabase.storage.from(BUCKET).download(customPath);
        if (error || !data) {
            throw new Error(
                'Der hochgeladene Leistungsnachweis konnte nicht geladen werden — Anlage entfernen oder neu hochladen.',
            );
        }
        return new Uint8Array(await data.arrayBuffer());
    }
    if (!invoice.primaryProjectId) return null;
    const project = await loadProjectById(invoice.primaryProjectId);
    if (!project) {
        throw new Error(
            'Das Projekt zu dieser Rechnung konnte nicht geladen werden — ohne Projektdaten entsteht kein Leistungsnachweis.',
        );
    }
    // Berichtsarchiv (eigenes + Tochterprojekte) mitgeben — derselbe Abgleich
    // wie im Leistungsnachweis-Editor. Ohne ihn behalten Buchungen aus
    // Tochterprojekten ihren Stub-Namen "System (Linked)" statt des
    // Koordinators, der die Begehung tatsaechlich durchgefuehrt hat.
    const reportHistory = await fetchReportHistoryWithChildren(invoice.primaryProjectId);
    const { buildPerformanceProofData, generatePerformanceProof } = await import('./pdf/generatorPerformance');
    // Der Filterzeitraum ist das Auswahlfenster des Belegs — es sei denn, am
    // Beleg steht ein eigener Nachweiszeitraum (proofFilterRange).
    const data = buildPerformanceProofData(project, proofFilterRange(invoice), reportHistory);
    // Abgewaehlte Positionen ausblenden statt aus der Liste zu werfen: der
    // Generator entscheidet ohnehin ueber `visible`, und was ausgeblendet ist,
    // bleibt so beim Nachsehen im Datenobjekt sichtbar. Nur die ANZEIGE faellt
    // weg — Mengen und Stand der Position bleiben unberuehrt.
    const excluded = new Set(proofExcludedServiceIds(invoice));
    if (excluded.size > 0) {
        data.services = data.services.map((s) =>
            excluded.has(s.id) ? { ...s, visible: false } : s,
        );
    }
    // Der Nachweis haengt an der Rechnung — er traegt deren Projektnamen
    // (eigener Anzeigename des Belegs eingeschlossen), nicht den internen.
    data.pdfProjectName = performanceProofProjectName(invoice);
    // Und deren ausgewiesenen Leistungszeitraum in der Kopfzeile: stuende dort
    // ein anderer als auf der Rechnung davor, saehe der Empfaenger zwei
    // Vorgaenge. Nur die Kopfzeile — welche Eintraege der Nachweis zeigt,
    // bleibt am Filterzeitraum oben und damit an dem, was der Beleg abrechnet.
    // Weichen beide voneinander ab, sagt der Nachweis das in einer zweiten
    // Zeile (generatorPerformance).
    //
    // Nur gesetzt, wenn es tatsaechlich einen ausgewiesenen Zeitraum gibt: ein
    // displayRange aus zwei null-Werten uebersteuerte den Filterzeitraum in der
    // Kopfzeile mit gar nichts und liesse die Angabe verschwinden.
    const printed = printedServicePeriod(invoice);
    if (printed.from || printed.to) {
        data.displayRange = { start: printed.from ?? null, end: printed.to ?? null };
    }
    // Der Leistungszeitraum des Nachweises wird bewusst NICHT hier gesetzt: der
    // Generator leitet ihn aus den Zeilen ab, die seine Tabelle tatsaechlich
    // auflistet (generatorPerformance: renderedPerformanceSpan). Nur so kann
    // sein Kopf der Aufstellung darunter nicht widersprechen — jede von aussen
    // hereingereichte Spanne kann von ihr abweichen.
    // Und deren Kostenstelle, mit derselben Regel wie die Belegzeile
    // (costCenterLine): ohne WERT keine Zeile, die Bezeichnung allein ist ein
    // Formularrest.
    const costCenterValue = invoice.details?.costCenter?.trim();
    if (costCenterValue) {
        data.pdfCostCenter = {
            label: invoice.details?.costCenterLabel?.trim() || DEFAULT_COST_CENTER_LABEL,
            value: costCenterValue,
        };
    }
    const doc = await generatePerformanceProof(project, userProfile, data);
    return new Uint8Array(doc.output('arraybuffer'));
};

/** Eine Position, so wie sie in der Abwahlliste des Nachweises steht. */
export interface PerformanceProofServiceRef {
    id: string;
    lvPosition: string;
    title: string;
    unit: string;
    /** Zahl der Protokollzeilen im gewaehlten Zeitraum — die Anzeige „was faellt weg?". */
    entryCount: number;
}

/**
 * Die Positionen, aus denen der Nachweis dieses Belegs besteht — fuer die
 * Abwahlliste in der Maske.
 *
 * Gebaut ueber denselben buildPerformanceProofData wie die Anlage selbst und
 * mit demselben Filterzeitraum, nicht ueber eine eigene Leseregel: Ids,
 * Reihenfolge und Zusammenfassungen muessen die der Anlage sein, sonst blendet
 * ein Haekchen etwas anderes aus, als danebensteht.
 *
 * OHNE Berichtsarchiv (fetchReportHistoryWithChildren): das ist der teure Teil
 * des Aufbaus, und es fuegt nur Protokollzeilen hinzu, nie Positionen. Die
 * Zahl der Eintraege kann dadurch niedriger liegen als im gedruckten Nachweis
 * — sie ist ein Anhaltspunkt in der Liste, keine Angabe auf einem Beleg.
 */
export const listPerformanceProofServices = async (
    invoice: ProofFilterSource & { primaryProjectId?: string },
): Promise<PerformanceProofServiceRef[]> => {
    if (!invoice.primaryProjectId) return [];
    const project = await loadProjectById(invoice.primaryProjectId);
    if (!project) return [];
    const { buildPerformanceProofData } = await import('./pdf/generatorPerformance');
    const data = buildPerformanceProofData(project, proofFilterRange(invoice), []);
    return data.services.map((s) => ({
        id: s.id,
        lvPosition: s.lvPosition || '',
        title: s.title || '',
        unit: s.unit || '',
        entryCount: s.logs.length,
    }));
};

// ─── Schreiben (Entwurfsphase) ──────────────────────────────────────────────

export interface CreateInvoiceDraftInput {
    customerId: string;
    documentType?: DocumentType;
    primaryProjectId?: string;
    quoteId?: string;
    billingRunId?: string;
    /** Teilrechnung: alle Belege EINER Aufteilung tragen dieselbe Gruppe. */
    splitGroupId?: string;
    /** Teilrechnung: WESSEN Beleg das ist (project_splits.id). */
    splitPartyId?: string;
    /** Anteil dieses Belegs an der Aufteilung (5000 = 50,00 %). */
    splitShareBp?: number;
    invoiceDate?: string;
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    paymentTermsDays?: number;
    lines?: InvoiceLine[];
    details?: Invoice['details'];
    /**
     * Bereits geladene Kundenkonditionen. Der Abrechnungslauf holt sie fuer
     * alle Gruppen gebuendelt (fetchCustomerTerms) und reicht sie durch; ohne
     * das Feld laedt der Entwurf sie selbst nach.
     */
    customerTerms?: CustomerTerms;
    /**
     * Bereits ermittelte Vorbelegungen DIESES PROJEKTS — Kostenstelle und
     * abweichender Projektname aus den letzten Belegen. Dasselbe Muster wie bei
     * den Kundenkonditionen und aus demselben Grund: der Abrechnungslauf
     * ermittelt sie fuer alle beteiligten Projekte in EINER Abfrage vor der
     * Schleife und reicht je Gruppe die passende durch, sonst kaeme ein
     * serieller Roundtrip je Beleg hinzu.
     *
     * Drei Zustaende, und alle drei werden gebraucht: `undefined` heisst "nicht
     * nachgeschlagen" (der Entwurf holt sie selbst), `null` heisst
     * "nachgeschlagen, es gibt keine" (nicht noch einmal fragen), ein Wert
     * heisst "diese hier".
     */
    lastDefaults?: LastUsedInvoiceDefaults | null;
}

/**
 * Nummer und Name der Projekte des Belegs, fuer den Vermerk am Beleg.
 *
 * Schmale Abfrage statt loadProjectById: gebraucht werden zwei Spalten, und der
 * Berichts-JSON eines Projekts ist gross. Fuer die Sammelrechnung reicht der
 * Beleg selbst — ihre Zeilen tragen den Projektbezug bereits.
 *
 * Scheitert das Nachschlagen, bleibt der Vermerk leer statt die Anlage des
 * Entwurfs scheitern zu lassen: Nummer und Name sind eine Angabe auf dem Beleg,
 * keine Bedingung seiner Entstehung.
 */
const resolveProjectRefs = async (
    input: CreateInvoiceDraftInput,
): Promise<InvoiceProjectRef[]> => {
    if (input.details?.projects?.length) return input.details.projects;

    const fromLines = invoiceProjectRefs({ lines: input.lines ?? [] });
    if (!input.primaryProjectId) return fromLines;

    try {
        const { data, error } = await supabase
            .from('projects')
            .select('id, project_number, name')
            .eq('id', input.primaryProjectId)
            .maybeSingle();
        if (error) throw error;
        if (data) {
            return [
                { id: data.id, number: data.project_number ?? undefined, name: data.name ?? undefined },
            ];
        }
    } catch (e) {
        // Dann eben aus den Zeilen — oder gar nicht. Der Fehlschlag muss aber
        // sichtbar bleiben: eine Rechte-Regression oder ein Netzfehler liesse
        // sonst jeden neuen Entwurf still ohne Projektvermerk entstehen, und
        // niemand haette einen Anhaltspunkt, woran es liegt.
        console.warn('Projektvermerk nicht auflösbar', e);
    }
    return invoiceProjectRefs({
        primaryProjectId: input.primaryProjectId,
        lines: input.lines ?? [],
    }).filter((r) => r.number || r.name);
};

/**
 * Kundenkonditionen fuer die Vorbelegung eines Entwurfs: Zahlungsziel und
 * Skonto. Beides steht in derselben Kundenzeile und wird deshalb gemeinsam
 * geladen — der Skonto-Satz nennt BEIDE Fristen, und die Nettofrist aus der
 * Konfiguration zu nehmen, waehrend die Skontofrist vom Kunden kommt, ergaebe
 * einen Satz, den der Kunde so nicht vereinbart hat.
 */
export interface CustomerTerms {
    /** Zahlungsziel des Kunden in Tagen; fehlend = Standard aus der Konfiguration. */
    paymentTermsDays?: number;
    skonto?: { percent: number; days: number };
}

/**
 * Kundenkonditionen nur auf originaeren Forderungsbelegen: Storno, Korrektur
 * und Gutschriften beziehen sich auf einen Ursprungsbeleg — ein dort neu
 * angebotener Abzug waere eine falsche Aussage, und ihre Faelligkeit richtet
 * sich nach dem Ursprungsbeleg, nicht nach dem heutigen Kundenstammsatz.
 */
const CUSTOMER_TERMS_DOCUMENT_TYPES = new Set<DocumentType>([
    'INVOICE',
    'ADVANCE_INVOICE',
    'FINAL_INVOICE',
    'REMAINING_INVOICE',
]);

const CUSTOMER_TERMS_COLUMNS = 'payment_terms_days, skonto_percent, skonto_days';

/**
 * Skonto nur, wenn BEIDE Werte tragen — ein halber Eintrag (Prozent ohne Frist
 * oder umgekehrt) ist keine anwendbare Kondition. Ein Zahlungsziel von 0 oder
 * NULL ist keine Angabe, dort bleibt der Standard der Konfiguration.
 *
 * Das traegt erst, seit `customers.payment_terms_days` NULL-bar ist
 * (customers_payment_terms_optional.sql). Vorher war die Spalte
 * `not null default 30`, jede Kundenzeile trug also einen Wert — und die
 * globale Einstellung "Zahlungsziel (Tage)" erreichte keine einzige normale
 * Rechnung. Wer hier wieder eine Vorbelegung einzieht, schaltet sie erneut ab.
 */
const customerTermsFromRow = (row: any): CustomerTerms => {
    const paymentTermsDays = Number(row?.payment_terms_days);
    const percent = Number(row?.skonto_percent);
    const days = Number(row?.skonto_days);
    return {
        ...(paymentTermsDays > 0 ? { paymentTermsDays } : {}),
        ...(percent > 0 && days > 0 ? { skonto: { percent, days } } : {}),
    };
};

/**
 * Konditionen EINES Kunden fuer die Vorbelegung des Entwurfs.
 *
 * Best effort wie resolveProjectRefs: die Kondition ist eine Vorbelegung,
 * keine Bedingung der Entstehung. Faengt insbesondere den Fall ab, dass
 * customers_skonto.sql in der Umgebung noch nicht gelaufen ist (unbekannte
 * Spalte → Beleg entsteht mit dem Standard der Konfiguration).
 */
const resolveCustomerTerms = async (customerId: string): Promise<CustomerTerms> => {
    try {
        const { data, error } = await supabase
            .from('customers')
            .select(CUSTOMER_TERMS_COLUMNS)
            .eq('id', customerId)
            .maybeSingle();
        if (error) throw error;
        if (data) return customerTermsFromRow(data);
    } catch (e) {
        console.warn('Kundenkonditionen nicht auflösbar', e);
    }
    return {};
};

/**
 * Konditionen VIELER Kunden in einer Abfrage — fuer den Abrechnungslauf.
 *
 * Der Lauf legt die Entwuerfe sequenziell an; eine Einzelabfrage je Gruppe
 * waere ein zusaetzlicher serieller Roundtrip pro Beleg. Wer hier fehlt (kein
 * Treffer, fehlgeschlagene Abfrage), bekommt in createInvoiceDraft weiterhin
 * die Einzelabfrage — der Lauf soll an den Konditionen nicht scheitern.
 */
export const fetchCustomerTerms = async (
    customerIds: string[],
): Promise<Map<string, CustomerTerms>> => {
    const out = new Map<string, CustomerTerms>();
    const ids = [...new Set(customerIds.filter(Boolean))];
    if (!ids.length) return out;
    try {
        const { data, error } = await supabase
            .from('customers')
            .select(`id, ${CUSTOMER_TERMS_COLUMNS}`)
            .in('id', ids);
        if (error) throw error;
        for (const row of data ?? []) out.set(row.id, customerTermsFromRow(row));
    } catch (e) {
        console.warn('Kundenkonditionen nicht auflösbar', e);
    }
    return out;
};

export const createInvoiceDraft = async (
    input: CreateInvoiceDraftInput,
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<Invoice> => {
    const documentType = input.documentType ?? 'INVOICE';
    const invoiceDate = input.invoiceDate ?? todayIso;
    // Zahlungsziel UND Skonto stammen aus derselben Kundenzeile und werden
    // gemeinsam beschafft: der Skonto-Satz nennt beide Fristen, und ein Kunde
    // mit 14 Tagen Ziel bekaeme sonst einen Beleg mit dem konfigurierten
    // Standard. Der Aufrufer schlaegt den Stammsatz, der Stammsatz die
    // Konfiguration; abgeleitete Belegarten bleiben beim Standard.
    const terms: CustomerTerms = CUSTOMER_TERMS_DOCUMENT_TYPES.has(documentType)
        ? (input.customerTerms ?? (await resolveCustomerTerms(input.customerId)))
        : {};
    const paymentTermsDays =
        input.paymentTermsDays ?? terms.paymentTermsDays ?? cfg.defaultPaymentTermsDays;
    // Der Satz kommt vom Beleg, falls der Aufrufer einen mitgibt; sonst aus der
    // Konfiguration. Gespeichert wird er in jedem Fall am Beleg — die
    // Konfiguration darf einen bestehenden Beleg spaeter nicht umrechnen.
    const ancillaryPercent = input.details?.ancillaryPercent ?? cfg.defaultAncillaryPercent;
    // Der Nachlass kommt AUSSCHLIESSLICH vom Aufrufer. Es gibt bewusst keinen
    // Konfigurations- oder Kundenstandard: ein stiller Durchgriff veraenderte
    // Betraege, ohne dass jemand ihn eintraegt.
    const documentDiscount = documentDiscountOf(input.details);
    // Der Sicherheitseinbehalt kommt AUSSCHLIESSLICH vom Aufrufer. Diese
    // Funktion laedt die Projektdaten nicht (resolveProjectRefs holt nur Nummer
    // und Name), und sie soll es fuer eine Vorbelegung auch nicht tun: der
    // Editor kennt den Projektwert (ProjectGlobalData.retention) bereits und
    // reicht ihn beim Anlegen mit. Ein stiller Durchgriff aus dem Projekt
    // veraenderte spaeter Betraege, ohne dass jemand sie am Beleg eintraegt —
    // dieselbe Begruendung wie beim Nachlass.
    const retentionPercent = input.details?.retentionPercent;
    // Reihenfolge ist tragend: ERST nachrechnen, DANN den Nachlass, ZULETZT den
    // Zuschlag. applyAncillaryCosts nimmt die Bemessungsgrundlage aus netCents,
    // und massgeblich ist netCents erst nach recalculateLines. Andersherum
    // rechnete der Zuschlag auf den Wert, den der Aufrufer mitgeschickt hat —
    // heute stimmt der zwar bei allen Aufrufern, aber die Annahme steht
    // nirgends. Und der Zuschlag rechnet auf die Honorarsumme ABZUEGLICH
    // Nachlaesse: entstuende der Nachlass erst danach, staende der Zuschlag auf
    // der ungeminderten Summe.
    const lines = nextInvoicePosNumbers(
        applyInvoiceSections(
            applyAncillaryCosts(
                applyDocumentDiscount(
                    recalculateLines(input.lines ?? []),
                    documentDiscount,
                    () => crypto.randomUUID(),
                ),
                ancillaryPercent,
                () => crypto.randomUUID(),
            ),
            input.details?.sections ?? [],
        ),
    );
    const projects = await resolveProjectRefs(input);

    // Skonto des Kunden an den Beleg: ausdrueckliche Vorgabe des Aufrufers
    // schlaegt die Kundenkondition; abgeleitete Belegarten bleiben ohne.
    const skonto = input.details?.skonto ?? terms.skonto;

    // Kostenstelle des zuletzt geschriebenen Belegs DESSELBEN PROJEKTS
    // uebernehmen. Der Betrieb schreibt seine Rechnungen in Serien zu einer
    // Baustelle; die Bezeichnung ("PSP-Element") und meist auch der Wert
    // bleiben dabei gleich. Der Bearbeiter kann beides in der Maske aendern
    // oder leeren.
    //
    // Der Projektbezug ist tragend: die global zuletzt verwendete Stelle gehoert
    // regelmaessig zu einer anderen Baustelle und damit zu einem anderen
    // Auftraggeber — sie einzutragen forderte den Empfaenger auf, auf ein Konto
    // zu buchen, das ihn nichts angeht. Ohne primaeres Projekt (Sammelrechnung)
    // gibt es deshalb keine Vorbelegung und auch keine Abfrage.
    //
    // Nur, wenn der Aufrufer nichts mitgibt und die Belegart es zulaesst
    // (wantsStickyCostCenter) — Storno, Korrektur und Gutschrift erben die
    // Stelle des Ursprungsbelegs und duerfen sich hier nichts einfangen. Und
    // nur dann wird ueberhaupt nachgeschlagen: der Aufruf kostet einen
    // Roundtrip, den die abgeleiteten Belege nicht bezahlen sollen.
    //
    // Denselben Weg geht der abweichende Projektname (details.projectNameOverride):
    // dieselbe Quelle, dieselbe Abfrage, dieselbe Regel. Gefragt wird, sobald
    // EINE der beiden Angaben fehlt — die Abfrage liefert ohnehin beide.
    const wantsCostCenter = wantsStickyCostCenter(documentType, input.details);
    const wantsProjectName = wantsStickyProjectName(documentType, input.details);
    const wantsAddressExtras = wantsStickyAddressExtras(documentType, input.details);
    const wantsCustomerOverride = wantsStickyCustomerOverride(documentType, input.details);
    const lastDefaults =
        wantsCostCenter || wantsProjectName || wantsAddressExtras || wantsCustomerOverride
            ? (input.lastDefaults !== undefined
                  ? input.lastDefaults
                  : await fetchLastUsedInvoiceDefault(input.primaryProjectId, input.customerId))
            : null;
    const stickyCostCenter = applyStickyCostCenter(
        documentType,
        input.details,
        lastDefaults?.costCenter,
    );
    const stickyProjectName = applyStickyProjectName(
        documentType,
        input.details,
        lastDefaults?.projectName,
    );
    const stickyAddressExtras = applyStickyAddressExtras(
        documentType,
        input.details,
        lastDefaults?.addressExtras,
    );
    // Und die abweichenden Kundenangaben des letzten Belegs desselben Projekts:
    // verlangt der Auftraggeber fuer DIESE Baustelle eine andere
    // Rechnungsanschrift, gilt das fuer Abschlag, Nachtrag und Schlussrechnung
    // gleichermassen und soll nicht an jedem Beleg neu eingetragen werden.
    //
    // Anders als Kostenstelle und Projektname haengt diese Angabe zusaetzlich am
    // DEBITOR (deshalb steht input.customerId in der Abfrage oben): wechselt ein
    // Projekt den Auftraggeber, adressierte eine rein projektbezogene
    // Vorbelegung den Beleg an den alten und buchte ihn auf den neuen.
    const stickyCustomerOverride = applyStickyCustomerOverride(
        documentType,
        input.details,
        lastDefaults?.customerOverride,
    );

    const row = toInvoiceRow({
        customerId: input.customerId,
        documentType,
        // Bewusst KEINE Nummer: die wird erst beim Ausstellen gezogen.
        series: resolveSeries(documentType, cfg),
        status: 'DRAFT',
        primaryProjectId: input.primaryProjectId,
        quoteId: input.quoteId,
        billingRunId: input.billingRunId,
        splitGroupId: input.splitGroupId,
        splitPartyId: input.splitPartyId,
        splitShareBp: input.splitShareBp,
        invoiceDate,
        deliveryDateFrom: input.deliveryDateFrom,
        deliveryDateTo: input.deliveryDateTo,
        dueDate: computeDueDate(invoiceDate, paymentTermsDays),
        paymentTermsDays,
        lines,
        advanceDeductions: [],
        advanceDeductionMode: cfg.advanceDeductionMode,
        // Ein frischer Entwurf hat keine Abzuege und damit keine Vorbelege:
        // prior 0. Die Liste entsteht in updateInvoice, sobald Abzuege dazu
        // kommen (createFinalInvoice) — dort werden die Vorbelege geladen.
        priorRetentions: [],
        // Der EFFEKTIVE Satz: eine Schlussrechnung mit Freigabe rechnet mit 0,
        // auch wenn der Aufrufer 5 % mitgibt. Der Satz selbst bleibt in den
        // Details stehen, damit ein spaeteres Abschalten der Freigabe ihn
        // wiederfindet.
        totals: invoiceTotalsWithRetention(documentType, lines, [], {
            percent: effectiveRetentionPercent(documentType, {
                retentionPercent,
                releaseRetention: input.details?.releaseRetention,
            }),
        }),
        details: {
            introText: cfg.defaultIntroText,
            outroText: cfg.defaultOutroText,
            paymentTerms: skonto
                ? skontoPaymentTermsText(invoiceDate, paymentTermsDays, skonto)
                : cfg.defaultPaymentTerms,
            bankAccount: { iban: cfg.iban, bic: cfg.bic, bankName: cfg.bankName },
            // Der Spread gewinnt: ein vom Aufrufer mitgegebener eigener
            // paymentTerms-Text schlaegt sowohl den Skonto-Text als auch den
            // Konfigurationsstandard.
            ...(input.details ?? {}),
            // Gedeckte Positionen (Monatspauschale) werden im Andruck
            // vorbelegt zusammengefasst: der Kunde soll die Pauschale sehen,
            // nicht den eingefrorenen Altbestand. Nur wenn der Aufrufer nichts
            // gesagt hat und der Beleg solche Zeilen ueberhaupt traegt — ein
            // Beleg ohne gedeckte Zeilen bekommt keinen Schalter, den nichts
            // liest. Altbelege ohne das Feld drucken wie bisher ('ALL').
            ...(input.details?.flatRateDisplay === undefined &&
            lines.some((l) => l.flatCovered === true)
                ? { flatRateDisplay: 'FLAT_ONLY' as const }
                : {}),
            // Nach dem Spread: der tatsaechlich angewandte Satz gehoert an den
            // Beleg, auch wenn er aus der Konfiguration stammt.
            //
            // Auch die Null: sie ist die ausdrueckliche Aussage "dieser Beleg
            // traegt keine Nebenkosten" und muss den spaeteren
            // Konfigurationsstandard schlagen. Nur ein fehlender Wert bedeutet
            // "nie festgelegt".
            ...(ancillaryPercent !== undefined ? { ancillaryPercent } : {}),
            // Und derselbe Grund fuer den Einbehalt: der angewandte Satz gehoert
            // an den Beleg. Auch hier ist die Null eine Aussage ("dieser Beleg
            // behaelt nichts ein") und kein fehlender Wert — sonst zoege eine
            // spaetere Vorbelegung sie nachtraeglich hoch.
            ...(retentionPercent !== undefined ? { retentionPercent } : {}),
            // Der Nachlass steht schon im Spread, wenn der Aufrufer ihn
            // mitgibt — hier wird nichts nachgetragen. Er hat keinen
            // Standardwert, den der Beleg festhalten muesste.
            // Ebenfalls nach dem Spread: der Aufrufer darf einen eigenen
            // Vermerk mitgeben, resolveProjectRefs reicht ihn dann unveraendert
            // durch. Leer bleibt leer — eine Rechnung ohne Projektbezug (Storno,
            // kaufmaennische Gutschrift) soll keine erfundene Angabe tragen.
            ...(projects.length ? { projects } : {}),
            ...(skonto ? { skonto } : {}),
            // Leer, sobald der Aufrufer selbst eine Kostenstelle mitgibt — die
            // Vorbelegung darf eine ausdrueckliche Angabe nie ueberschreiben.
            ...stickyCostCenter,
            // Ebenso der abweichende Projektname des letzten Belegs desselben
            // Projekts: heisst die Baustelle auf den Rechnungen an den
            // Auftraggeber "SuedLink Los 5 und 6", soll das nicht bei jedem
            // Beleg neu getippt werden. Ohne Vorbelegung bleibt es beim echten
            // Projektnamen.
            ...stickyProjectName,
            // Und die Adress-Zusatzzeilen (unter der Empfaengeradresse im
            // Anschriftfeld) — gleiche Mechanik, gleiche Quelle.
            ...stickyAddressExtras,
            // Ebenso die belegbezogene Kundenabweichung: sie gehoert dem
            // Projekt, nicht dem einzelnen Beleg.
            ...stickyCustomerOverride,
        },
    });

    const { data, error } = await supabase.from('invoices').insert(row).select(SELECT).single();
    if (error) throw error;
    return mapInvoiceRow(data);
};

export interface CreateSplitDraftsInput {
    /**
     * Die Vorlage: volle, UNGETEILTE Zeilen und alles, was fuer jeden Beleg der
     * Gruppe gleich ist. customerId darin wird je Teilnehmer ersetzt.
     */
    base: CreateInvoiceDraftInput;
    split: ProjectSplit;
    /**
     * Leistungsprotokoll des Projekts — Quelle der Staende je Auftraggeber.
     * Ohne Angabe wird es geladen.
     */
    serviceLog?: ServiceTransaction[];
    /**
     * Bereits ausgestellte Belege des Projekts, fuer die Deckung je Kunde.
     * Ohne Angabe werden sie geladen.
     */
    priorInvoices?: Invoice[];
    /** Kumulativ? Nur dann tragen die Zeilen einen Vorabrechnungsstand. */
    cumulative: boolean;
    /**
     * Belegart mit ABZUG (Schluss-, Rest- oder Teilleistungsrechnung).
     *
     * Gesetzt laeuft jeder Beleg der Gruppe ueber createFinalInvoice und zieht
     * dabei AUSSCHLIESSLICH die Vorrechnungen seines eigenen Auftraggebers ab
     * (customerScope). Ohne diese Einschraenkung zoege die Teilrechnung des
     * einen auch die Zahlungen der uebrigen ab und forderte fast nichts mehr —
     * und der Beleg saehe dabei richtig aus.
     */
    deduction?: { documentType: 'FINAL_INVOICE' | 'REMAINING_INVOICE' | 'INVOICE' };
}

/**
 * Stellt den Abschnitt in den Betreffblock des Belegs.
 *
 * `subjectNote` und kein eigenes Feld: das ist genau der freie Zusatztext im
 * Kopf ("Bauteil B, Vergabenummer …"), er wird bereits vom PDF UND von beiden
 * E-Rechnungsformaten ausgegeben. Ein neues Feld daneben muesste an allen drei
 * Stellen nachgezogen werden und waere dort dasselbe.
 *
 * Ohne Abschnitt bleibt alles unveraendert — auch die Abwesenheit des Feldes,
 * damit ungeteilte Belege Zeichen fuer Zeichen dieselben bleiben.
 */
const withSectionNote = (
    details: Invoice['details'] | undefined,
    participant: ProjectSplitParticipant,
): Invoice['details'] | undefined => {
    const label = (participant.label || '').trim();
    if (!label) return details;
    const vorhanden = (details?.subjectNote || '').trim();
    return {
        ...(details ?? {}),
        subjectNote: vorhanden ? `Abschnitt: ${label}\n${vorhanden}` : `Abschnitt: ${label}`,
    } as Invoice['details'];
};

/**
 * Legt aus EINER Auswahl die Teilrechnungen aller Teilnehmer an.
 *
 * Ein Beleg je Auftraggeber, alle mit derselben Gruppe. Nicht ein Beleg mit
 * mehreren Empfaengern: jeder Auftraggeber braucht eigene Nummer, eigenen
 * Kundenschnappschuss und eigenen Debitor — ein Beleg mit mehreren Koepfen
 * waere keiner.
 *
 * DIE REIHENFOLGE IST TRAGEND. Erst pruefen, dann anlegen: eine Gruppe, deren
 * Anteile nicht auf 100 % kommen, darf gar nicht erst entstehen. Ist sie
 * einmal da, wird ihr Fehler erst auf den fertigen Belegen sichtbar — und dann
 * fehlt dieselbe Menge bei allen Beteiligten gleichzeitig.
 *
 * Und alles-oder-nichts: scheitert ein Beleg, werden die bereits angelegten
 * wieder entfernt. Eine halbe Gruppe ist der gefaehrlichste Zustand ueberhaupt
 * — sie sieht vollstaendig aus, fordert aber nur einen Teil, und das faellt
 * niemandem auf, weil jeder einzelne Beleg fuer sich stimmt.
 */
export const createSplitInvoiceDrafts = async (
    input: CreateSplitDraftsInput,
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<Invoice[]> => {
    assertCryptoAvailable();

    const projectId = input.base.primaryProjectId;
    if (!projectId) {
        throw new Error('Eine Teilrechnung braucht das Projekt — der Aufteilungsschlüssel gehört ihm.');
    }

    // S5a Stufe 3c, Paket T: der Tor-Notschalter frisch und, nur im
    // Journal-Modus, das Journal — angestossen, NIE abgewartet. Massgeblich
    // bleibt das JSON bzw. `input.serviceLog`; der Schatten unten rechnet die
    // drei Groessen nur nach und meldet (torLeserSchatten.ts). Ein Ladefehler
    // sperrt hier nicht, er wird gemeldet.
    const aufteilungsJournal: Promise<ServiceJournalRow[] | null | 'json'> = fetchTorNotschalter()
        .then((stand): Promise<ServiceJournalRow[] | null | 'json'> =>
            stand.modus === 'json' ? Promise.resolve('json') : loadJournal(projectId),
        )
        .catch(() => null);

    // Protokoll und Vorbelege sind Pflichtstoff der Rechnung, kein Beiwerk:
    // ohne sie liesse sich der Stand je Auftraggeber nicht bilden. Der Aufrufer
    // darf sie mitgeben (er hat sie oft schon), sonst werden sie hier geholt —
    // besser ein zusaetzlicher Roundtrip als ein Aufrufer, der sie vergisst.
    const serviceLog =
        input.serviceLog ??
        (((await loadProjectById(projectId))?.data as Report | undefined)?.metadata as
            | ReportMetadata
            | undefined)?.globalData?.serviceLog ??
        [];
    const priorInvoices = input.priorInvoices ?? (await fetchProjectInvoices(projectId));

    const baseLines = input.base.lines ?? [];
    const serviceIds = [
        ...new Set(
            baseLines.filter((l) => l.kind === 'SERVICE' && l.serviceId).map((l) => l.serviceId as string),
        ),
    ];

    if (!hasSplit(input.split)) {
        throw new Error(
            'Für eine Teilrechnung braucht es mindestens zwei Auftraggeber im Aufteilungsschlüssel.',
        );
    }

    const issues = validateSplit(serviceIds, input.split);
    if (issues.length) {
        throw new Error('Der Aufteilungsschlüssel stimmt nicht: ' + issues[0].message);
    }

    // Nicht zugeordneter Altbestand sperrt. Ihn einem Teilnehmer zuzuschlagen
    // waere geraten, ihn zu uebergehen rechnete ihn ein zweites Mal ab.
    //
    // `legacy` ist dabei der Unterschied zwischen "geht" und "geht nicht": ein
    // Projekt, das sich bisher nur nach Auftraggebern teilte, hat je
    // Auftraggeber genau einen Teilnehmer — seine Altzeilen sind damit
    // eindeutig und keine Sperre. Erst wenn ein Auftraggeber MEHRERE Abschnitte
    // bekommt, wird seine ungestempelte Altmenge unentscheidbar.
    const legacy = legacyPartyByCustomer(input.split);
    const blockers = unassignedBilledIssues(serviceIds, serviceLog, legacy);
    // Die Staende je Teilnehmer — rein aus Protokoll und Schluessel, deshalb
    // schon hier und nicht erst nach den Riegeln: der Leser-Schatten (S5a 3c)
    // haelt alle drei Groessen fest, auch wenn gleich ein Riegel wirft.
    const billedStands = billedByParty(serviceLog, legacy);
    const altStands = altCoveredByParty(serviceLog, legacy);
    starteAufteilungsSchatten(
        () =>
            aufteilungsAufnahme({
                kopf: {
                    projectId,
                    projectNumber: null,
                    invoiceId: 'neu',
                    invoiceNumber: null,
                    zeitraum: { from: input.base.deliveryDateFrom, to: input.base.deliveryDateTo },
                },
                serviceIds,
                legacy,
                jsonLog: serviceLog,
                blockers,
                billedStands,
                altStands,
            }),
        aufteilungsJournal,
    );
    if (blockers.length) {
        throw new Error(
            blockers[0].message +
                ' Bitte den Altbestand zuerst einem Teilnehmer der Aufteilung zuordnen.',
        );
    }

    // Dasselbe fuer die Alt-Abschlaege: ein Abzugsposten ohne Auftraggeber
    // laesst sich weder einem zuschlagen noch anteilig verteilen, ohne zu
    // raten. Und geraten wird hier an einem Geldbetrag.
    if (input.deduction && input.cumulative) {
        // Nicht mehr nur "ohne Kunden": auch ein Alt-Abschlag MIT Kunden ist
        // unentscheidbar, wenn dieser Kunde mehrere Abschnitte hat. Beide Faelle
        // beantwortet `resolveParty` mit derselben Antwort — keiner.
        const offen = (await fetchExternalAdvances(projectId)).filter(
            (a) => !resolveParty(a, legacy),
        );
        if (offen.length) {
            throw new Error(
                `${offen.length} Alt-Abschlag/Alt-Abschläge (${offen
                    .map((a) => a.invoiceNumber)
                    .join(', ')}) sind keinem Teilnehmer der Aufteilung zugeordnet. Ihr Abzug ließe sich nur raten — bitte den Teilnehmer in den Vorrechnungen nachtragen.`,
            );
        }
    }

    const perParty = splitLines(
        baseLines,
        input.split,
        input.cumulative ? billedStands : undefined,
    );

    const splitGroupId = crypto.randomUUID();
    const created: Invoice[] = [];

    /**
     * Aufraeumen nach einem Abbruch — und MELDEN, was nicht wegging.
     *
     * Frueher wurde ein gescheitertes Loeschen verschluckt. Damit blieb ein
     * Entwurf der halben Gruppe als gewoehnlicher Entwurf stehen: er sieht
     * richtig aus, haelt aber eine Reservierung und fordert einen Anteil, dem
     * kein Gegenstueck mehr gegenuebersteht. Wer das nicht erfaehrt, findet es
     * erst auf der naechsten Rechnung — oder nie.
     *
     * Der Riegel beim Ausstellen (groupCoverageIssues) faengt den Fall
     * zusaetzlich ab. Beides zusammen: hier wird es gesagt, dort wird es
     * verhindert.
     */
    const rollback = async (): Promise<string[]> => {
        const leftovers: string[] = [];
        for (const inv of created) {
            try {
                await deleteInvoice(inv.id);
            } catch {
                leftovers.push(inv.id);
            }
        }
        return leftovers;
    };

    const withLeftovers = (message: string, leftovers: string[]): Error =>
        new Error(
            leftovers.length
                ? `${message} ACHTUNG: ${leftovers.length} bereits angelegte(r) Entwurf/Entwürfe konnten nicht entfernt werden (${leftovers.join(', ')}). Sie halten eine Reservierung und müssen von Hand gelöscht werden.`
                : message,
        );

    try {
        for (const participant of input.split.participants) {
            const lines = perParty.get(participant.partyId) ?? [];
            // Ein Teilnehmer ohne eine einzige Leistungszeile bekommt keinen
            // Beleg. Bei positionsweiser Aufteilung ist das der Normalfall:
            // seine Positionen sind in diesem Zeitraum nicht angefallen. Eine
            // Rechnung ueber nichts waere kein Beleg, sondern eine verbrauchte
            // Nummer.
            if (!lines.some((l) => l.kind === 'SERVICE')) continue;

            // Die Deckung durch BELEGE, je Auftraggeber. Gegenstueck zu
            // previouslyBilledQty (dem Stand des Leistungsverzeichnisses); die
            // Differenz beider ist die manuelle Verbuchung ohne Beleg, die
            // uncoveredPriorBilling sichtbar macht. Mit der ungeteilten Deckung
            // bestueckt meldete jeder Teilbeleg eine Luecke, die es nicht gibt.
            //
            // Eingegrenzt wird auf den TEILNEHMER: vier Haeuser desselben
            // Auftraggebers haben vier getrennte Deckungen, und ueber den
            // Auftraggeber gerechnet meldete jedes Haus die Vorbelege der
            // anderen drei als eigene Deckung.
            const scope = partyScopeFor(input.split, participant);
            const eigene = scopeToParty(priorInvoices, scope);
            if (eigene.ambiguous.length) {
                throw new Error(
                    `${eigene.ambiguous.length} Beleg(e) dieses Auftraggebers (${eigene.ambiguous
                        .map((i) => i.invoiceNumber ?? '(Entwurf)')
                        .join(', ')}) stammen aus der Zeit vor den Abschnitten und lassen sich keinem davon zuordnen. Solange sie offen sind, ließe sich die Deckung von „${participantLabel(participant)}" nur raten.`,
                );
            }
            const invoicedByService = invoicedQuantityByService(eigene.own);

            const withCoverage = lines.map((l) => {
                if (!input.cumulative || l.previouslyBilledQty === undefined || !l.serviceId) {
                    return l;
                }
                return {
                    ...l,
                    previouslyInvoicedQty:
                        (invoicedByService.get(l.serviceId) ?? 0) +
                        billedFor(altStands.get(l.serviceId), participant.partyId),
                };
            });

            // Der Anteil AM BELEG ist eine Angabe fuer Anzeige und Andruck. Nur
            // wenn alle Leistungszeilen denselben Anteil tragen, gibt es
            // ueberhaupt einen; bei positionsweiser Aufteilung gaebe jede Zahl
            // hier ein falsches Bild, und der Anteil steht dann ausschliesslich
            // an den Zeilen.
            const shares = new Set(
                withCoverage.filter((l) => l.kind === 'SERVICE').map((l) => l.splitShareBp),
            );
            const uniformShare = shares.size === 1 ? [...shares][0] : undefined;

            // Konditionen und Vorbelegungen gehoeren dem jeweiligen Kunden und
            // duerfen nicht vom ersten geerbt werden: Zahlungsziel und Skonto
            // stehen an SEINEM Stammsatz.
            const gemeinsam = {
                ...input.base,
                customerId: participant.customerId,
                lines: withCoverage,
                splitGroupId,
                splitPartyId: participant.partyId,
                splitShareBp: uniformShare,
                customerTerms: undefined,
                lastDefaults: undefined,
                // DER ABSCHNITT GEHOERT AUF DEN BELEG. Bei mehreren Haeusern
                // desselben Auftraggebers ist er das Einzige, was die Belege
                // unterscheidet — ohne ihn liegen vier Rechnungen mit
                // identischem Betreff beim Kunden, und niemand weiss, welche
                // welches Haus abrechnet. Er steht im Betreffblock (subjectNote,
                // vom PDF und von der E-Rechnung gelesen) und ueberschreibt
                // einen vorhandenen Zusatztext NICHT, sondern stellt sich davor.
                details: withSectionNote(input.base.details, participant),
            };

            if (input.deduction) {
                const { invoice } = await createFinalInvoice(
                    {
                        ...gemeinsam,
                        primaryProjectId: projectId,
                        documentType: input.deduction.documentType,
                        cumulative: input.cumulative,
                        // Der Kern der Sache: nur SEINE Vorrechnungen.
                        splitScope: scope,
                    },
                    cfg,
                    todayIso,
                );
                created.push(invoice);
            } else {
                created.push(await createInvoiceDraft(gemeinsam, cfg, todayIso));
            }
        }
    } catch (e) {
        const leftovers = await rollback();
        if (!leftovers.length) throw e;
        throw withLeftovers(e instanceof Error ? e.message : String(e), leftovers);
    }

    // KEINE Mindestzahl von zwei Belegen.
    //
    // Bei positionsweiser Aufteilung ist ein einzelner Beleg der Normalfall:
    // in diesem Zeitraum sind nur Positionen EINES Auftraggebers angefallen,
    // die der uebrigen kommen spaeter. Auf zwei zu bestehen machte solche
    // Zeitraeume unfakturierbar — und der Beleg selbst waere richtig gewesen.
    //
    // Dass er nichts verschluckt, sichert der Riegel beim Ausstellen:
    // groupCoverageIssues prueft je Position, ob die Gruppe zusammen 100 %
    // traegt. Eine Position, die zu 100 % diesem einen gehoert, besteht sie —
    // eine geteilte ohne Geschwisterbeleg nicht.
    if (!created.length) {
        const leftovers = await rollback();
        throw withLeftovers(
            'Die Aufteilung ergab für keinen Auftraggeber eine abrechenbare Position.',
            leftovers,
        );
    }

    return created;
};


/**
 * Liest die gespeicherten Abschlagsabzuege eines Belegs.
 *
 * Eigene, schmale Abfrage statt fetchInvoice: updateInvoice braucht nur dieses
 * eine Feld, und der Aufruf liegt im Autosave-Pfad.
 */
/**
 * Nebenkostensatz des Belegs. Wie bei den Abschlagsabzuegen: der Editor schickt
 * beim Autosave nur Zeilen, und ohne den gespeicherten Satz fiele der Zuschlag
 * bei jeder Aenderung weg.
 */
const fetchLineModifiers = async (
    id: string,
): Promise<{
    ancillaryPercent?: number;
    discount: DocumentDiscount;
    sections?: InvoiceSection[];
    documentType: DocumentType;
    primaryProjectId?: string;
    retentionPercent?: number;
    releaseRetention?: boolean;
    priorRetentions: PriorRetention[];
    /**
     * Die gespeicherten Einbehalt-Felder — nur fuer den Stornobeleg von
     * Bedeutung: er SPIEGELT die Werte des Originals und rechnet nicht neu.
     * Beim Autosave eines Stornoentwurfs (heute unerreichbar, der Storno wird
     * sofort ausgestellt) muessen sie stehen bleiben.
     */
    storedRetention: RetentionTotals;
}> => {
    const { data, error } = await supabase
        .from('invoices')
        // Belegart und Projekt gehoeren in dieselbe Abfrage: der
        // Sicherheitseinbehalt haengt an beidem. Die Belegart entscheidet ueber
        // den effektiven Satz (Schlussrechnung mit Freigabe: 0) und ob der
        // Einbehalt gespiegelt wird (Storno); das Projekt liefert die
        // Vorbelege, aus denen die Vorbelegliste entsteht.
        .select(
            'details, document_type, primary_project_id, retention_releases, prepaid_cents, retention_cents, retention_release_cents',
        )
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    const details = data?.details as Invoice['details'] | null;
    const totals = mapTotals(data ?? {});
    return {
        ancillaryPercent: details?.ancillaryPercent,
        discount: documentDiscountOf(details),
        // Die Gliederung gehoert in dieselbe Abfrage: sie ordnet die Zeilen,
        // und der Editor schickt beim Autosave nur Zeilen. Ohne den
        // gespeicherten Stand fielen die Abschnittsueberschriften bei jeder
        // Aenderung weg und die Gruppierung mit ihnen.
        sections: details?.sections,
        documentType: (data?.document_type ?? 'INVOICE') as DocumentType,
        primaryProjectId: data?.primary_project_id ?? undefined,
        retentionPercent: details?.retentionPercent,
        releaseRetention: details?.releaseRetention,
        // Fehlertolerant wie im Row-Mapper: ohne Migration Nr. 104 gibt es die
        // Spalte nicht, und eine leere Liste ist dort die Wahrheit.
        priorRetentions: mapPriorRetentions(data?.retention_releases),
        storedRetention: retentionTotalsOf(totals),
    };
};

/**
 * Die gespeicherten Zeilen und Abzuege eines Belegs.
 *
 * Nur fuer den Fall, dass sich AUSSCHLIESSLICH der Sicherheitseinbehalt aendert
 * — dann schickt der Aufrufer keine Zeilen, die Summen muessen aber trotzdem
 * neu entstehen. `lines` ist die mit Abstand groesste Spalte der Tabelle
 * (siehe SELECT_WITHOUT_LINES); sie wird deshalb NUR in diesem Fall geladen und
 * nicht bei jedem Autosave.
 */
const fetchStoredLinesAndDeductions = async (
    id: string,
): Promise<{ lines: InvoiceLine[]; deductions: AdvanceDeduction[] }> => {
    const { data, error } = await supabase
        .from('invoices')
        .select('lines, advance_deductions')
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    return {
        lines: Array.isArray(data?.lines) ? (data.lines as InvoiceLine[]) : [],
        deductions: Array.isArray(data?.advance_deductions)
            ? (data.advance_deductions as AdvanceDeduction[])
            : [],
    };
};

/**
 * Die fuenf Einbehalt-Felder aus gespeicherten Summen — das, was ein Storno
 * beim Ausstellen SPIEGELT statt neu zu rechnen (invoiceTotalsWithRetention,
 * fuenfter Parameter). Altbestand ohne die Felder ergibt 0.
 */
const retentionTotalsOf = (totals: InvoiceTotals): RetentionTotals => ({
    cumulativeRetentionCents: totals.cumulativeRetentionCents ?? 0,
    priorRetentionCents: totals.priorRetentionCents ?? 0,
    retentionCents: totals.retentionCents ?? 0,
    retentionReleaseCents: totals.retentionReleaseCents ?? 0,
    paidPriorCents: totals.paidPriorCents ?? 0,
});

/**
 * Die Vorbelegliste, mit der ein Beleg zu rechnen ist (Invoice.priorRetentions).
 *
 * Drei Faelle, und die Unterscheidung ist tragend:
 *   - Der Aufrufer gibt eine Liste mit: sie gilt. So kommt die GESPIEGELTE
 *     Liste eines Stornos an den Beleg, und so spart createFinalInvoice die
 *     zweite Abfrage der Vorbelege, die es gerade selbst geladen hat.
 *   - Stornobeleg: die gespeicherte Liste bleibt stehen. Seine Abzuege sind
 *     gespiegelt (negativ); aus ihnen neu gerechnet, kaeme die Liste mit dem
 *     Vorzeichen der Vorbelege zurueck — also POSITIV — und das Storno hoebe
 *     das Original nicht mehr auf.
 *   - Alles andere: frisch aus den Abzuegen. Je abgezogenem Vorbeleg sein
 *     Nettoeinbehalt plus Backfill, dazu forderungslose Vorbelege mit
 *     Freigabe (priorRetentionsFromDeductions). Der Stand kann sich seit dem
 *     letzten Speichern geaendert haben (Backfill nachgetragen, Vorbeleg
 *     storniert), und der Entwurf muss ihm folgen — deshalb bei JEDEM
 *     Autosave, nicht nur bei einer Aenderung am Einbehalt.
 *
 * Ohne Abzuege ist die Liste leer, und das ohne Abfrage: ein fortschreibender
 * Beleg hat prior 0, weil gross dort nur die eigene neue Leistung ist. Die
 * Vorbelege und den Backfill zu laden, kostete zwei Roundtrips im
 * Autosave-Pfad fuer ein Ergebnis, das feststeht.
 */
const resolvePriorRetentions = async (
    id: string,
    explicit: PriorRetention[] | undefined,
    documentType: DocumentType,
    primaryProjectId: string | undefined,
    deductions: AdvanceDeduction[],
    stored: PriorRetention[],
): Promise<PriorRetention[]> => {
    if (explicit !== undefined) return explicit;
    if (documentType === 'CANCELLATION_INVOICE') return stored;
    if (deductions.length === 0 || !primaryProjectId) return [];
    const [projectInvoices, backfill] = await Promise.all([
        fetchProjectInvoices(primaryProjectId),
        fetchRetentionBackfill(primaryProjectId),
    ]);
    return priorRetentionsFromDeductions(deductions, projectInvoices, backfill, id);
};

/** Aufruferseitige Zusaetze zu updateInvoice, die kein Belegfeld sind. */
export interface UpdateInvoiceOptions {
    /**
     * Storno: die NEGIERTEN Einbehalt-Felder des Originals
     * (mirrorRetentionTotals). Der Stornobeleg uebernimmt sie wortwoertlich
     * statt neu zu rechnen — er hebt genau die Werte auf, die der Beleg
     * getragen hat, auch wenn das Original noch nach dem alten inkrementellen
     * Modell gerechnet wurde. Nur fuer CANCELLATION_INVOICE von Bedeutung;
     * invoiceTotalsWithRetention ignoriert den Wert bei allen anderen Arten.
     */
    mirroredRetention?: RetentionTotals;
}

const fetchAdvanceDeductions = async (id: string): Promise<AdvanceDeduction[]> => {
    const { data, error } = await supabase
        .from('invoices')
        .select('advance_deductions')
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    return (data?.advance_deductions as AdvanceDeduction[] | null) ?? [];
};

/**
 * Aktualisiert einen Entwurf. Summen und Positionsnummern werden dabei neu
 * gerechnet, damit sie nie von den Zeilen abweichen koennen.
 *
 * Ab der Ausstellung lehnt der Datenbank-Trigger jede Aenderung an Belegdaten
 * ab; diese Funktion verhindert das nicht zusaetzlich, sondern verlaesst sich
 * bewusst auf den Trigger als einzige Wahrheit.
 */
export const updateInvoice = async (
    id: string,
    updates: Partial<Invoice>,
    options: UpdateInvoiceOptions = {},
): Promise<Invoice> => {
    const next = { ...updates };
    // Der Sicherheitseinbehalt haengt NICHT an den Zeilen: ein geaenderter Satz
    // oder der Schalter "Einbehalte freigeben" muss die Summen auch dann neu
    // bilden, wenn der Aufrufer gar keine Zeilen schickt. Ohne diesen Zweig
    // stuende der neue Satz in den Details, waehrend payable_cents noch den
    // alten Zahlbetrag traegt — der Beleg widerspraeche sich selbst.
    const touchesRetention =
        updates.details?.retentionPercent !== undefined ||
        updates.details?.releaseRetention !== undefined ||
        updates.priorRetentions !== undefined ||
        options.mirroredRetention !== undefined;
    if (updates.lines || touchesRetention) {
        // Nachlass UND Nebenkostenzuschlag haengen an der Honorarsumme und
        // muessen deshalb bei jeder Aenderung der Positionen neu gebildet
        // werden. Sonst bliebe der Zuschlag auf dem Betrag stehen, der vor der
        // Aenderung galt.
        //
        // Beide Werte kommen aus DEMSELBEN gespeicherten details-Objekt: der
        // Editor schickt beim Autosave nur Zeilen, und ohne den gespeicherten
        // Stand fielen Nachlass und Zuschlag bei jeder Aenderung weg. Ein vom
        // Aufrufer mitgegebenes details schlaegt den gespeicherten Stand je
        // Feld — die ausdrueckliche Null bleibt dabei eine Aussage und wird
        // nicht vom gespeicherten Wert ueberholt.
        const wantsStoredDiscount =
            updates.details?.discountPercent === undefined &&
            updates.details?.discountAmountCents === undefined;
        // Frueher nur geladen, wenn eine der drei Angaben fehlte. Jetzt IMMER:
        // der Sicherheitseinbehalt braucht Belegart, Projekt und die
        // gespeicherte Vorbelegliste, und die stehen in derselben Zeile —
        // eine zweite Abfrage waere dieselbe Zeile ein zweites Mal.
        const stored = await fetchLineModifiers(id);
        const percent = updates.details?.ancillaryPercent ?? stored.ancillaryPercent;
        const discount = wantsStoredDiscount
            ? (stored.discount ?? {})
            : documentDiscountOf(updates.details);
        const sections = updates.details?.sections ?? stored.sections ?? [];
        // Erst nachrechnen, dann den Nachlass, zuletzt den Zuschlag — siehe
        // createInvoiceDraft. Die Gliederung kommt danach: sie ordnet die
        // fertigen Zeilen und stellt die erzeugten (Nachlass, Zuschlag) ans
        // Ende. Nummeriert wird zuallerletzt, damit die Nummern der
        // gedruckten Reihenfolge folgen.
        //
        // Ohne mitgeschickte Zeilen (reine Einbehalt-Aenderung) bleiben die
        // gespeicherten Zeilen UNANGETASTET: sie durch die Aufbereitung zu
        // schicken, erzeugte neue Ids fuer Nachlass- und Zuschlagszeilen und
        // verstellte die Gliederung, ohne dass jemand eine Position geaendert
        // haette. Gerechnet wird mit ihnen, geschrieben werden sie nicht.
        const storedRecord = updates.lines
            ? undefined
            : await fetchStoredLinesAndDeductions(id);
        const lines = updates.lines
            ? nextInvoicePosNumbers(
                  applyInvoiceSections(
                      applyAncillaryCosts(
                          applyDocumentDiscount(recalculateLines(updates.lines), discount, () =>
                              crypto.randomUUID(),
                          ),
                          percent,
                          () => crypto.randomUUID(),
                      ),
                      sections,
                  ),
              )
            : (storedRecord?.lines ?? []);
        if (updates.lines) next.lines = lines;

        // Die Abschlagsabzuege muessen mitgerechnet werden, auch wenn der
        // Aufrufer nur Zeilen schickt — und das tut der Editor bei jedem
        // Autosave. Mit einer leeren Abzugsliste faellt prepaidCents auf 0 und
        // payableCents auf den ungekuerzten Bruttobetrag zurueck, waehrend die
        // Abzuege im Datensatz stehen bleiben: der Beleg zeigt dann den
        // Abzugsblock UND fordert die bereits gezahlten Abschlaege erneut.
        const deductions =
            updates.advanceDeductions ??
            storedRecord?.deductions ??
            (await fetchAdvanceDeductions(id));

        // Und dieselbe Falle beim Sicherheitseinbehalt: der Editor schickt beim
        // Autosave nur Zeilen. Ohne den gespeicherten Satz fiele retention_cents
        // auf 0 und payable_cents auf den ungekuerzten Betrag zurueck, waehrend
        // details.retentionPercent im Datensatz stehen bliebe — der Beleg
        // druckte die Einbehaltzeile UND forderte den vollen Betrag.
        //
        // Ein mitgegebener Wert schlaegt den gespeicherten je Feld: die
        // ausdrueckliche Null ("kein Einbehalt mehr") ist eine Aussage und darf
        // nicht vom gespeicherten Satz ueberholt werden.
        const retentionPercent = updates.details?.retentionPercent ?? stored.retentionPercent;
        const releaseRetention = updates.details?.releaseRetention ?? stored.releaseRetention;
        const priorRetentions = await resolvePriorRetentions(
            id,
            updates.priorRetentions,
            stored.documentType,
            stored.primaryProjectId,
            deductions,
            stored.priorRetentions,
        );
        next.priorRetentions = priorRetentions;
        // Der Storno spiegelt (mirroredRetention vom Aufrufer, sonst die
        // gespeicherten Felder, die er beim Anlegen bekommen hat); alle anderen
        // Belegarten rechnen kumulativ — invoiceTotalsWithRetention laesst den
        // fuenften Parameter dort unbeachtet.
        const mirrored =
            stored.documentType === 'CANCELLATION_INVOICE'
                ? (options.mirroredRetention ?? stored.storedRetention)
                : undefined;
        next.totals = invoiceTotalsWithRetention(
            stored.documentType,
            lines,
            deductions,
            {
                percent: effectiveRetentionPercent(stored.documentType, {
                    retentionPercent,
                    releaseRetention,
                }),
                priorRetentions,
            },
            mirrored,
        );
    }
    if (updates.invoiceDate && updates.paymentTermsDays !== undefined) {
        next.dueDate = computeDueDate(updates.invoiceDate, updates.paymentTermsDays);
    }

    const { data, error } = await supabase
        .from('invoices')
        .update(toInvoiceRow(next))
        .eq('id', id)
        .select(SELECT)
        .single();
    if (error) throw error;
    return mapInvoiceRow(data);
};

/** Loeschen nur im Entwurf — der Trigger setzt das hart durch. */
export const deleteInvoice = async (id: string): Promise<void> => {
    const attempt = () => supabase.from('invoices').delete().eq('id', id).select('id');

    let { data, error } = await attempt();

    // invoice_relations verweist per Fremdschluessel auf beide Seiten und kennt
    // kein ON DELETE CASCADE. Ein Entwurf, der bereits mit seinem Ursprungsbeleg
    // verbunden ist (Gutschrift, Storno, Korrektur), liess sich deshalb gar
    // nicht mehr loeschen — die Oberflaeche zeigte die rohe
    // Fremdschluesselverletzung aus der Datenbank.
    //
    // Die Verknuepfungen werden ERST NACH einem gescheiterten Loeschversuch
    // entfernt, nicht vorher. Andernfalls haette ein Loeschversuch, den der
    // Trigger anschliessend ablehnt (Beleg inzwischen ausgestellt), die
    // Verknuepfungen eines gueltigen Belegs zerstoert — und damit die Spur
    // zwischen einem Storno und seiner Ursprungsrechnung, genau das, wofuer die
    // Tabelle da ist.
    if (error && (error as { code?: string }).code === '23503') {
        for (const column of ['from_invoice_id', 'to_invoice_id']) {
            const { error: relError } = await supabase
                .from('invoice_relations')
                .delete()
                .eq(column, id);
            if (relError) throw relError;
        }
        ({ data, error } = await attempt());
    }

    if (error) throw error;
    // Null geloeschte Zeilen meldet PostgREST als Erfolg. Die Oberflaeche
    // navigiert unmittelbar danach zurueck: ohne diese Pruefung verschwaende
    // der Entwurf scheinbar, bliebe aber bestehen — und wuerde ueber
    // v_invoice_line_reservations weiterhin genau die Mengen blockieren, die
    // der Nutzer gerade freigeben wollte.
    if (!data?.length) {
        throw new Error(
            'Der Entwurf wurde nicht gelöscht — er wurde nicht gefunden, ist nicht mehr im Entwurfsstatus, oder der Zugriff wurde verweigert.',
        );
    }
};

// ─── Statuspfad ─────────────────────────────────────────────────────────────

const setStatus = async (
    id: string,
    from: Invoice['status'],
    to: Invoice['status'],
    extra: Record<string, any> = {},
) => {
    if (!canTransitionInvoice(from, to)) {
        throw new Error(`Statuswechsel von ${from} nach ${to} ist nicht vorgesehen.`);
    }
    // Die Bedingung auf den Ausgangsstatus gehoert in die Datenbank, nicht nur
    // in die Pruefung darueber: `from` stammt aus einem Objekt im Speicher, das
    // beliebig alt sein kann. Ohne sie holt ein zweiter Nutzer mit veralteter
    // Ansicht eine laengst ausgestellte Rechnung nach APPROVED zurueck — der
    // Festschreibungs-Trigger greift nicht, weil der Status ausdruecklich
    // aenderbar bleibt — und stellt sie ein zweites Mal aus. Der Beleg traegt
    // danach eine andere Nummer als sein archiviertes PDF.
    const { data, error } = await supabase
        .from('invoices')
        .update({ status: to, ...extra })
        .eq('id', id)
        .eq('status', from)
        .select('id');
    if (error) throw error;
    if (!data?.length) {
        throw new Error(
            `Der Beleg steht nicht mehr auf "${from}" — jemand anderes hat ihn inzwischen geändert. Bitte neu laden.`,
        );
    }
};

export const submitForReview = (inv: Invoice) => setStatus(inv.id, inv.status, 'READY_FOR_REVIEW');

export const approveInvoice = (inv: Invoice, actorId: string) =>
    setStatus(inv.id, inv.status, 'APPROVED', {
        approved_at: new Date().toISOString(),
        approved_by: actorId,
    });

/**
 * Weist einen Beleg zurueck in die Bearbeitung.
 *
 * Die Freigabe wird dabei GELOESCHT, nicht nur der Status gesenkt: bliebe
 * approved_by stehen, traegt ein Beleg in Bearbeitung den Namen dessen, der ihn
 * angeblich freigegeben hat — und nach einer erneuten Freigabe durch jemand
 * anderen waere nicht mehr erkennbar, wer zuletzt zugestimmt hat.
 *
 * Der Grund ist Pflicht und wird am Beleg vermerkt. Eine Zurueckweisung ohne
 * Begruendung zwingt den Bearbeiter zum Raten, und das Protokoll zeigt spaeter
 * nur, DASS etwas zurueckkam.
 */
export const rejectInvoice = (inv: Invoice, actorId: string, reason: string) => {
    const text = reason.trim();
    if (!text) throw new Error('Bitte einen Grund für die Zurückweisung angeben.');
    return setStatus(inv.id, inv.status, 'DRAFT', {
        approved_at: null,
        approved_by: null,
        details: {
            ...(inv.details ?? {}),
            reviewRejection: { reason: text, at: new Date().toISOString(), by: actorId },
        },
    });
};

/**
 * Vermerkt den Versand.
 *
 * SENT wird NUR aus ISSUED heraus gesetzt. Frueher ueberschrieb diese Funktion
 * jeden Status bedingungslos: eine per Vorkasse bezahlte Rechnung fiel von PAID
 * zurueck auf SENT, galt damit wieder als offen und wurde nach Ablauf der Frist
 * gemahnt — und ein stornierter Beleg stand nach dem Versand wieder als gueltig
 * da. Ist der Beleg weiter, wird nur noch der Versand protokolliert.
 */
export const markInvoiceSent = async (id: string, sentTo: string): Promise<void> => {
    const sentFields = { sent_at: new Date().toISOString(), sent_to: sentTo };

    const { data: promoted, error } = await supabase
        .from('invoices')
        .update({ status: 'SENT', ...sentFields })
        .eq('id', id)
        .eq('status', 'ISSUED')
        .select('id');
    if (error) throw error;
    if (promoted?.length) return;

    const { data: stamped, error: stampErr } = await supabase
        .from('invoices')
        .update(sentFields)
        .eq('id', id)
        .select('id');
    if (stampErr) throw stampErr;
    // Null getroffene Zeilen meldet PostgREST als Erfolg. Ohne diese Pruefung
    // meldet die Oberflaeche "versendet", waehrend nirgends ein sent_at steht.
    if (!stamped?.length) {
        throw new Error(
            'Der Versand konnte nicht vermerkt werden — der Beleg wurde nicht gefunden oder der Zugriff wurde verweigert.',
        );
    }
};

// ─── Rueckschreibung ins Projekt-LV ─────────────────────────────────────────

/**
 * Buchungsreferenz einer Rechnung.
 *
 * Praefix BU- damit isAccountingEntry, die Latenz-Alerts und der
 * Leistungsnachweis diese Eintraege weiterhin als Abrechnung erkennen und aus
 * der Leistungsermittlung ausschliessen.
 *
 * Schluessel ist die ID, nicht die Nummer: die ID existiert bereits vor der
 * Nummernziehung, sodass die umkehrbare Rueckschreibung VOR dem irreversiblen
 * Schritt stattfinden kann.
 */
/**
 * 16 Hex-Zeichen, nicht 8.
 *
 * Auf 8 Zeichen sind das 32 Bit. Kollidieren zwei Belege desselben Projekts
 * darin, greift die Idempotenzpruefung faelschlich: die Rueckschreibung wird
 * mit skipped uebersprungen, die Rechnung geht raus, die Menge bleibt im
 * Projekt offen — und der naechste Rechnungslauf stellt sie erneut in
 * Rechnung. Der Ausfall waere lautlos und nach der Festschreibung nicht mehr
 * zu korrigieren.
 */
const BOOKING_REF_HEX = 16;

export const bookingRefForInvoice = (invoiceId: string): string =>
    `BU-INV-${invoiceId.replace(/-/g, '').slice(0, BOOKING_REF_HEX)}`;

export const bookingRefForCancellation = (cancellationId: string): string =>
    `BU-STO-${cancellationId.replace(/-/g, '').slice(0, BOOKING_REF_HEX)}`;

/**
 * Buchungsreferenz je nach Belegart.
 *
 * Storno und Gutschrift bekommen ein eigenes Praefix, damit in der
 * Buchungshistorie auf einen Blick erkennbar ist, welcher Eintrag eine
 * Abrechnung war und welcher deren Ruecknahme — beide bleiben stehen, geloescht
 * wird unter GoBD nichts.
 */
const bookingRefFor = (invoiceId: string, documentType: DocumentType): string =>
    documentType === 'CANCELLATION_INVOICE' ||
    documentType === 'COMMERCIAL_CREDIT_INVOICE' ||
    documentType === 'CORRECTION_INVOICE'
        ? bookingRefForCancellation(invoiceId)
        : bookingRefForInvoice(invoiceId);

export interface WriteBackRecord {
    projectId: string;
    bookingReference: string;
    updatedAt: string;
    lineIds: string[];
    skipped?: boolean;
}

export interface IssueContext {
    actorId: string;
    userName?: string;
    config: InvoiceConfig;
    /**
     * Firmenname und Anschrift des Rechnungsausstellers.
     *
     * Beides ist nach §14 Abs. 4 Nr. 1 UStG Pflichtangabe und steht nicht in
     * InvoiceConfig, sondern im Benutzerprofil. Ohne diese Angaben darf keine
     * Rechnung entstehen — der Snapshot muss sie historisiert festhalten,
     * damit eine spaetere Umfirmierung alte Belege nicht veraendert.
     */
    company: { name: string; address: string; email?: string; phone?: string };
    /**
     * Rendert das PDF. Hereingereicht statt importiert, damit dieser Service
     * nicht von jsPDF abhaengt und ohne PDF-Umgebung testbar bleibt.
     */
    renderPdf: (invoice: Invoice, customer: Customer) => Promise<Uint8Array>;
    generatorVersion?: string;
    /**
     * Projektcheck, FRISCH beim Ausstellen ausgefuehrt.
     *
     * Hereingereicht statt importiert, damit dieser Service nicht von der
     * Projektbeschaffung abhaengt. Fehlt die Funktion, wird nicht geprueft —
     * das ist der Pfad fuer Storno und Korrekturbelege, bei denen das Projekt
     * nichts mehr beisteuert.
     *
     * Warum frisch und nicht das Ergebnis vom Entwurf: zwischen Entwurf und
     * Ausstellung koennen Stunden liegen. Ein altes Gruen darf das Tor nicht
     * oeffnen, wenn inzwischen jemand den Leistungsstand geaendert hat.
     */
    runProjectCheck?: (invoice: Invoice) => Promise<ProjectCheckResult>;
    /** Pflicht-Notiz bei gelber Ampel. Ohne sie wird nicht ausgestellt. */
    projectCheckAcknowledgement?: string;
    /**
     * Buchungsreferenz des Belegs, den dieser hier aufhebt.
     *
     * Nur cancelInvoice setzt sie. Damit nimmt die Rueckschreibung exakt das
     * zurueck, was das Original gebucht hat — nachgelesen in dessen Abdruck im
     * Leistungsprotokoll, statt es aus der Stornozeile zu erschliessen. Die
     * Zeile taugt dafuer nicht: bei kumulativer Abrechnung nennt sie den
     * Gesamtstand, von dem nur der Zuwachs aus diesem Beleg stammt.
     *
     * Die kaufmaennische Gutschrift bekommt sie bewusst NICHT: sie erstattet
     * einen frei gewaehlten Teilbetrag und ist keine Ruecknahme der Buchung.
     */
    reverseOf?: string;
}

/** Die Ampel des Projektchecks steht dem Ausstellen entgegen. */
export class ProjectCheckBlockedError extends Error {
    constructor(
        message: string,
        public readonly result: ProjectCheckResult,
        /**
         * Rote Ampel oder fehlende Kenntnisnahme — fuer die Zaehlung der
         * Sperren beim Ausstellen (ausstellSperren.ts), damit sie nicht am
         * Text haengt. Optional: ohne Angabe entscheidet dort die Ampel.
         */
        public readonly grund?: ProjektcheckSperrGrund,
    ) {
        super(message);
        this.name = 'ProjectCheckBlockedError';
    }
}

const groupLinesByProject = (lines: InvoiceLine[]): Map<string, InvoiceLine[]> => {
    const map = new Map<string, InvoiceLine[]>();
    for (const line of lines) {
        if (line.kind !== 'SERVICE' || !line.projectId || !line.serviceId) continue;
        const list = map.get(line.projectId) ?? [];
        list.push(line);
        map.set(line.projectId, list);
    }
    return map;
};

/** Was EIN Projekt an EINER Ausstellung traegt — aus EINEM Schnappschuss. */
interface ProjectIssuePlan {
    /** Die zugeordneten Zeilen genau dieses Projekts. */
    lines: InvoiceLine[];
    /** Der fortgeschriebene Projektstand. Fehlt, wenn bereits gebucht ist. */
    nextReport?: Report;
    /** projects.updated_at des Standes, aus dem geplant wurde — fuer den WriteBackRecord. */
    loadedUpdatedAt: string;
    /** Die Buchungsreferenz steht schon im Protokoll — Idempotenz-Schicht 3. */
    alreadyBooked: boolean;
    /**
     * Erster und letzter Tag, an dem in diesem Projekt bis zum Stichtag
     * geleistet wurde — aus dem FRISCH geladenen Protokoll. Wird zum
     * Leistungszeitraum des Belegs eingefroren.
     */
    performed: { from?: string; to?: string };
    /**
     * Nur die NEUEN Protokollzeilen dieser Buchung — die Zweitschrift ins
     * Buchungsjournal (S4b). Bei einer Teilrechnung steht hier je Teilnehmer
     * eine eigene Zeile; sie unterscheiden sich allein durch customerId und
     * splitPartyId und gehen ALLE einzeln ins Journal.
     */
    newEntries?: ServiceTransaction[];
    /**
     * Der Protokollstand VOR dieser Buchung. Er liefert dem Journal die
     * bereits vergebenen ids — ohne ihn verschluckte `on conflict do nothing`
     * eine neue Zeile mit belegter id.
     */
    previousLog?: ServiceTransaction[];
    /** Fuer `billing_mode_at_entry` der Journalzeilen. */
    linkedProjectConfig?: LinkedProjectConfig | null;
}

/**
 * Plant aus EINEM geladenen Projektstand BEIDES: welche Protokolleintraege die
 * Zeilen dieses Projekts abrechnen, und was dafuer im Projekt zu buchen ist.
 *
 * Dass beides aus demselben Stand kommt, ist der Integritaetskern des Moduls,
 * keine Sparsamkeit. Frueher lud die Zuordnung das Projekt selbst und die
 * Rueckschreibung ein zweites, unabhaengiges Mal. Zwischen beiden Lesevorgaengen
 * konnte ein paralleler Vorgang — ein Rechnungslauf neben einer Handausstellung
 * am selben Projekt — quantityBilled veraendern: der Beleg fror dann eine
 * Zuordnung ueber N Eintraege ein, waehrend M != N gebucht wurde. Genau der
 * "geraten statt belegt"-Zustand, den die Zuordnung abschaffen soll.
 *
 * Aus demselben Grund speist EINE services-Liste beide Rechnungen: den
 * kumulativen Zuwachs der Zuordnung und den Zuwachs, den planWriteBack bucht.
 *
 * Der teure Teil laeuft nur beim Ausstellen, also einmal je Beleg und Projekt.
 * Das ist der richtige Zeitpunkt: vorher kann sich der Entwurf noch aendern,
 * nachher ist der Beleg eingefroren.
 *
 * Storno- und Gutschriftzeilen tragen ihre Zuordnung bereits gespiegelt und
 * haben eine negative Menge; planAllocations laesst sie unberuehrt, und
 * planWriteBack liest genau diese mitgebrachte Zuordnung.
 *
 * `tor` sagt, woher die Zuordnung kommt (S5a Stufe 3b): 'journal' ist der
 * Normalbetrieb — das Tor (allocationGate.ts) friert die Zuordnung aus dem
 * Buchungsjournal ein und sperrt bei Abweichung —, 'json' der Notschalter.
 * Gelesen wird er EINMAL je Ausstellung in issueInvoice.
 *
 * S5a Stufe 3c, Paket T: die uebrigen Leser des Protokolls (Aufteilung,
 * Idempotenz, Leistungszeitraum, Pauschal- und Fehlstandriegel, Storno-Menge)
 * lesen weiter das JSON. Im Journal-Modus haelt der Rumpf ihre JSON-Werte im
 * Moment ihrer Verwendung fest (`notiere`), und wenn er fertig ist — ob mit
 * Plan, Sperre oder Riegel —, startet hier der Schatten ohne `await`, der sie
 * auf dem schon angeforderten Journal nachrechnet und nur meldet
 * (torLeserSchatten.ts). Er gibt nichts zurueck und wirft nie.
 */
const planProjectIssue = async (
    projectId: string,
    projectLines: InvoiceLine[],
    bookingReference: string,
    invoice: Invoice,
    ctx: IssueContext,
    tor: TorModus,
): Promise<ProjectIssuePlan> => {
    const leser: TorLeserAufnahme | null =
        tor === 'journal'
            ? torLeserAufnahme(
                  {
                      projectId,
                      projectNumber: null,
                      invoiceId: invoice.id,
                      invoiceNumber: invoice.invoiceNumber ?? null,
                      zeitraum: { from: invoice.deliveryDateFrom, to: invoice.deliveryDateTo },
                  },
                  bookingReference,
              )
            : null;
    try {
        return await planProjectIssueRumpf(projectId, projectLines, bookingReference, invoice, ctx, tor, leser);
    } finally {
        try {
            starteTorLeserSchatten(leser);
        } catch {
            // Der Schatten darf die Ausstellung nie beruehren — auch kein Wurf hier.
        }
    }
};

/** Der Rumpf von planProjectIssue; `leser` nimmt die JSON-Werte fuer den Schatten auf (3c). */
const planProjectIssueRumpf = async (
    projectId: string,
    projectLines: InvoiceLine[],
    bookingReference: string,
    invoice: Invoice,
    ctx: IssueContext,
    tor: TorModus,
    leser: TorLeserAufnahme | null,
): Promise<ProjectIssuePlan> => {
    const full = await loadProjectById(projectId);
    if (!full) throw new Error(`Projekt ${projectId} wurde nicht gefunden.`);

    // S5a Stufe 3b: das Journal fuer das Tor GLEICH anfordern, parallel zu
    // Vorbelegen und Aufteilung — sonst kaeme seine Rundreise (seitenweise)
    // obendrauf, und das Fenster zwischen Laden und Speichern (last write
    // wins) wuechse um sie. Gelesen wird es erst am Tor. `loadJournal` lehnt
    // nie ab (null bei jedem Fehler); das `.catch` haelt das auch dann, wenn
    // planProjectIssue vorher wirft und niemand mehr wartet.
    const journalVorab = tor === 'journal' ? loadJournal(projectId).catch(() => null) : null;

    const loadedUpdatedAt: string = full.updated_at;
    const report = full.data as Report;
    const meta = report.metadata as ReportMetadata;
    const services: ServiceItem[] = meta.globalData?.services || getNormalizedServices(full);
    const log: ServiceTransaction[] = meta.globalData?.serviceLog || [];
    // S5a Stufe 3c: dasselbe Protokoll und dasselbe angeforderte Journal fuer
    // den Leser-Schatten — kein zweites Laden.
    notiere(leser, (a) => {
        a.kopf.projectNumber = full.project_number ? String(full.project_number) : null;
        a.jsonLog = log.slice();
        a.journal = journalVorab;
    });

    // Den eigenen Beleg ausschliessen: bei einem Wiederholungsversuch nach
    // einem Abbruch traegt er womoeglich schon eine Zuordnung, und die wuerde
    // sich sonst selbst blockieren.
    const prior = (await fetchProjectInvoices(projectId)).filter((i) => i.id !== invoice.id);

    // Kumulativ wird nur der ZUWACHS zugeordnet — dieselbe Menge, die die
    // Rueckschreibung gleich bucht. Die volle Zeilenmenge zuzuordnen liesse
    // einen Junibeleg die Julieintraege mitverbrauchen, obwohl er sie nicht
    // abrechnet: die Julileistung waere danach keinem Monat mehr zuzuordnen und
    // erschiene als "ohne Datum".
    // Bei einer TEILRECHNUNG ist die Bemessungsgrundlage der Stand des eigenen
    // Auftraggebers und nicht der des Projekts.
    //
    // quantityBilled ist die Summe über ALLE Beteiligten. Kumulativ nennt die
    // Zeile einer Teilrechnung aber den Gesamtstand ihres ANTEILS — davon den
    // Stand aller abzuziehen ergäbe beim zweiten Beleg der Gruppe einen zu
    // kleinen oder gar keinen Zuwachs, und die Leistung fiele lautlos aus der
    // Abrechnung. Der Stand je Auftraggeber wird aus den Abrechnungszeilen des
    // Protokolls ABGELEITET; ein zweiter Zähler daneben liefe auseinander.
    //
    // Ohne Aufteilung bleibt alles, wie es war: dieselbe Karte, gefüllt aus
    // quantityBilled.
    //
    // Geführt wird der Stand je TEILNEHMER und nicht je Auftraggeber (SQL
    // Nr. 54): bei vier Häusern desselben Auftraggebers wäre sein Stand die
    // Summe aller vier, und die Rechnung für Haus B fände nichts mehr zu
    // fordern. Welcher Teilnehmer der Beleg ist, sagt `splitPartyId`; an
    // Altbelegen, die es noch nicht tragen, löst der Schlüssel es über den
    // Auftraggeber auf — aber nur, solange das eindeutig ist.
    //
    // Gefragt wird nach GRUPPE ODER TEILNEHMER. Ein Gegenbeleg (Storno,
    // Korrektur, Gutschrift) traegt bewusst KEINE Gruppe — er gehoert nicht zur
    // Forderungsgruppe, deren Anteile sich auf 100 % addieren muessen —, wohl
    // aber den Teilnehmer des Ursprungsbelegs. Ohne ihn waere seine
    // Gegenbuchung nicht zuordenbar und sperrte die naechste Teilrechnung.
    const geteilt = Boolean(invoice.splitGroupId || invoice.splitPartyId);
    const split = geteilt ? await fetchProjectSplit(projectId) : null;
    const legacy = legacyPartyByCustomer(split);
    const partyId = geteilt ? resolveParty(invoice, legacy) : undefined;
    if (geteilt && !partyId) {
        throw new Error(
            'Dieser Beleg gehört zu einer Aufteilung, lässt sich aber keinem Teilnehmer zuordnen. ' +
                'Das passiert, wenn der Aufteilungsschlüssel nach dem Anlegen geändert wurde — ' +
                'bitte den Entwurf verwerfen und neu erzeugen.',
        );
    }
    const splitStands = partyId ? billedByParty(log, legacy) : null;
    const billedByService = new Map(
        services.map((s) => [
            s.id,
            splitStands && partyId
                ? billedFor(splitStands.get(s.id), partyId)
                : s.quantityBilled || 0,
        ]),
    );
    // S5a Stufe 3c, Leser-Schatten: der Stand je Teilnehmer, wie er eben aus
    // dem JSON kam — je Leistung der Belegzeilen, nur die gehen in Zuwachs und
    // Buchung.
    if (splitStands && partyId) {
        const teilnehmer = partyId;
        notiere(leser, (a) => {
            a.aufteilung = {
                legacy: new Map(legacy),
                partyId: teilnehmer,
                json: new Map(
                    projectLines
                        .filter((l) => !!l.serviceId)
                        .map((l) => [l.serviceId as string, billedByService.get(l.serviceId as string) ?? 0]),
                ),
            };
        });
    }

    // Der Anteil wurde nach einer Teilabrechnung GESENKT: der Auftraggeber hat
    // mehr getragen, als er nach dem heutigen Schlüssel tragen müsste.
    //
    // Das darf nicht stillschweigend auf null geklemmt werden — genau das tut
    // die Zeile unten (Math.max(0, …)), und beim einzelnen Auftraggeber ist das
    // auch richtig: dort kann der Stand nicht sinken. Mit Anteilen kann er es,
    // und der Auftraggeber bekäme sein Geld nie zurück, ohne dass irgendwo
    // etwas auffiele. Die Entscheidung darüber (Gutschrift oder Schlüssel
    // zurücknehmen) gehört an einen Menschen.
    if (splitStands && invoice.details?.billingMode === 'CUMULATIVE') {
        for (const l of projectLines) {
            const soll = l.quantity ?? 0;
            // Gegenbelege tragen gespiegelte, negative Mengen — sie geben
            // zurück und beanspruchen nichts.
            if (!l.serviceId || soll < 0) continue;
            const ist = billedByService.get(l.serviceId) ?? 0;
            if (soll < ist - SPLIT_EPSILON) {
                throw new Error(
                    `Der Anteil an "${l.title}" wurde auf ${formatShare(invoice.splitShareBp ?? 0)} gesenkt, ` +
                        `abgerechnet sind bei diesem Auftraggeber aber bereits ${ist} statt ${soll}. ` +
                        'Die Differenz muss als Gutschrift zurück; die Rechnung wird nicht ausgestellt.',
                );
            }
        }
    }

    // Zeitraum und Zuwachsfunktion EINMAL — der JSON-Weg, das Tor und der
    // Schatten bekommen dieselben Objekte. Bis Stufe 3a stand hier eine
    // wortgleiche Abschrift fuer den Schatten; zwei Fassungen derselben Regel
    // laufen frueher oder spaeter auseinander.
    const zeitraum = { from: invoice.deliveryDateFrom, to: invoice.deliveryDateTo };
    const quantityOf =
        invoice.details?.billingMode === 'CUMULATIVE'
            ? (l: InvoiceLine) =>
                  Math.max(0, (l.quantity ?? 0) - (billedByService.get(l.serviceId ?? '') ?? 0))
            : undefined;

    // Ohne die verzichteten Eintraege (ohneVerzichteteEintraege): ein
    // verzichteter Termin wird nie gefordert und darf keiner Rechnung
    // zugeordnet werden. Der Journal-Weg am Tor filtert genauso
    // (journalwegAmTor) — sonst wichen beide Zuordnungen ab.
    const jsonLines = planAllocations(
        projectLines,
        ohneVerzichteteEintraege(log, services),
        allocatedByEntry(prior),
        zeitraum,
        quantityOf,
    );

    // Idempotenz-Schicht 3: dieselbe Buchungsreferenz nie zweimal anlegen
    // (Ausstieg weiter unten). Hier schon gefragt, weil das Tor davon abhaengt.
    const bereitsGebucht = isAlreadyBooked(log, bookingReference);
    // Bleibt bis S9 auf dem JSON (torLeserSchatten.ts, Kopf); der Schatten
    // meldet nur den umgekehrten Fall — das Journal kennt die Buchung schon.
    notiere(leser, (a) => {
        a.bereitsGebucht = bereitsGebucht;
    });

    // ── S5a Stufe 3b: das Tor ─────────────────────────────────────────────
    //
    // JOURNAL-MODUS (Normalbetrieb): das Tor rechnet dieselbe Zuordnung aus
    // dem Buchungsjournal, vergleicht sie mit `jsonLines` und friert bei
    // Gleichheit die des JOURNALS ein — oder wirft `TorGesperrtError`
    // (Abweichung mit Geldwirkung, Journal nicht lesbar, Pruefung
    // ausgefallen). Begruendung, Ausnahmen und was „gleich" am Eingefrorenen
    // aendern darf: allocationGate.ts.
    //
    // HIER und nicht spaeter: vor der Idempotenz-Schicht, den Riegeln,
    // planWriteBack, der Sperrfrage und dem Speichern. Bis hierher ist an
    // diesem Projekt nichts geschrieben; der Statusanspruch ISSUING und die
    // Rueckschreibung der Projekte davor (Sammelrechnung) nimmt die
    // Kompensation in issueInvoice zurueck, eine Nummer ist noch nicht
    // gezogen. Bei einer bereits gebuchten Ausstellung sperrt das Tor nicht
    // an einer Abweichung (die eigene Buchung steht im JSON, aber noch nicht
    // im Journal) — Begruendung dort.
    //
    // JSON-MODUS (Tor-Notschalter AN): exakt der Weg bis Stufe 3a — die
    // Zuordnung aus dem JSON, der Schatten ohne `await` und ohne Wirkung. Er
    // liest das Journal ueber das Netz, und die Ausstellung darf darauf weder
    // warten noch an ihm scheitern; das `.catch` ist nur der zweite Gurt.
    //
    // DURCHGEREICHT (Controller-Entscheid 23.10.2026): traegt der Beleg in
    // diesem Projekt keine Leistungszeile mit positiver Menge (Storno,
    // kaufmaennische Gutschrift, reine Betragskorrektur — `nurDurchgereicht`,
    // aus der Zeilenlogik), laeuft das Projekt auch im Journal-Modus wie im
    // JSON-Modus, mit dem schon angeforderten Journal und einer eigenen ersten
    // Meldungszeile. Eine kumulative Zeile ohne Zuwachs geht weiter durchs Tor.
    // Begruendung in allocationGate.ts („Durchgereichte Belege").
    const torEingang: SchattenAmTorEingang = {
        projectId,
        projectNumber: full.project_number ?? null,
        invoice,
        projectLines,
        jsonLog: log,
        prior,
        zeitraum,
        quantityOf,
        jsonLines,
        services,
    };
    let lines: InvoiceLine[];
    if (tor === 'json') {
        lines = jsonLines;
        void schattenAmTor(torEingang).catch(() => {});
    } else if (nurDurchgereicht(projectLines, quantityOf)) {
        lines = jsonLines;
        void schattenAmTor(
            torEingang,
            { ...STANDARD_TOR_DEPS, ladeJournal: () => journalVorab ?? loadJournal(projectId) },
            'durchgereicht',
        ).catch(() => {});
    } else {
        lines = await torAmTor(
            torEingang,
            { bereitsGebucht },
            { ...STANDARD_TOR_DEPS, ladeJournal: () => journalVorab ?? loadJournal(projectId) },
        );
    }

    // Idempotenz-Schicht 3: dieselbe Buchungsreferenz nie zweimal anlegen. Die
    // Zuordnung wird trotzdem geplant — der Beleg wird ja fertig ausgestellt
    // und braucht sie, auch wenn die Buchung schon steht.
    // Der Leistungszeitraum aus genau dem Protokollstand, aus dem auch
    // zugeordnet und gebucht wird — begrenzt auf den Stichtag des Belegs,
    // fortschreibend zusaetzlich auf seinen Beginn (wie beim Aufbau).
    const performedSeit = invoice.details?.billingMode === 'CUMULATIVE' ? undefined : invoice.deliveryDateFrom;
    const performed = performedSpanFromLog(log, invoice.deliveryDateTo, performedSeit);
    notiere(leser, (a) => {
        a.leistungszeitraum = { stichtag: invoice.deliveryDateTo, seit: performedSeit, json: { ...performed } };
    });

    if (bereitsGebucht) {
        return { lines, loadedUpdatedAt, alreadyBooked: true, performed };
    }

    // ── MONATSPAUSCHALE: die drei Riegel, bindend ────────────────────────
    //
    // Geprueft aus GENAU dem Schnappschuss, aus dem gleich zugeordnet und
    // gebucht wird — nicht aus dem Stand, den der Editor irgendwann geladen
    // hat. Ein altes Gruen darf das Tor nicht oeffnen, wenn inzwischen jemand
    // Monate gebucht oder geloescht hat (dieselbe Begruendung wie beim
    // Projektcheck). NACH dem Idempotenz-Ausstieg: ein Wiederholungsversuch
    // einer bereits gebuchten Ausstellung darf nicht an einer Pruefung
    // stranden, deren Anlass er selbst geschrieben hat.
    //
    // Regeln und Begruendung an flatRateIssues (invoiceLogic); Projekte ohne
    // Pauschalposition kosten hier nichts.
    const projektStand = {
        [projectId]: {
            services,
            serviceLog: log,
            startDate: meta.globalData?.startDate,
            endDate: meta.globalData?.endDate,
        },
    };
    const flatIssues = flatRateIssues(invoice, projektStand);
    // Leser-Schatten (3c): Ergebnis und KOPIEN der Eingaben, VOR dem Wurf —
    // gerade ein Riegel, der nur auf einer Seite haelt, ist der Befund.
    notiere(leser, (a) => {
        a.riegel = {
            projectId,
            invoice: structuredClone(invoice),
            stand: {
                services: services.map((s) => ({ ...s })),
                startDate: meta.globalData?.startDate,
                endDate: meta.globalData?.endDate,
            },
        };
        a.pauschalriegel = riegelListe(flatIssues);
    });
    if (flatIssues.length > 0) throw new InvoiceValidationError(flatIssues);

    // ── ERBRACHT, ABER NICHT IM LEISTUNGSSTAND: bindend ──────────────────
    //
    // Aus demselben Schnappschuss und an derselben Stelle wie die
    // Pauschalriegel, aus denselben zwei Gruenden: der Stand des Editors kann
    // alt sein, und ein Wiederholungsversuch einer bereits gebuchten
    // Ausstellung darf nicht stranden.
    //
    // Ohne diesen Halt bliebe der Riegel umgehbar — die Maske prueft mit einem
    // Kontext, den sie nur laedt, wenn ein Projekt am Beleg haengt. Was das
    // Tor bewacht, gehoert ans Tor. Begruendung an missingUsedIssues.
    const fehlstand = missingUsedIssues(invoice, projektStand);
    notiere(leser, (a) => {
        a.fehlstand = riegelListe(fehlstand);
    });
    if (fehlstand.length > 0) throw new InvoiceValidationError(fehlstand);

    // ── PAKETPOSITION: Bestandteile offen oder ueber dem Deckel — bindend ─
    //
    // Aus demselben Schnappschuss, aus denselben Gruenden: der Editor kann
    // einen Stand von vor der Ruecknahme eines Bestandteils zeigen, und eine
    // Rechnung aus dem Lauf oder per Hand ergaenzt laeuft gar nicht ueber die
    // Maske. `services` kommt aus loadProjectById (`select *`), traegt also
    // `bestandteile` und das von der Datenbank gepflegte `bestandteile_offen`
    // (projectLv.bestandteilFelderAusZeile) — fehlt die Spalte, ist das Gate
    // unbekannt und sperrt (fail closed). planWriteBack haelt dieselbe Regel
    // als letzte Schranke noch einmal. Begruendung an componentLineIssues.
    const paketRiegel = componentLineIssues(invoice, projektStand);
    if (paketRiegel.length > 0) throw new InvoiceValidationError(paketRiegel);

    // Leser-Schatten (3c): was planWriteBack beim Storno aus dem Abdruck des
    // Originals liest — dieselbe reine Funktion auf demselben Protokoll, im
    // selben synchronen Schritt.
    if (ctx.reverseOf) {
        const reverseOf = ctx.reverseOf;
        notiere(leser, (a) => {
            a.storno = { reverseOf, json: bookedByReference(log, reverseOf) };
        });
    }

    // Der eigentliche Rechenkern liegt pur in invoiceWriteBackLogic — hier
    // bleibt nur, was Projekt laden und speichern betrifft.
    const { services: updatedServices, logEntries: newLog } = planWriteBack(services, lines, {
        cumulative: invoice.details?.billingMode === 'CUMULATIVE',
        bookingReference,
        // Beim Storno: die Referenz des Originals und das Protokoll, in dem sein
        // Abdruck steht. Aus genau DEM Stand, aus dem auch zugeordnet und
        // gebucht wird — dieselbe Begruendung wie oben.
        reverseOf: ctx.reverseOf,
        serviceLog: log,
        entryDate: invoice.deliveryDateTo || invoice.invoiceDate,
        // Beim Ausstellen ist die Nummer hier NIE vergeben: gezogen wird sie
        // erst in Phase B, wenn alle Projekte geschrieben sind. Frueher stand
        // deshalb '(Entwurf)' im Protokoll — und blieb dort stehen, denn der
        // Eintrag wird nach dem Ausstellen nicht mehr angefasst. Ein Beleg,
        // der laut Protokoll ein Entwurf ist, obwohl er ausgestellt und
        // versendet wurde, ist beim Nachforschen schlimmer als gar keine
        // Angabe. Ohne Nummer bleibt sie jetzt weg; der Bezug zum Beleg haengt
        // ohnehin an bookingReference (BU-INV-<Belegkennung>), nicht am Text.
        documentLabel: invoice.invoiceNumber,
        actorId: ctx.actorId,
        actorName: ctx.userName || 'Administration',
        // Der Auftraggeber wird IMMER gestempelt, auch ohne Aufteilung: er
        // kostet dort nichts und macht den Bestand zuordenbar, falls das
        // Projekt später geteilt wird. Ohne ihn wäre die Menge Altbestand ohne
        // Zuordnung und sperrte die spätere Aufteilung.
        customerId: invoice.customerId,
        // Der Teilnehmer daneben, aus demselben Grund: ohne ihn wäre jede
        // Buchung eines Abschnittsprojekts Altbestand ohne Zuordnung und
        // sperrte die nächste Teilrechnung.
        splitPartyId: partyId,
        splitShareBp: invoice.splitShareBp,
        billedForCustomer: splitStands ? billedByService : undefined,
        newId: () => crypto.randomUUID(),
        bookedAt: () => new Date().toISOString(),
    });

    const netSum = lines.reduce((sum, l) => sum + l.netCents, 0);
    const note = {
        id: crypto.randomUUID(),
        date: new Date().toISOString(),
        author: ctx.userName || 'System',
        text: `Rechnung ${invoice.invoiceNumber ?? ''} erstellt — ${formatCents(netSum)} netto`,
    };

    const nextReport = {
        ...report,
        metadata: {
            ...meta,
            globalData: {
                ...meta.globalData,
                services: updatedServices,
                serviceLog: [...newLog, ...log],
                notes: [note, ...(meta.globalData?.notes || [])],
            },
        },
    } as Report;

    return {
        lines,
        nextReport,
        loadedUpdatedAt,
        alreadyBooked: false,
        performed,
        newEntries: newLog,
        previousLog: log,
        linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
    };
};

/**
 * Absage wegen fremder Projektsperre — oder null, wenn geschrieben werden darf.
 *
 * Duenne Huelle um projectWriteBlockedMessage mit EINER zusaetzlichen Regel:
 * ohne eigene Benutzerkennung wird NICHT blockiert.
 *
 * Grund: `isLockHeldByOther` liest eine leere Kennung als „anonymer
 * Betrachter" und meldet dann jede lebende Sperre als fremd — auch die eigene.
 * Der Aufrufer (InvoiceEditor) reicht `userProfile?.id ?? ''` durch; ist das
 * Profil beim Klick noch nicht geladen, scheiterte die eigene Ausstellung mit
 * „Projekt wird gerade von <mir selbst> bearbeitet". Fail-open wie ueberall in
 * der Sperrlogik: unbekannt heisst nicht blockiert.
 */
export const projectWriteBlockForActor = (
    lock: ProjectLock | null | undefined,
    actorId: string | null | undefined,
    nowMs: number,
): string | null =>
    (actorId ?? '').trim() === ''
        ? null
        : projectWriteBlockedMessage(lock, actorId, nowMs);

/**
 * Schreibt die abgerechneten Mengen in das Projekt zurueck und liefert die
 * Zuordnung, die zu genau dieser Buchung gehoert.
 *
 * Abweichung von handleExecuteBilling in AdminAccountingView: bei Ueberbuchung
 * wird geworfen statt still auf die offene Menge zu klemmen. Weniger
 * abzurechnen, als das PDF ausweist, waere schlimmer als eine fehlgeschlagene
 * Ausstellung.
 *
 * Es gibt keinen Konflikt-Anker und keinen Wiederholungsversuch mehr: seit dem
 * Ausbau der optimistischen Sperre speichert saveProjectToCloud immer (last
 * write wins). Zuordnung und Buchung stammen weiterhin aus EINEM geladenen
 * Stand — das bleibt der Integritaetskern; die zurueckgegebenen lines gehoeren
 * deshalb immer zu der Planung, die tatsaechlich gespeichert wurde.
 */
const writeBackToProject = async (
    projectId: string,
    projectLines: InvoiceLine[],
    bookingReference: string,
    invoice: Invoice,
    ctx: IssueContext,
    tor: TorModus,
): Promise<{
    record: WriteBackRecord;
    lines: InvoiceLine[];
    performed: { from?: string; to?: string };
    /**
     * Die Zweitschrift ins Buchungsjournal (S4b) — GEPLANT, nicht geschrieben.
     *
     * Diese Funktion haengt bewusst nicht mehr selbst an: sie laeuft mitten in
     * der umkehrbaren Phase A des Ausstellens, und alles, was dort geschrieben
     * wird, kann die Kompensation (`revertWriteBack`) noch zuruecknehmen. Das
     * Journal kann sie nicht zuruecknehmen — es kennt weder Update noch Delete.
     * Also wird hier nur die Absicht zurueckgegeben; geschrieben wird sie in
     * `issueInvoice`, wenn der Beleg steht.
     *
     * Fehlt (`undefined`) im Idempotenz-Fall `alreadyBooked`: dort wird gar
     * nichts gespeichert, also gibt es auch nichts zu spiegeln.
     */
    journal?: JournalMirrorRequest;
}> => {
    const lineIds = projectLines.map((l) => l.id);
    const plan = await planProjectIssue(projectId, projectLines, bookingReference, invoice, ctx, tor);

    if (plan.alreadyBooked) {
        return {
            record: {
                projectId,
                bookingReference,
                updatedAt: plan.loadedUpdatedAt,
                lineIds,
                skipped: true,
            },
            lines: plan.lines,
            performed: plan.performed,
        };
    }

    // ─── Letzter Halt vor dem Schreiben: hat das Projekt jemand offen? ───────
    //
    // Es gibt keinen Konflikt-Anker mehr; saveProjectToCloud schreibt
    // bedingungslos. Ohne diese Frage laufe der Rechnungslauf ueber die eben
    // erfassten Maengel dessen hinweg, der gerade im Pruefmodul sitzt — ohne
    // Meldung, ohne Spur, und die Ruecknahme unten koennte nur die eigene
    // Buchung zuruecknehmen, nicht dessen verlorene Arbeit.
    //
    // Genau hier und nicht frueher: der Idempotenz-Fall oben (`alreadyBooked`)
    // schreibt gar nicht und darf deshalb auch nicht an einer fremden Sperre
    // scheitern. Der Wurf trifft die umkehrbare Phase A des Ausstellens; die
    // Kompensation (revertWriteBack) nimmt die bereits geschriebenen Projekte
    // einer Sammelrechnung zurueck, eine Nummer ist noch nicht gezogen.
    //
    // FAIL-OPEN: fetchProjectLock wirft nie und liefert bei Offline-Betrieb
    // oder nicht ausgefuehrter Migration Nr. 33 (`PGRST202`) schlicht null.
    // Eine fehlende Migration darf den Rechnungsbetrieb nicht lahmlegen. Und
    // ohne eigene Kennung wird ebenfalls nicht blockiert, sonst gaelte die
    // EIGENE Sperre als fremd (projectWriteBlockForActor).
    //
    // Eigene Klasse, gleiche Nachricht: die Maske zeigt weiter `message`,
    // die Zaehlung der Sperren beim Ausstellen erkennt sie an der Klasse
    // (ausstellSperren.ts).
    const blocked = projectWriteBlockForActor(
        await fetchProjectLock(projectId),
        ctx.actorId,
        Date.now(),
    );
    if (blocked) throw new ProjektInBearbeitungError(blocked);

    const row = await saveProjectToCloud(plan.nextReport as Report, projectId);

    // ─── Zweitschrift ins Buchungsjournal (S4b): geplant, nicht geschrieben ──
    //
    // Frueher wurde hier gespiegelt, unmittelbar nach dem Speichern. Das war
    // die falsche Stelle, und zwar aus einem Grund, der nur der Sammelrechnung
    // gehoert: dieser Aufruf steht in der UMKEHRBAREN Phase A. Scheitert
    // danach noch irgendetwas — das naechste Projekt, die Nummernziehung, das
    // PDF, das finale Update —, nimmt `revertWriteBack` die JSON-Zeilen aller
    // bereits geschriebenen Projekte zurueck. Die Journalzeilen blieben stehen,
    // denn das Journal kennt weder Update noch Delete. Der Wiederholungsversuch
    // legte neue Zeilen mit neuen ids daneben, und die Abweichungssicht meldete
    // den Ueberschuss dauerhaft.
    //
    // Deshalb wird die Zweitschrift hier nur GEPLANT und mit herausgegeben.
    // `issueInvoice` sammelt die Plaene ueber alle Projekte und schreibt sie
    // erst, wenn der Beleg wirklich steht — hinter der letzten Stelle, an der
    // die Kompensation noch laufen kann. Ein eigenes `service_journal_void`
    // fuer diesen Pfad wird damit ueberfluessig.
    //
    // Was uebrig bleibt, ist die ANDERE Richtung: bricht die App zwischen dem
    // ausgestellten Beleg und dem Spiegeln ab, FEHLEN Journalzeilen. Das ist
    // die sichere Richtung — die Reparatur laeuft ohnehin nur JSON -> Journal
    // und zieht sie beim naechsten Lauf nach (siehe „Bekannte Abweichungen" in
    // serviceJournal.ts). Ueberzaehlige Zeilen kann sie dagegen nicht wegnehmen.
    //
    // Mitgegeben werden AUSSCHLIESSLICH die neuen Zeilen dieser Buchung, und
    // `previousLog` ist der Stand GENAU DIESES Projekts. Eine Teilrechnung
    // schreibt je Teilnehmer eine eigene Zeile mit gleichem Beleg, gleicher
    // Position und gleichem Tag; sie unterscheiden sich nur durch
    // customerId/splitPartyId und gehen deshalb ALLE einzeln mit — hier wird
    // nichts zusammengefasst oder entdoppelt.
    return {
        record: {
            projectId,
            bookingReference,
            updatedAt: row?.updated_at ?? plan.loadedUpdatedAt,
            lineIds,
        },
        lines: plan.lines,
        performed: plan.performed,
        journal: {
            projectId,
            entries: plan.newEntries ?? [],
            previousLog: plan.previousLog ?? [],
            source: 'invoice_writeback',
            linkedProjectConfig: plan.linkedProjectConfig ?? null,
        },
    };
};

/**
 * Kehrt eine Rueckschreibung exakt um. Kompensation, wenn das Ausstellen
 * scheitert.
 *
 * Meldet zurueck, ob ALLE Projekte zurueckgenommen werden konnten. Frueher
 * wurden Fehlschlaege nur in die Konsole geschrieben und der Aufrufer setzte
 * die Rechnung trotzdem auf APPROVED — dann standen im Projekt gebuchte Mengen
 * ohne zugehoerigen Beleg, und ein erneuter Ausstellversuch wurde von der
 * Idempotenzpruefung uebersprungen, weil die Buchungsreferenz schon existierte.
 *
 * ─── ERST DIE RPC, DANN DER SAVE ───────────────────────────────────────────
 * Die 'BU-INV-…'/'BU-STO-…'-Zeilen dieser Buchung verschwinden hier aus dem
 * Leistungsprotokoll. Ab Migration Nr. 66 darf das nur noch
 * `service_log_remove_entries` — der Riegel `service_log_guard_trg` WIRFT
 * sonst, weil er die Zeilen in OLD sieht und in NEW vermisst. Also erst
 * serverseitig entfernen, dann den uebrigen Stand (die zurueckgesetzten
 * `quantityBilled`) speichern; danach fehlen sie bereits in OLD und der Save
 * ist unauffaellig. Die ausfuehrliche Begruendung steht im Kopf von
 * serviceLogRemoval.ts.
 *
 * Ist die RPC nicht ausgerollt (`legacy`), bleibt alles wie bisher: der Save
 * nimmt die Zeilen mit, und der alte Riegel laesst sie ueber die
 * BU-Ausnahme ziehen.
 *
 * Scheitert die Entfernung mit einem ECHTEN Fehler, gilt dieses Projekt als
 * NICHT zurueckgenommen — der Beleg landet auf DISPUTED statt still auf
 * APPROVED. Das ist die sichtbare Richtung: gebuchte Mengen ohne Beleg
 * verlangen eine Klaerung von Hand, und genau dazu zwingt DISPUTED.
 */
const revertWriteBack = async (
    records: WriteBackRecord[],
): Promise<{ allReverted: boolean; failedProjectIds: string[] }> => {
    const failedProjectIds: string[] = [];

    for (const rec of records) {
        if (rec.skipped) continue;
        try {
            const full = await loadProjectById(rec.projectId);
            if (!full) {
                failedProjectIds.push(rec.projectId);
                continue;
            }
            const report = full.data as Report;
            const meta = report.metadata as ReportMetadata;
            const log: ServiceTransaction[] = meta.globalData?.serviceLog || [];
            const plan = planWriteBackRevert(
                meta.globalData?.services || [],
                log,
                rec.bookingReference,
            );
            // null heisst: zu dieser Buchungsreferenz steht nichts im Protokoll,
            // es gibt nichts zurueckzunehmen.
            if (!plan) continue;

            // Welche Zeilen der Plan tatsaechlich herausnimmt, wird aus dem
            // Vergleich der beiden Staende abgeleitet und nicht ein zweites Mal
            // hingeschrieben: `planWriteBackRevert` filtert ueber die
            // Buchungsreferenz, und zwei getrennt gepflegte Praedikate laufen
            // frueher oder spaeter auseinander — dann entfernte die RPC andere
            // Zeilen, als der Save schreibt.
            const { ids: removedEntryIds } = collectRemovedEntryIds(log, plan.serviceLog);
            await removeServiceLogEntries(
                rec.projectId,
                removedEntryIds,
                `Rechnungs-Rückschreibung ${rec.bookingReference} zurückgenommen (Ausstellen abgebrochen)`,
            );

            const nextReport = {
                ...report,
                metadata: {
                    ...meta,
                    globalData: {
                        ...meta.globalData,
                        services: plan.services,
                        serviceLog: plan.serviceLog,
                    },
                },
            } as Report;

            // Die Kompensation speichert wie jeder andere Pfad ohne Anker: der
            // frisch geladene Stand ist die Grundlage, und der Schreibvorgang
            // gewinnt.
            await saveProjectToCloud(nextReport, rec.projectId);
        } catch (e) {
            // Die Kompensation darf den urspruenglichen Fehler nicht verdecken —
            // aber sie darf auch nicht so tun, als waere sie gelungen.
            failedProjectIds.push(rec.projectId);
            console.error(`Kompensation der Rückschreibung in ${rec.projectId} fehlgeschlagen`, e);
        }
    }

    return { allReverted: failedProjectIds.length === 0, failedProjectIds };
};

// ─── Ausstellen ─────────────────────────────────────────────────────────────

export class InvoiceValidationError extends Error {
    constructor(public readonly issues: ValidationMessage[]) {
        super('Die Rechnung ist unvollständig und kann nicht ausgestellt werden.');
        this.name = 'InvoiceValidationError';
    }
}

/**
 * Stellt eine Rechnung aus. Der irreversible Teil des Moduls.
 *
 * Reihenfolge ist Absicht: die Rueckschreibung ins Projekt (umkehrbar) laeuft
 * VOR der Nummernziehung (nicht umkehrbar). Der haeufige Fehlerfall — ein
 * Konflikt im Projekt-LV — kostet dadurch keine Rechnungsnummer.
 *
 * Scheitert danach etwas, wird die Nummer als VOID protokolliert und die
 * Rueckschreibung exakt invertiert. GoBD verlangt eine lueckenlose oder eine
 * dokumentiert lueckenhafte Nummernfolge; eine begruendete Luecke erfuellt das.
 */
/**
 * Belegarten, die einen AKTIVEN Kunden voraussetzen.
 *
 * Storno, Berichtigung und die beiden Gutschriftsarten fehlen bewusst: sie
 * korrigieren einen bereits ausgestellten Beleg und muessen auch dann noch
 * moeglich sein, wenn der Kunde inzwischen archiviert oder zusammengefuehrt
 * wurde. Sie zu sperren hiesse, einen falschen Beleg nicht mehr aus der Welt
 * schaffen zu koennen.
 */
const REQUIRES_ACTIVE_CUSTOMER: ReadonlySet<DocumentType> = new Set<DocumentType>([
    'INVOICE',
    'ADVANCE_INVOICE',
    'FINAL_INVOICE',
    'REMAINING_INVOICE',
]);

/**
 * Kein neuer Beleg auf einen archivierten oder zusammengefuehrten Kunden.
 *
 * Der Kundenschnappschuss wird beim Ausstellen eingefroren und ist danach nicht
 * mehr aenderbar (GoBD) — ein Entwurf, der seit Tagen offen liegt, wuerde sonst
 * einen Kunden festschreiben, den es fachlich nicht mehr gibt, und die Rechnung
 * landete in einer Akte, die niemand mehr ansieht. Frisch gegen die Datenbank
 * geprueft, nicht gegen das mitgegebene Objekt: zwischen Anlegen des Entwurfs
 * und Ausstellen koennen Tage liegen.
 */
const assertCustomerIssuable = async (invoice: Invoice, customer: Customer): Promise<void> => {
    if (!REQUIRES_ACTIVE_CUSTOMER.has(invoice.documentType)) return;
    const customerId = invoice.customerId || customer?.id;
    if (!customerId) return;

    const { data, error } = await supabase
        .from('customers')
        .select('archived_at, merged_into_id')
        .eq('id', customerId)
        .maybeSingle();
    if (error) throw error;
    const row = (Array.isArray(data) ? data[0] : data) as
        | { archived_at?: string | null; merged_into_id?: string | null }
        | null
        | undefined;
    if (!row?.archived_at && !row?.merged_into_id) return;

    let hint = '';
    if (row.merged_into_id) {
        // Der Zielkunde ist der einzige Zusatzaufwand hier — und nur im
        // Fehlerfall, in dem ohnehin abgebrochen wird. Faellt die Abfrage aus,
        // bleibt die Meldung ohne Namen, statt den Abbruch zu verschlucken.
        const target = await supabase
            .from('customers')
            .select('name, customer_number')
            .eq('id', row.merged_into_id)
            .maybeSingle()
            .then((r: any) => (Array.isArray(r?.data) ? r.data[0] : r?.data), () => null);
        const name = target?.name as string | undefined;
        if (name) hint = ` Aktueller Kunde: ${name}${target?.customer_number ? ` (${target.customer_number})` : ''}.`;
    }
    throw new Error(
        `Der Kunde wurde ${row.merged_into_id ? 'zusammengeführt' : 'archiviert'} — die Rechnung kann so nicht ` +
            `ausgestellt werden. Bitte den Beleg auf den aktuellen Kunden umstellen.${hint}`,
    );
};

/**
 * Die Huelle um das Ausstellen: jeder gescheiterte Versuch wird gemeldet
 * (ausstellSperren.ts), damit die Ueberwachung Sperren je Art zaehlen kann.
 *
 * Nur melden, nichts aendern: derselbe Fehler — dasselbe Objekt — geht
 * weiter, und die Meldung laeuft erst, wenn der Rumpf schon gescheitert ist,
 * also nach seiner Kompensation. Storno, Korrektur und Gutschrift stellen
 * ueber diese Funktion aus und werden damit mitgezaehlt, genau einmal: sie
 * melden selbst nichts.
 */
export const issueInvoice = async (
    id: string,
    customer: Customer,
    ctx: IssueContext,
): Promise<Invoice> => {
    // Die Belegart traegt der Rumpf ein, sobald er den Beleg geladen hat —
    // eine eigene Abfrage nur fuer die Meldung gibt es nicht. Scheitert er
    // vorher, fehlt sie, und die Abfrage holt sie per Join.
    const gelesen: { documentType?: DocumentType } = {};
    try {
        return await issueInvoiceRumpf(id, customer, ctx, gelesen);
    } catch (e) {
        try {
            meldeAusstellSperre(e, { belegId: id, documentType: gelesen.documentType });
        } catch {
            // Die Meldung ist eine Zugabe: sie ersetzt nie den Fehler der Ausstellung.
        }
        throw e;
    }
};

const issueInvoiceRumpf = async (
    id: string,
    customer: Customer,
    ctx: IssueContext,
    gelesen: { documentType?: DocumentType },
): Promise<Invoice> => {
    assertCryptoAvailable();

    const invoice = await fetchInvoice(id);
    if (!invoice) throw new Error('Die Rechnung wurde nicht gefunden.');
    gelesen.documentType = invoice.documentType;
    if (!canIssueFrom(invoice.status)) {
        throw new Error(
            `Eine Rechnung im Status ${invoice.status} kann nicht ausgestellt werden. Ausgestellt wird aus "Freigegeben" oder "In Prüfung" heraus.`,
        );
    }

    // Vor dem Statusanspruch, nicht erst vor dem Einfrieren: der Abbruch soll
    // keinen ISSUING-Claim hinterlassen, den jemand von Hand aufraeumen muss.
    // Geprueft wird der ECHTE Kunde: die belegbezogene Abweichung aendert nur
    // den Andruck, nie die Zuordnung — ein archivierter oder zusammengefuehrter
    // Kunde bleibt es auch unter abweichender Anschrift.
    await assertCustomerIssuable(invoice, customer);

    // Ab hier gilt der Kunde, wie ihn DIESER Beleg ausgibt: Kundenstamm,
    // ueberlagert mit der belegbezogenen Abweichung (details.customerOverride).
    // Er wird geprueft, gedruckt UND eingefroren — der Snapshot ist danach die
    // einzige Quelle des Belegs (PDF-Archiv, E-Rechnung, DATEV), und eine
    // Abweichung, die es nicht in ihn schafft, waere ab dem Ausstellen
    // spurlos verschwunden.
    const documentCustomer = applyCustomerOverride(customer, invoice.details?.customerOverride);

    // Firmenname und Anschrift sind Pflichtangaben (§14 Abs. 4 Nr. 1 UStG) und
    // liegen nicht in InvoiceConfig, sondern im Profil. Ohne sie darf kein
    // Beleg entstehen — spaeter waere er nicht mehr korrigierbar.
    if (!ctx.company?.name?.trim() || !ctx.company?.address?.trim()) {
        throw new Error(
            'Firmenname und Firmenanschrift fehlen. Beide sind Pflichtangaben auf einer Rechnung und müssen in den Einstellungen hinterlegt sein.',
        );
    }

    // Gegen den ANGEDRUCKTEN Kunden: fehlt dem Beleg die Anschrift, ist die
    // abweichende gemeint und nicht die des Stamms — und umgekehrt darf eine
    // gepflegte Stammanschrift eine leer ueberschriebene nicht decken.
    //
    // OHNE Pauschal-Kontext: die Monatspauschal-Riegel laufen hier bewusst
    // nicht (der Editor zeigt sie mit seinem geladenen Stand frueher an) —
    // bindend prueft sie planProjectIssue aus GENAU dem Schnappschuss, aus dem
    // auch zugeordnet und gebucht wird. Ein zweiter Lesevorgang nur fuer die
    // Pruefung wuerde die "ein Schnappschuss je Projekt"-Invariante brechen.
    const issues = validateInvoice(invoice, documentCustomer, ctx.config);
    if (hasBlockingErrors(issues)) throw new InvoiceValidationError(issues);

    // TEILRECHNUNG: die GRUPPE muss zusammen 100 % abdecken.
    //
    // Nicht der einzelne Beleg — der ist immer fuer sich richtig. Eine Gruppe,
    // die nur zu zwei Dritteln existiert (abgebrochene Anlage, von Hand
    // geloeschter Geschwisterbeleg), fordert lautlos zu wenig, und die fehlende
    // Menge taucht Monate spaeter als "erbracht, nie abgerechnet" wieder auf.
    // Der Beleg ist bis dahin festgeschrieben.
    if (invoice.splitGroupId) {
        const group = await fetchSplitGroupInvoices(invoice.splitGroupId);
        const ownServiceIds = [
            ...new Set(
                (invoice.lines ?? [])
                    .filter((l) => l.kind === 'SERVICE' && l.serviceId)
                    .map((l) => l.serviceId as string),
            ),
        ];
        // Zwei Wege, ein Zweck. Belege MIT LV-Bezug werden je Position
        // geprueft — dort haengt der Anteil an der Position und kann sich von
        // Position zu Position unterscheiden. Der Abschlag hat keinen
        // LV-Bezug; bei ihm zaehlt der Anteil am Belegkopf.
        const fehlt = ownServiceIds.length
            ? groupCoverageIssues(ownServiceIds, group.flatMap((i) => i.lines ?? []))[0]
            : groupShareIssue(group.map((i) => i.splitShareBp));
        if (fehlt) {
            throw new Error(
                `${fehlt.message} Die Rechnung wird nicht ausgestellt — fehlt ein Beleg der Aufteilung, fordert die Gruppe zusammen zu wenig.`,
            );
        }
    }

    // Projektcheck, frisch gegen die aktuellen Projektdaten.
    //
    // Er LAEUFT immer — seine SPERRWIRKUNG gilt aber nicht fuer Belege, die
    // Menge nur ZURUECKGEBEN. Der Check fragt, ob genug offene Menge da ist,
    // um sie zu beanspruchen; ein Gegenbeleg beansprucht aber keine. Seine
    // Zeilen tragen gespiegelte, negative Mengen.
    //
    // Er konnte dort deshalb nur falsch-positiv sperren, und genau das ist
    // passiert: nach einer vollstaendig abgerechneten Position ist offen = 0,
    // und jede Reservierung eines anderen Entwurfs loeste "Menge doppelt
    // beansprucht" aus. Das Storno war nicht mehr ausstellbar — obwohl es die
    // Lage entspannt haette, denn es gibt die Menge ja gerade frei. Damit
    // blieben auch quantityBilled ungemindert und der Entwurf liegen.
    //
    // Den Check dafuer ganz zu ueberspringen ginge einen Schritt zu weit: dann
    // bliebe checkResult leer und unten wuerde details.projectCheck nicht
    // festgeschrieben. Ausgerechnet bei Storno und Gutschrift — den Belegen,
    // die spaeter am ehesten geprueft werden — fehlte der Nachweis, was das
    // System beim Ausstellen wusste. Also ausfuehren und festschreiben, nur
    // nicht daran scheitern.
    //
    // Hier entschieden und nicht beim Aufrufer: die Regel, WANN die Ampel
    // sperrt, gehoert an genau eine Stelle — sonst muesste sie jeder Weg zum
    // Ausstellen (Storno, Korrektur, Gutschrift) fuer sich nachbauen.
    //
    // Der Check selbst ist damit NICHT erzwungen: er laeuft nur, wenn der
    // Aufrufer runProjectCheck mitgibt. Heute tut das allein der
    // Rechnungseditor — und der reicht denselben Kontext an alle abgeleiteten
    // Belege durch, es gibt also keinen Ausstellweg ohne Check. Der
    // Rechnungslauf stellt nichts aus: er legt nur Entwuerfe an und prueft
    // dafuer selbst (billingRunService, base.check). Wer kuenftig hier
    // vorbeikommt, ohne runProjectCheck zu setzen, stellt ohne Ampel aus und
    // schreibt auch kein details.projectCheck fest — dann bitte hier
    // nachziehen und nicht dort.
    //
    // Zwei Kriterien, oder-verknuepft, weil keines allein reicht:
    //
    // 1. Die Belegart. Storno und kaufmaennische Gutschrift sind IMMER
    //    mindernd — auch dann, wenn ihre Zeilen gar keinen LV-Bezug haben
    //    (Storno einer Abschlagsrechnung: eine Pauschalposition, kein
    //    serviceId). Ueber die Zeilen allein waeren sie wieder gesperrt.
    // 2. Die Zeilen. Die Korrekturrechnung laesst sich an der Belegart nicht
    //    einordnen: nach unten gibt sie zurueck, nach oben beansprucht sie.
    //    Sie in NEGATIVE_DOCUMENT_TYPES aufzunehmen waere darum falsch, jene
    //    Menge steuert auch das Umsatzvorzeichen. Eine Abwaertskorrektur lief
    //    deshalb in denselben falsch-positiven Riegel wie frueher das Storno.
    let checkResult: ProjectCheckResult | undefined;
    if (ctx.runProjectCheck) {
        checkResult = await ctx.runProjectCheck(invoice);
        const blocking = !(
            NEGATIVE_DOCUMENT_TYPES.has(invoice.documentType) ||
            onlyReleasesProjectQuantity(invoice.lines)
        );
        if (blocking && !checkResult.canIssue) {
            throw new ProjectCheckBlockedError(
                `Der Projektcheck sperrt das Ausstellen: ${
                    checkResult.findings.find((f) => f.traffic === 'red')?.title ??
                    'schwerwiegender Befund'
                }.`,
                checkResult,
                'rot',
            );
        }
        if (
            blocking &&
            checkResult.requiresAcknowledgement &&
            !ctx.projectCheckAcknowledgement?.trim()
        ) {
            throw new ProjectCheckBlockedError(
                'Der Projektcheck hat Auffälligkeiten gefunden. Bitte eine Kenntnisnahme mit Begründung erfassen.',
                checkResult,
                'kenntnisnahme',
            );
        }
    }

    // Abschlagskette FRISCH pruefen, aus demselben Grund wie den Projektcheck:
    // zwischen Entwurf und Ausstellung koennen Stunden liegen. Die Abzugsliste
    // wurde beim Anlegen eingefroren, und der Statuswechsel eines Abschlags auf
    // "abgeloest" passiert erst beim Ausstellen — zwei parallel angelegte
    // Schlussrechnungen sahen den Abschlag deshalb beide als abzugsfaehig. Ohne
    // diese Pruefung haette der zweite Beleg denselben Abschlag ein zweites Mal
    // abgezogen, und nach der Festschreibung waere das nicht mehr korrigierbar.
    // Externe Alt-Abschlaege (ext:…) sind keine App-Belege: fuer sie gibt es
    // keinen Status, der kippen koennte, und ihre Id ist keine uuid — sie
    // duerfen die invoices-Abfrage weder erreichen noch als "fehlt" gelten.
    //
    // NICHT fuer das Storno. Seine Abzugsliste ist die GESPIEGELTE des
    // Originals (mirrorDeductions): es zieht nichts ab, es hebt einen Abzug
    // auf, der beim Ausstellen des Originals gegolten hat. Ob der Vorbeleg
    // heute noch abzugsfaehig ist, spielt dafuer keine Rolle — Original und
    // Storno muessen auf null aufgehen. Genau daran scheiterte 26091099: die
    // Buchhaltung hatte ZUERST die nie versandte Vorrechnung storniert, und
    // danach liess sich die Schlussrechnung nicht mehr stornieren, weil ihr
    // Storno den inzwischen stornierten Vorbeleg als "nicht mehr
    // abzugsfaehig" fand. Der Nachlauf restoreSupersededAdvances kennt den
    // Fall laengst und laesst einen zwischenzeitlich stornierten Vorbeleg
    // storniert; nur diese Pruefung davor stand ihm im Weg.
    //
    // Dieselbe Kette traf auch das fortschreibende Modell: dort stehen die
    // Abschlaege nach der Schlussrechnung auf SUPERSEDED_BY_FINAL_INVOICE, und
    // isDeductibleAdvance zaehlt genau diesen Status nicht mehr — das Storno
    // einer Schlussrechnung mit Abschlaegen war damit gar nicht ausstellbar,
    // und restoreAdvances weiter unten wurde nie erreicht.
    //
    // Dieselbe Menge wie beim Abloesen/Wiederherstellen weiter unten
    // (NEGATIVE_DOCUMENT_TYPES): was einen Abzug aufhebt, prueft ihn nicht.
    const reversesDeductions = NEGATIVE_DOCUMENT_TYPES.has(invoice.documentType);
    const appAdvanceIdsToCheck = reversesDeductions
        ? []
        : [
              ...new Set(
                  (invoice.advanceDeductions ?? [])
                      .map((d) => d.advanceInvoiceId)
                      .filter((id) => !isExternalAdvanceId(id)),
              ),
          ];
    if (appAdvanceIdsToCheck.length) {
        const advanceIdsToCheck = appAdvanceIdsToCheck;
        const { data: freshAdvances, error: advErr } = await supabase
            .from('invoices')
            .select('id, invoice_number, document_type, status')
            .in('id', advanceIdsToCheck);
        if (advErr) throw advErr;

        type FreshAdvance = {
            id: string;
            invoice_number: string | null;
            document_type: DocumentType;
            status: Invoice['status'];
        };
        const byId = new Map<string, FreshAdvance>(
            ((freshAdvances ?? []) as FreshAdvance[]).map((a) => [a.id, a]),
        );
        // Je Abrechnungsmodell die passende Regel. Kumulativ werden ALLE
        // Vorbelege des Projekts abgezogen — gewoehnliche Rechnungen also, keine
        // Abschlaege. Die Abschlagsregel darauf anzuwenden hielt jede
        // Vorrechnung fuer nicht abzugsfaehig und blockierte einen
        // einwandfreien Beleg mit einer Meldung, die von Abschlaegen sprach,
        // die es gar nicht gibt.
        const cumulativeDoc = invoice.details?.billingMode === 'CUMULATIVE';
        const stillDeductible = cumulativeDoc ? isDeductiblePriorInvoice : isDeductibleAdvance;
        const stale = advanceIdsToCheck.filter((id) => {
            const a = byId.get(id);
            return !a || !stillDeductible({ documentType: a.document_type, status: a.status });
        });
        if (stale.length) {
            const names = stale.map((id) => byId.get(id)?.invoice_number ?? id).join(', ');
            throw new Error(
                cumulativeDoc
                    ? `Die abgezogenen Belege ${names} sind nicht mehr abzugsfähig — vermutlich wurden sie storniert. Bitte den Beleg neu erstellen.`
                    : `Die abgezogenen Abschläge ${names} sind nicht mehr abzugsfähig — vermutlich wurden sie bereits von einer anderen Schlussrechnung abgelöst oder storniert. Bitte den Beleg neu erstellen.`,
            );
        }
    }

    // S5a Stufe 3b: der Tor-Notschalter, FRISCH gelesen und EINMAL je
    // Ausstellung — alle Projekte einer Sammelrechnung laufen durch dasselbe
    // Tor. Kein Cache: ein Administrator, der den Schalter umlegt, wirkt mit
    // der naechsten Ausstellung in jedem Tab, ohne Neuladen. Jeder Lesefehler
    // heisst Journal-Modus (streng, torNotschalter.ts) — der Schalter oeffnet
    // das Tor nur, wenn die Zeile es ausdruecklich sagt. Gelesen VOR dem
    // Statusanspruch: er schreibt nichts und kann nicht scheitern.
    const torModus: TorModus = (await fetchTorNotschalter()).modus;

    // Atomarer Claim ueber einen ECHTEN Zustandswechsel nach ISSUING.
    //
    // Frueher setzte dieser Aufruf APPROVED und filterte zugleich auf APPROVED.
    // Unter READ COMMITTED wertet eine zweite, blockierte Transaktion ihre
    // WHERE-Bedingung nach dem Commit der ersten neu aus — und findet den Status
    // unveraendert vor. Beide Aufrufer gewannen den Claim. APPROVED -> ISSUING
    // ist dagegen eine tatsaechliche Aenderung und passt nur einmal.
    const { data: claimed, error: claimErr } = await supabase
        .from('invoices')
        .update({
            status: 'ISSUING',
            approved_at: invoice.approvedAt ?? new Date().toISOString(),
            approved_by: invoice.approvedBy ?? ctx.actorId,
        })
        .eq('id', id)
        .in('status', ['APPROVED', 'READY_FOR_REVIEW'])
        .select('id');
    if (claimErr) throw claimErr;
    if (!claimed?.length) {
        throw new Error('Die Rechnung wird bereits ausgestellt oder ist es schon.');
    }

    const writeBack: WriteBackRecord[] = [];
    let drawnNumber: string | undefined;

    try {
        // ── Phase A: umkehrbar ────────────────────────────────────────────
        //
        // Je Projekt EIN Durchgang: laden, Zuordnung und Buchung aus demselben
        // Stand planen, speichern. Die Zuordnung entsteht damit an der Stelle,
        // an der Zeitraum und Mengen endgueltig feststehen, und sie kommt aus
        // genau dem Projektstand, in den auch gebucht wurde.
        //
        // Sammelrechnung: die Zuordnung eines Projekts betrifft nur dessen
        // Zeilen. Ein Konflikt-Wiederholungsversuch bei Projekt B fasst die
        // bereits eingefrorenen Zeilen von Projekt A nicht an — deshalb werden
        // die zurueckgegebenen Zeilen einzeln nach Id eingesetzt und die Liste
        // nicht ersetzt.
        const bookingReference = bookingRefFor(id, invoice.documentType);
        let issuedLines = invoice.lines;
        // Der Leistungszeitraum wird hier eingefroren, aus genau den
        // Protokollstaenden, aus denen auch zugeordnet und gebucht wird. Ueber
        // mehrere Projekte hinweg (Sammelrechnung) spannt er sich ueber alle:
        // der Beleg rechnet sie gemeinsam ab und nennt EINEN Zeitraum.
        let performedFrom: string | undefined;
        let performedTo: string | undefined;
        // Ob ueberhaupt ein Projekt durchgerechnet wurde. Dann gilt sein
        // Ergebnis auch, wenn es leer ist: eine Spanne, die der Entwurf von
        // einem frueheren Aufbau mitbringt, darf nicht eingefroren werden.
        let spanFromProjects = false;
        // Die Zweitschriften ins Buchungsjournal — eine je Projekt, in der
        // Reihenfolge der Projektschleife. Sie werden hier nur GESAMMELT;
        // geschrieben wird ganz am Ende, siehe die Begruendung dort. Nichts
        // wird zusammengefasst: je Projekt bleibt ein eigener Plan mit dem
        // previousLog genau dieses Projekts.
        const journalMirrors: JournalMirrorRequest[] = [];
        for (const [projectId, projectLines] of groupLinesByProject(invoice.lines)) {
            const done = await writeBackToProject(
                projectId,
                projectLines,
                bookingReference,
                invoice,
                ctx,
                torModus,
            );
            writeBack.push(done.record);
            if (done.journal) journalMirrors.push(done.journal);
            const byId = new Map(done.lines.map((l) => [l.id, l]));
            issuedLines = issuedLines.map((l) => byId.get(l.id) ?? l);
            spanFromProjects = true;
            if (done.performed.from && (!performedFrom || done.performed.from < performedFrom)) {
                performedFrom = done.performed.from;
            }
            if (done.performed.to && (!performedTo || done.performed.to > performedTo)) {
                performedTo = done.performed.to;
            }
        }
        // Ein von Hand gesetzter Leistungsbeginn (kumulativ,
        // details.cumulativeFromManual) ueberlebt das Ausstellen: ohne diese
        // Zeile schriebe das Einfrieren den ermittelten Beginn zurueck, und der
        // fertige Beleg widerspraeche seinem Entwurf.
        const manualStart = manualCumulativeStart(invoice);
        if (manualStart) performedFrom = manualStart;
        // Die Spanne aus dem Entwurf faellt weg, sobald ein Projekt
        // durchgerechnet wurde — sonst ueberlebte ein Wert aus einem frueheren
        // Aufbau (fortschreibend etwa "02.02." aus der Zeit, als der Beginn
        // noch nicht am Zeitraum gekappt war) jede leere Neuermittlung.
        const { performedFrom: _alteSpanneVon, performedTo: _alteSpanneBis, ...ohneSpanne } =
            invoice.details ?? {};
        void _alteSpanneVon;
        void _alteSpanneBis;
        const detailsBasis = invoice.details && spanFromProjects ? ohneSpanne : invoice.details;
        const issued: Invoice = {
            ...invoice,
            lines: issuedLines,
            // Ohne datierten Eintrag bleibt die Angabe weg statt geraten zu
            // werden — performancePeriod faellt dann auf seine Naeherung
            // zurueck. Ein Storno erbt sie ohnehin vom Ursprungsbeleg.
            details:
                performedFrom || performedTo
                    ? {
                          ...detailsBasis,
                          ...(performedFrom ? { performedFrom } : {}),
                          ...(performedTo ? { performedTo } : {}),
                      }
                    : detailsBasis,
        };

        // ── Phase B: nicht umkehrbar ──────────────────────────────────────
        //
        // Typziffer der Belegnummer aus dem PRIMAEREN abgerechneten Projekt:
        // Beleg und Projekt sollen im selben Typkreis liegen (1 = SiGeKo,
        // 2 = SiFa, 3 = Sonstige). invoiceProjectRefs liefert die Projekte in
        // der Rangfolge des Belegs — bei der Sammelrechnung entscheidet das
        // erste, bei der Einzelrechnung ist es primaryProjectId.
        //
        // Der VOLLE Beleg geht hier hinein, und der eingefrorene Vermerk
        // details.projects zaehlt damit bewusst mit: er traegt die
        // Projektnummer in genau den Faellen, in denen die Zeilen sie nicht
        // haben. Eine Abschlagsrechnung hat eine Pauschalzeile ohne
        // projectNumber, ebenso ein Storno mit gespiegelten Pauschalzeilen —
        // ohne den Vermerk fiele die Typziffer dort auf '1' zurueck, obwohl das
        // Projekt in Kreis 2 oder 3 liegt. Die Nummer ist danach verbraucht und
        // die Ziffer steht unveraenderlich auf dem Beleg; ein solcher Fehlgriff
        // ist nicht zurueckzunehmen.
        //
        // Der NEUE Listen-Rueckfall gilt hier ausdruecklich NICHT
        // (invoiceListProjectRefs): er laesst ohne Kopfprojekt und ohne Zeile
        // mit Projektbezug auch Vermerkseintraege gelten, die der Beleg selbst
        // gar nicht als abgerechnet ausweist. Das ist eine Verbesserung der
        // ANZEIGE in der Belegliste und darf keinen Nummernkreis wechseln.
        const typeDigit = typeDigitFromProjectNumber(invoiceProjectRefs(invoice)[0]?.number);
        drawnNumber = await nextDocumentNumber(invoice.series, typeDigit);

        const frozen: Invoice = {
            ...issued,
            invoiceNumber: drawnNumber,
            customerSnapshot: documentCustomer,
            companySnapshot: {
                name: ctx.company.name,
                address: ctx.company.address,
                email: ctx.company.email,
                phone: ctx.company.phone,
                vatId: ctx.config.vatId,
                taxNumber: ctx.config.taxNumber,
                registerCourt: ctx.config.registerCourt,
                registerNumber: ctx.config.registerNumber,
                managingDirectors: ctx.config.managingDirectors,
                iban: ctx.config.iban,
                bic: ctx.config.bic,
                bankName: ctx.config.bankName,
                accountHolder: ctx.config.accountHolder,
            },
            // Die Vorbelegliste wird hier NICHT neu gerechnet, sondern aus
            // dem Entwurf uebernommen — genauso wie die Abschlagsabzuege
            // daneben. Beide sind im Entwurf entstanden, stehen im Datensatz
            // und wurden dort vom Freigebenden gesehen. Frisch gerechnet
            // koennte zwischen Freigabe und Ausstellung ein weiterer Vorbeleg
            // oder ein Backfill hinzugekommen sein, und der ausgestellte Beleg
            // forderte einen anderen Betrag als der freigegebene.
            //
            // Der Storno spiegelt seine gespeicherten Einbehalt-Felder (die
            // negierten des Originals, gesetzt in cancelInvoice) statt neu zu
            // rechnen; alle anderen Belegarten rechnen kumulativ mit dem
            // effektiven Satz, und der fuenfte Parameter bleibt dort ohne
            // Wirkung.
            totals: invoiceTotalsWithRetention(
                invoice.documentType,
                invoice.lines,
                invoice.advanceDeductions,
                {
                    percent: effectiveRetentionPercent(invoice.documentType, invoice.details),
                    priorRetentions: invoice.priorRetentions ?? [],
                },
                invoice.documentType === 'CANCELLATION_INVOICE'
                    ? retentionTotalsOf(invoice.totals)
                    : undefined,
            ),
        };

        const bytes = await ctx.renderPdf(frozen, documentCustomer);
        const pdfSha256 = await sha256Hex(bytes);
        const year = frozen.invoiceDate.slice(0, 4);
        const path = `${year}/${drawnNumber}/${drawnNumber}.pdf`;

        // upsert:false ist tragend — ein zweiter Schreibversuch auf denselben
        // Pfad muss scheitern, nicht das Archiv ueberschreiben.
        const { error: upErr } = await supabase.storage
            .from(BUCKET)
            .upload(path, new Blob([bytes as BlobPart], { type: 'application/pdf' }), {
                upsert: false,
                contentType: 'application/pdf',
            });
        if (upErr) throw upErr;

        const { data: snap, error: snapErr } = await supabase
            .from('invoice_snapshots')
            .insert({
                invoice_id: id,
                invoice_number: drawnNumber,
                issued_by: ctx.actorId,
                payload: frozen as unknown as Record<string, unknown>,
                pdf_path: path,
                pdf_sha256: pdfSha256,
                pdf_bytes: bytes.byteLength,
                generator_version: ctx.generatorVersion ?? '',
            })
            .select('id')
            .single();
        if (snapErr) throw snapErr;

        // Abgezogene Abschlaege abloesen — VOR dem Einfrieren, nicht danach.
        //
        // Zwei Gruende. Erstens scheitert ein Fehler hier noch die ganze
        // Ausstellung: die Nummer wird verworfen, die Rueckschreibung
        // zurueckgenommen, der Beleg bleibt Entwurf. Eine Schlussrechnung,
        // deren Abschlaege NICHT abgeloest wurden, ist gefaehrlich — der
        // naechste Beleg zoege dieselben Abschlaege noch einmal ab.
        // Zweitens laesst der Festschreibungs-Trigger nach dem Wechsel auf
        // ISSUED keinen Vermerk mehr am Beleg zu; ein Scheitern liesse sich
        // danach nirgends dauerhaft festhalten.
        // Bei kumulativer Abrechnung duerfen die abgezogenen Belege NICHT
        // abgeloest werden: die naechste Rechnung zeigt wieder den gesamten
        // Leistungsstand und muss sie erneut abziehen. Wuerde man sie hier
        // verbrauchen, fehlte dort der Abzug — der Kunde zahlte die frueheren
        // Rechnungen ein zweites Mal.
        const cumulative = invoice.details?.billingMode === 'CUMULATIVE';
        const advanceIds = cumulative
            ? []
            : [
                  ...new Set(
                      (invoice.advanceDeductions ?? [])
                          .map((d) => d.advanceInvoiceId)
                          // Externe Alt-Abschlaege haben keinen Belegstatus,
                          // den man abloesen koennte.
                          .filter((id) => !isExternalAdvanceId(id)),
                  ),
              ];
        if (advanceIds.length) {
            // Ein Storno und eine Gutschrift heben den Abzug auf, sie
            // wiederholen ihn nicht. Ohne diese Unterscheidung passierte
            // zweierlei: das Storno versuchte dieselben Abschlaege ein zweites
            // Mal abzuloesen und scheiterte daran, und der Abschlag blieb
            // dauerhaft verbraucht — die naechste Schlussrechnung konnte ihn
            // nie wieder abziehen ("nicht mehr abzugsfaehig").
            if (NEGATIVE_DOCUMENT_TYPES.has(invoice.documentType)) {
                await restoreAdvances(advanceIds);
            } else {
                await supersedeAdvances(advanceIds);
            }
        }

        const { data: updated, error: updErr } = await supabase
            .from('invoices')
            .update({
                status: 'ISSUED',
                invoice_number: drawnNumber,
                issued_at: new Date().toISOString(),
                issued_by: ctx.actorId,
                snapshot_id: snap.id,
                pdf_url: path,
                // Die Zeilen MUESSEN mit: sie tragen seit der Zuordnung mehr
                // als beim Anlegen, naemlich welche Protokolleintraege dieser
                // Beleg abrechnet. Ohne sie stand die Zuordnung nur im PDF und
                // im Archiv — die Spalte lines, aus der jede Auswertung liest,
                // blieb auf dem Stand des Entwurfs. Der Zeitraumfilter bot
                // denselben Monat danach erneut an.
                lines: frozen.lines,
                customer_snapshot: frozen.customerSnapshot,
                company_snapshot: frozen.companySnapshot,
                net_total_cents: frozen.totals.netTotalCents,
                tax_total_cents: frozen.totals.taxTotalCents,
                gross_total_cents: frozen.totals.grossTotalCents,
                prepaid_cents: frozen.totals.prepaidCents,
                // Der Einbehalt wird MIT eingefroren. Ohne diese drei Spalten
                // stuende er nur im Snapshot und im PDF: der naechste Beleg
                // liest ihn aber aus der Tabelle (priorRetentionsFromDeductions)
                // und faende nichts — er hielte den vollen Satz ein zweites
                // Mal ein, und die Schlussrechnung gaebe nichts frei.
                retention_cents: frozen.totals.retentionCents ?? 0,
                retention_release_cents: frozen.totals.retentionReleaseCents ?? 0,
                // Feld priorRetentions, Spalte retention_releases — siehe mapPriorRetentions.
                retention_releases: frozen.priorRetentions ?? [],
                payable_cents: frozen.totals.payableCents,
                tax_groups: frozen.totals.taxGroups,
                details: {
                    // frozen.details, nicht invoice.details: der eingefrorene
                    // Leistungszeitraum entsteht erst in Phase A und steckt
                    // bereits im gerenderten PDF und im Snapshot. Die Spalte
                    // muss dasselbe sagen wie das Archiv.
                    ...frozen.details,
                    writeBack,
                    // Der Projektcheck wird MIT festgeschrieben: bei einer
                    // spaeteren Pruefung soll nachvollziehbar sein, was das
                    // System zum Zeitpunkt der Ausstellung wusste und wer eine
                    // gelbe Ampel mit welcher Begruendung passieren liess.
                    ...(checkResult
                        ? {
                              projectCheck: {
                                  traffic: checkResult.traffic,
                                  findings: checkResult.findings,
                                  checkedAt: new Date().toISOString(),
                                  acknowledgedBy: ctx.projectCheckAcknowledgement
                                      ? ctx.actorId
                                      : undefined,
                                  acknowledgedAt: ctx.projectCheckAcknowledgement
                                      ? new Date().toISOString()
                                      : undefined,
                                  acknowledgementNote: ctx.projectCheckAcknowledgement,
                              },
                          }
                        : {}),
                },
            })
            .eq('id', id)
            .select(SELECT)
            .single();
        if (updErr) throw updErr;

        // Fehler hier NICHT verschlucken: supabase.rpc lehnt nicht ab, sondern
        // liefert { error }. Ohne Pruefung bliebe die Nummer im Protokoll als
        // DRAWN stehen, obwohl sie auf einer ausgestellten Rechnung steht — und
        // genau dieses Protokoll ist der GoBD-Nachweis fuer die Nummernfolge.
        // Werfen darf es trotzdem nicht: die Rechnung IST ausgestellt.
        const { error: markErr } = await supabase.rpc('mark_document_number_used', {
            p_number: drawnNumber,
            p_invoice_id: id,
        });
        if (markErr) {
            console.error(
                `Rechnung ${drawnNumber} wurde ausgestellt, aber im Nummernprotokoll nicht als verwendet markiert. Der Eintrag muss von Hand auf USED gesetzt werden.`,
                markErr,
            );
        }

        // Das Ergebnis VOR dem Spiegeln bilden: danach darf im try nichts mehr
        // stehen, das werfen kann. `return` selbst wirft nicht — damit ist die
        // Zeile unten die letzte Anweisung, und ab dem Spiegeln kann der
        // kompensierende catch nicht mehr erreicht werden.
        const ergebnis = mapInvoiceRow(updated);

        // ─── Zweitschrift ins Buchungsjournal (S4b) ─────────────────────────
        //
        // GENAU HIER, und keine Zeile frueher. Alles oberhalb liegt im try,
        // dessen catch `revertWriteBack` faehrt: ein Fehlschlag beim zweiten
        // Projekt, bei der Nummernziehung, beim PDF, beim Snapshot, beim
        // Abloesen der Abschlaege oder beim finalen Update nimmt die
        // Rueckschreibung wieder zurueck. Haette das Journal da schon Zeilen,
        // blieben sie stehen — es kennt weder Update noch Delete —, und der
        // naechste Versuch legte neue mit neuen ids daneben.
        //
        // Der Beleg steht ab dem `updErr`-Check oben unumkehrbar auf ISSUED,
        // mit Nummer, PDF und Snapshot. Was danach kommt, ist bereits so
        // gebaut, dass es NICHT mehr wirft: `mark_document_number_used`
        // protokolliert seinen Fehler und laeuft weiter, aus genau demselben
        // Grund. Diese Stelle ist damit die erste, an der die Kompensation
        // sicher nicht mehr laufen kann.
        //
        // Der eigene try ist so eng wie moeglich um den Spiegel gelegt und
        // umschliesst nichts sonst: `mirrorEntriesToJournal` faengt zwar selbst
        // alles ab, aber eine ausgestellte Rechnung darf unter keinen Umstaenden
        // wegen einer fehlgeschlagenen Zweitschrift zurueckgerollt werden.
        //
        // Je Projekt ein eigener Aufruf mit dem previousLog genau dieses
        // Projekts; bei einer Teilrechnung gehen die Zeilen aller Teilnehmer
        // einzeln mit (gleicher Beleg, gleiche Position, gleicher Tag,
        // Unterschied nur customerId/splitPartyId).
        try {
            for (const req of journalMirrors) await mirrorEntriesToJournal(req);
        } catch (e) {
            console.error('[serviceJournal] Zweitschrift der Rückschreibung fehlgeschlagen', e);
        }

        return ergebnis;
    } catch (err) {
        if (drawnNumber) {
            const { error: voidErr } = await supabase.rpc('void_document_number', {
                p_number: drawnNumber,
                p_reason: String((err as Error)?.message ?? err).slice(0, 500),
            });
            if (voidErr) {
                console.error(
                    `Nummer ${drawnNumber} konnte nicht als verworfen protokolliert werden. Die Lücke im Nummernkreis ist damit undokumentiert und muss von Hand nachgetragen werden.`,
                    voidErr,
                );
            }
        }

        const reverted = await revertWriteBack(writeBack);

        // Status NUR zuruecksetzen, wenn wir den Claim noch halten. Ohne diese
        // Bedingung koennte ein fehlgeschlagener zweiter Aufruf eine bereits
        // erfolgreich ausgestellte Rechnung wieder auf APPROVED setzen — und
        // damit aus der Festschreibung herausholen, obwohl Nummer, PDF und
        // Snapshot schon existieren.
        if (reverted.allReverted) {
            await supabase
                .from('invoices')
                .update({ status: 'APPROVED' })
                .eq('id', id)
                .eq('status', 'ISSUING');
        } else {
            // Die Rueckschreibung konnte nicht vollstaendig zurueckgenommen
            // werden. Die Rechnung darf jetzt NICHT wieder bearbeitbar werden:
            // im Projekt stehen gebuchte Mengen, zu denen es keinen Beleg gibt.
            // DISPUTED zwingt zur manuellen Klaerung, statt den Widerspruch
            // stillschweigend weiterlaufen zu lassen.
            await supabase
                .from('invoices')
                .update({
                    status: 'DISPUTED',
                    details: {
                        ...invoice.details,
                        writeBack,
                        failedRevert: reverted.failedProjectIds,
                    },
                })
                .eq('id', id)
                .eq('status', 'ISSUING');
            console.error(
                `Rechnung ${id}: Rückschreibung konnte für die Projekte ${reverted.failedProjectIds.join(', ')} nicht zurückgenommen werden. Der Beleg steht auf DISPUTED und muss von Hand geklärt werden.`,
            );
        }
        throw err;
    }
};

// ─── Storno ─────────────────────────────────────────────────────────────────

// ─── Abschlag, Schluss, Rest ────────────────────────────────────────────────

/**
 * Legt eine Abschlagsrechnung an.
 *
 * Sie traegt EINE Pauschalposition und keinen Bezug auf Leistungspositionen —
 * beim Ausstellen darf sie deshalb nichts ins Projekt zurueckschreiben. Das ist
 * kein Versehen: die Leistung ist zum Zeitpunkt des Abschlags noch nicht
 * erbracht, und ein Rueckschreiben wuerde sie als erbracht ausweisen.
 */
export const createAdvanceInvoice = async (
    input: {
        customerId: string;
        primaryProjectId?: string;
        request: AdvanceRequest;
        /** Teilrechnung: Gruppe, Teilnehmer und Anteil dieses Abschlags. */
        splitGroupId?: string;
        splitPartyId?: string;
        splitShareBp?: number;
        invoiceDate?: string;
        deliveryDateFrom?: string;
        deliveryDateTo?: string;
    },
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<Invoice> => {
    const line = buildAdvanceLine(input.request, crypto.randomUUID());
    return createInvoiceDraft(
        {
            customerId: input.customerId,
            documentType: 'ADVANCE_INVOICE',
            primaryProjectId: input.primaryProjectId,
            invoiceDate: input.invoiceDate,
            deliveryDateFrom: input.deliveryDateFrom,
            deliveryDateTo: input.deliveryDateTo,
            lines: [line],
            splitGroupId: input.splitGroupId,
            splitPartyId: input.splitPartyId,
            splitShareBp: input.splitShareBp,
        },
        cfg,
        todayIso,
    );
};

/** Bisherige Abschlagsrechnungen eines Projekts. */
/**
 * Alle Belege eines Projekts — Grundlage des Abzugs bei kumulativer Abrechnung.
 *
 * Anders als fetchAdvancesForProject NICHT auf Abschlagsrechnungen begrenzt:
 * kumulativ wird alles abgezogen, was bereits gefordert wurde, gleich welcher
 * Belegart. Welche Status zaehlen, entscheidet deductionsFromPriorInvoices.
 */
/**
 * Alle Belege eines Projekts.
 *
 * `splitScope` schraenkt auf EINEN Teilnehmer der Aufteilung ein. Gebraucht
 * wird das bei geteilten Projekten und nur dort: kumulativ ist diese Liste die
 * Abzugsgrundlage, und die Teilrechnung eines Teilnehmers darf ausschliesslich
 * SEINE Vorrechnungen abziehen. Zoege sie auch die der Geschwister ab, bliebe
 * fast nichts zu fordern — und der Beleg saehe dabei richtig aus.
 *
 * Teilnehmer und nicht Auftraggeber, seit es Abschnitte gibt: vier Haeuser
 * DESSELBEN Auftraggebers haben vier getrennte Abzugsgrundlagen.
 *
 * Ohne die Einschraenkung bleibt es beim bisherigen Verhalten. Sie einem
 * ungeteilten Projekt aufzuzwingen waere falsch: dort wechselt der Auftraggeber
 * gelegentlich (Verkauf, Umfirmierung), und die Vorrechnungen des Vorgaengers
 * gehoeren weiterhin abgezogen.
 */
/**
 * Abschlagsrechnungen eines GETEILTEN Projekts: je Auftraggeber eine.
 *
 * Ein einziger Abschlag an den bisherigen Projektkunden forderte den vollen
 * Betrag von einem, der nur seinen Anteil schuldet — und die Schlussrechnung
 * zoege ihn spaeter bei allen ab.
 *
 * ZWEI Dinge sind hier tragend:
 *
 * 1. Geteilt wird der FERTIGE Betrag, nicht die Bemessungsgrundlage. Erst die
 *    Grundlage zu teilen und dann je Teil zu runden erzeugt Differenzen: 2 Cent
 *    Grundlage, 50 %, haelftig geteilt ergaeben zweimal 1 Cent — der ungeteilte
 *    Abschlag waere 1 Cent. Der Prozentsatz wandert deshalb in den Positionstext
 *    und der Anteil als fester Betrag in die Zeile.
 *
 * 2. Alles oder nichts. Scheitert ein Beleg, werden die bereits angelegten
 *    entfernt. Eine halbe Abschlagsgruppe fordert lautlos zu wenig, und jeder
 *    einzelne Beleg darin sieht dabei richtig aus.
 */
export const createSplitAdvanceInvoices = async (
    input: {
        primaryProjectId: string;
        split: ProjectSplit;
        request: AdvanceRequest;
    },
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<Invoice[]> => {
    // Geschluesselt nach TEILNEHMER: bei mehreren Abschnitten desselben
    // Auftraggebers faellt sonst der zweite in denselben Kartenplatz wie der
    // erste — die Quotensumme kaeme nicht mehr auf 100 %, und der Abschlag
    // waere gar nicht mehr aufteilbar.
    const quoten = new Map(
        input.split.participants.map((t) => [t.partyId, t.defaultShareBp] as const),
    );
    const summe = [...quoten.values()].reduce((a, b) => a + b, 0);
    if (summe !== SHARE_FULL_BP) {
        throw new Error(
            `Die Grundquoten des Aufteilungsschlüssels ergeben ${formatShare(summe)} statt 100,00 %. Ein Abschlag lässt sich damit nicht aufteilen.`,
        );
    }

    // Derselbe Rechenweg wie in buildAdvanceLine — hier einmal, fuer die ganze
    // Gruppe. Ein zweiter Rechenweg an zweiter Stelle waere die naechste
    // Groesse, die auseinanderlaeuft.
    const gesamtNetto = advanceNetCents(input.request);
    const anteile = splitCents(gesamtNetto, quoten);

    // Die Gruppe ist kein Schmuck: ohne sie liesse sich ein Geschwisterbeleg
    // loeschen und die uebrigen einzeln ausstellen — die Aufteilung forderte
    // dauerhaft weniger als 100 %, und jeder einzelne Beleg saehe dabei
    // richtig aus. Der Riegel beim Ausstellen (groupShareIssue) haengt daran.
    const splitGroupId = crypto.randomUUID();
    const created: Invoice[] = [];
    try {
        for (const teilnehmer of input.split.participants) {
            const anteil = anteile.get(teilnehmer.partyId) ?? 0;
            // Ein Anteil, der auf null Cent faellt, bekommt keinen Beleg: eine
            // Rechnung ueber 0,00 EUR ist keine Forderung, sondern eine
            // verbrauchte Nummer.
            if (anteil <= 0) continue;
            const bp = quoten.get(teilnehmer.partyId) ?? 0;
            created.push(
                await createAdvanceInvoice(
                    {
                        customerId: teilnehmer.customerId,
                        primaryProjectId: input.primaryProjectId,
                        splitGroupId,
                        splitPartyId: teilnehmer.partyId,
                        splitShareBp: bp,
                        request: {
                            ...input.request,
                            // Der Anteil als FESTER Betrag: ein zweites Mal
                            // prozentual zu rechnen rundete ein zweites Mal.
                            percent: undefined,
                            fixedNetCents: anteil,
                            // Der Prozentsatz bleibt lesbar — er gehoert auf den
                            // Beleg, denn er erklaert, wovon dieser Abschlag ein
                            // Anteil ist.
                            title:
                                input.request.title ??
                                `${input.request.ordinal ? `${input.request.ordinal}. ` : ''}Abschlag${
                                    input.request.percent != null
                                        ? ` (${input.request.percent} % von ${formatCents(input.request.basisNetCents)})`
                                        : ''
                                } — Anteil ${formatShare(bp)}`,
                        },
                    },
                    cfg,
                    todayIso,
                ),
            );
        }
    } catch (e) {
        const uebrig: string[] = [];
        for (const inv of created) {
            try {
                await deleteInvoice(inv.id);
            } catch {
                uebrig.push(inv.id);
            }
        }
        if (!uebrig.length) throw e;
        throw new Error(
            `${e instanceof Error ? e.message : String(e)} ACHTUNG: ${uebrig.length} bereits angelegte(r) Abschlagsentwurf/-entwürfe konnten nicht entfernt werden (${uebrig.join(', ')}) und müssen von Hand gelöscht werden.`,
        );
    }

    // Dieselbe Regel wie beim Ausstellen, hier schon bei der Anlage: tragen die
    // entstandenen Belege zusammen keine 100 %, ist die Gruppe von Anfang an
    // unvollstaendig und liesse sich NIE ausstellen.
    //
    // Der Fall ist kein Randfall: bei 1 Cent und 50/50 bekommt einer den Cent
    // und der andere nichts. Ein Entwurf, der dauerhaft an seinem eigenen
    // Riegel haengt, ist schlimmer als eine klare Absage.
    const unvollstaendig = groupShareIssue(created.map((i) => i.splitShareBp));
    if (unvollstaendig) {
        const uebrig: string[] = [];
        for (const inv of created) {
            try {
                await deleteInvoice(inv.id);
            } catch {
                uebrig.push(inv.id);
            }
        }
        throw new Error(
            `${unvollstaendig.message} Der Betrag ist zu klein, um ihn auf alle Auftraggeber aufzuteilen.${
                uebrig.length
                    ? ` ACHTUNG: ${uebrig.length} Entwurf/Entwürfe konnten nicht entfernt werden (${uebrig.join(', ')}).`
                    : ''
            }`,
        );
    }
    return created;
};

/**
 * Die Vorbelege EINES Teilnehmers — und ein Abbruch, wenn sich etwas nicht
 * zuordnen laesst.
 *
 * Der Riegel steht hier und nicht nur beim Anlegen der Gruppe, weil an dieser
 * Stelle das Geld entsteht: aus dieser Liste wird der Abzug gebildet. Ein
 * Vorbeleg, der weder eindeutig diesem Teilnehmer noch eindeutig einem anderen
 * gehoert, hat zwei falsche Antworten — weggelassen zahlt der Kunde doppelt,
 * mitgezaehlt fordert die Gruppe zu wenig. Beide sehen auf dem fertigen Beleg
 * richtig aus. Also keine Antwort, sondern eine Meldung.
 */
const scopeOwn = <T extends { splitPartyId?: string; customerId?: string; invoiceNumber?: string }>(
    rows: readonly T[],
    scope: PartyScope,
    was: string,
): T[] => {
    const { own, ambiguous } = scopeToParty(rows, scope);
    if (ambiguous.length) {
        throw new Error(
            `${ambiguous.length} ${was} dieses Auftraggebers (${ambiguous
                .map((r) => r.invoiceNumber ?? '(ohne Nummer)')
                .join(', ')}) sind keinem Abschnitt zugeordnet. Solange das offen ist, ließe sich der Abzug nur raten.`,
        );
    }
    return own;
};

export const fetchProjectInvoices = async (
    projectId: string,
    splitScope?: PartyScope,
): Promise<Invoice[]> => {
    let query = supabase.from('invoices').select(SELECT).eq('primary_project_id', projectId);
    // Vorgefiltert wird weiterhin auf den AUFTRAGGEBER — er steht als Spalte in
    // der Abfrage und haelt die Menge klein. Die feine Trennung nach Teilnehmer
    // passiert danach in `scopeOwn`: sie braucht den Schluessel und laesst sich
    // in PostgREST nicht ausdruecken, ohne den Altbestand ohne Stempel zu
    // verlieren.
    if (splitScope) query = query.eq('customer_id', splitScope.customerId);
    const { data, error } = await query.order('invoice_date', { ascending: true });
    if (error) throw error;
    const rows = (data || []).map(mapInvoiceRow);
    return splitScope ? scopeOwn(rows, splitScope, 'Rechnungen') : rows;
};

// ── Externe Alt-Abschlaege (public.external_advances) ───────────────────────
// Bestandsprojekte: Abschlaege aus der Zeit vor der App. Reine Abzugsdaten
// der kumulativen Abrechnung — keine Belege (kein Nummernkreis, kein
// Snapshot, kein DATEV). Fachliche Umrechnung: services/externalAdvances.ts.

const mapExternalAdvanceRow = (row: any): ExternalAdvance => ({
    id: row.id,
    projectId: row.project_id,
    invoiceNumber: row.invoice_number,
    invoiceDate: row.invoice_date ?? undefined,
    grossCents: row.gross_cents,
    taxRateBp: row.tax_rate_bp,
    note: row.note ?? undefined,
    customerId: row.customer_id ?? undefined,
    splitPartyId: row.split_party_id ?? undefined,
});

/**
 * Externe Alt-Abschlaege eines Projekts.
 *
 * `splitScope` schraenkt auf EINEN Teilnehmer der Aufteilung ein — gebraucht
 * bei geteilten Projekten. Ohne die Einschraenkung zoege die Teilrechnung des
 * einen auch die Abschlaege der uebrigen ab und forderte fast nichts mehr; der
 * Fehler saehe auf dem Beleg richtig aus.
 *
 * Altbelege OHNE Kunden fallen dabei HERAUS — und das ist der schwierige Teil.
 *
 * Sie mitzunehmen war der erste Entwurf und war falsch: bei 50/50 zoege JEDER
 * Teilbeleg denselben Alt-Abschlag ueber 30 EUR voll ab, die Gruppe
 * beruecksichtigte also 60 statt 30. Sie anteilig zu verteilen waere geraten —
 * niemand weiss, ob der Altbeleg dem Verhaeltnis von heute folgt.
 *
 * Deshalb: heraus, UND der Ausstellpfad sperrt, solange es sie gibt
 * (assertExternalAdvancesAssigned). Der Kunde wird am Altbeleg nachgetragen,
 * dann ist die Lage eindeutig. Fehlte die Sperre, waere das Herausfiltern die
 * schlimmere Variante — der Abzug fiele lautlos weg und der Kunde zahlte
 * doppelt.
 */
export const fetchExternalAdvances = async (
    projectId: string,
    splitScope?: PartyScope,
): Promise<ExternalAdvance[]> => {
    let query = supabase.from('external_advances').select('*').eq('project_id', projectId);
    if (splitScope) query = query.eq('customer_id', splitScope.customerId);
    const { data, error } = await query
        .order('invoice_date', { ascending: true, nullsFirst: true })
        .order('invoice_number', { ascending: true });
    if (error) throw error;
    const rows = (data || []).map(mapExternalAdvanceRow);
    return splitScope ? scopeOwn(rows, splitScope, 'Alt-Abschläge') : rows;
};

/**
 * Alle externen Alt-Abschlaege, nach Projekt gebuendelt.
 *
 * Fuer den Anlagedialog: er muss fuer JEDES gelistete Projekt wissen, ob eine
 * ungedeckte Altfakturierung im LV bereits durch einen Alt-Abschlag abgegolten
 * ist (projectUncoveredPriorBilling). Je Projekt einzeln zu fragen waeren bei
 * zweihundert Projekten zweihundert Abfragen.
 *
 * Bewusst OHNE Projektfilter statt mit einer `in`-Liste: die Liste truege
 * zweihundert uuids in die Adresszeile, und die Tabelle ist klein — sie fuehrt
 * nur die Altbelege von Bestandsprojekten, und je Projekt eine Handvoll. Die
 * Zeilensicht (RLS) begrenzt ohnehin auf den eigenen Mandanten.
 *
 * Ein leeres Ergebnis heisst „keine erfasst"; ein Fehlschlag wirft und wird vom
 * Aufrufer als „nicht ermittelt" behandelt — die beiden duerfen nicht
 * zusammenfallen (siehe ProjectPriorBilling.externalAdvances).
 *
 * Seitenweise: „klein" heisst nicht „unter 1000" (23.10.2026: 259 Zeilen), und
 * PostgREST schneidet dort still ab. Ein fehlender Alt-Abschlag liesse eine
 * laengst abgegoltene Altfakturierung als ungedeckt erscheinen. `id` zuletzt
 * macht die Sortierung eindeutig.
 */
export const fetchAllExternalAdvances = async (): Promise<Map<string, ExternalAdvance[]>> => {
    const data = await ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
        supabase
            .from('external_advances')
            .select('*')
            .order('invoice_date', { ascending: true, nullsFirst: true })
            .order('invoice_number', { ascending: true })
            .order('id', { ascending: true })
            .range(von, bis),
    );
    const out = new Map<string, ExternalAdvance[]>();
    for (const row of data) {
        const advance = mapExternalAdvanceRow(row);
        const found = out.get(advance.projectId);
        if (found) found.push(advance);
        else out.set(advance.projectId, [advance]);
    }
    return out;
};

export const createExternalAdvance = async (
    input: Omit<ExternalAdvance, 'id'>,
): Promise<ExternalAdvance> => {
    const { data, error } = await supabase
        .from('external_advances')
        .insert({
            project_id: input.projectId,
            invoice_number: input.invoiceNumber.trim(),
            invoice_date: input.invoiceDate || null,
            gross_cents: input.grossCents,
            tax_rate_bp: input.taxRateBp,
            note: input.note?.trim() || null,
            // Leer bleibt NULL: ein Altbeleg aus der Zeit vor der Aufteilung
            // hat keinen Auftraggeber, und einen zu erfinden waere schlimmer
            // als keiner. Bei geteilten Projekten sperrt NULL das Ausstellen.
            customer_id: input.customerId || null,
            split_party_id: input.splitPartyId || null,
        })
        .select('*')
        .single();
    if (error) throw error;
    return mapExternalAdvanceRow(data);
};

export const updateExternalAdvance = async (
    id: string,
    patch: Partial<Omit<ExternalAdvance, 'id' | 'projectId'>>,
): Promise<ExternalAdvance> => {
    const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.invoiceNumber !== undefined) row.invoice_number = patch.invoiceNumber.trim();
    if (patch.invoiceDate !== undefined) row.invoice_date = patch.invoiceDate || null;
    if (patch.grossCents !== undefined) row.gross_cents = patch.grossCents;
    if (patch.taxRateBp !== undefined) row.tax_rate_bp = patch.taxRateBp;
    if (patch.note !== undefined) row.note = patch.note?.trim() || null;
    if (patch.customerId !== undefined) row.customer_id = patch.customerId || null;
    if (patch.splitPartyId !== undefined) row.split_party_id = patch.splitPartyId || null;
    const { data, error } = await supabase
        .from('external_advances')
        .update(row)
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw error;
    return mapExternalAdvanceRow(data);
};

export const deleteExternalAdvance = async (id: string): Promise<void> => {
    const { error } = await supabase.from('external_advances').delete().eq('id', id);
    if (error) throw error;
};

/**
 * Belege eines Kunden fuer die Kundenakte — inklusive der Belege gemergter
 * Quellkunden (deren Rechnungen bleiben beim Merge am alten Kunden, GoBD).
 */
export const fetchInvoicesForCustomer = async (customerIds: string[]): Promise<Invoice[]> => {
    if (!customerIds.length) return [];
    const { data, error } = await supabase
        .from('invoices')
        .select(SELECT)
        .in('customer_id', customerIds)
        .order('invoice_date', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapInvoiceRow);
};

export const fetchAdvancesForProject = async (
    projectId: string,
    splitScope?: PartyScope,
): Promise<Invoice[]> => {
    // splitScope: nur die Abschlaege DIESES Teilnehmers. Siehe
    // fetchProjectInvoices — dieselbe Begruendung, dieselbe Bedingung.
    let query = supabase
        .from('invoices')
        .select(SELECT)
        .eq('primary_project_id', projectId)
        .eq('document_type', 'ADVANCE_INVOICE');
    if (splitScope) query = query.eq('customer_id', splitScope.customerId);
    const { data, error } = await query.order('invoice_date', { ascending: true });
    if (error) throw error;
    const rows = (data || []).map(mapInvoiceRow);
    return splitScope ? scopeOwn(rows, splitScope, 'Abschlagsrechnungen') : rows;
};

/**
 * Legt einen Beleg an, der die Abschlaege des Projekts abzieht.
 *
 * Drei Belegarten nutzen denselben Mechanismus:
 *  - FINAL_INVOICE     Schlussrechnung, Projekt abgeschlossen
 *  - REMAINING_INVOICE Restrechnung, Projekt laeuft weiter
 *  - INVOICE           Teilleistungsrechnung: eine gewoehnliche Rechnung ueber
 *                      eine erbrachte Teilleistung, von der Abschlaege
 *                      abgezogen werden. Rechtlich eine normale Rechnung, keine
 *                      Schlussrechnung — deshalb bleibt die Belegart INVOICE.
 *
 * Der Abzug haengt nirgends an der Belegart: supersedeAdvances loest beim
 * Ausstellen genau die Abschlaege ab, die in advanceDeductions stehen. Ein
 * Doppelabzug ist damit auch ueber gemischte Belegarten hinweg ausgeschlossen.
 *
 * Der Abzug mindert ausschliesslich den Zahlbetrag; ausgewiesener Umsatz und
 * Umsatzsteuer bleiben die der GESAMTEN Leistung (§14 UStG). Waeren sie
 * gemindert, waere der Vorsteuerabzug des Kunden falsch.
 *
 * Die Verknuepfung DEDUCTS_ADVANCE entsteht sofort, das Abloesen der Abschlaege
 * (SUPERSEDED_BY_FINAL_INVOICE) dagegen ERST beim Ausstellen — solange die
 * Schlussrechnung ein Entwurf ist, koennte sie noch verworfen werden, und die
 * Abschlaege muessten dann weiter als offen gelten.
 */
export const createFinalInvoice = async (
    input: {
        customerId: string;
        primaryProjectId: string;
        lines: InvoiceLine[];
        documentType?: 'FINAL_INVOICE' | 'REMAINING_INVOICE' | 'INVOICE';
        invoiceDate?: string;
        deliveryDateFrom?: string;
        deliveryDateTo?: string;
        /**
         * Kumulativ: die Positionen zeigen den GESAMTEN Leistungsstand, und
         * abgezogen wird alles, was vorherige Belege des Projekts bereits
         * gefordert haben — nicht nur Abschlagsrechnungen.
         */
        cumulative?: boolean;
        /**
         * Weitere Angaben fuer den Entwurf — heute der ausgewiesene
         * Leistungszeitraum (servicePeriodFrom/-To). Das Abrechnungsmodell
         * setzt diese Funktion selbst und laesst sich hier NICHT
         * ueberschreiben: es haengt an `cumulative` und an nichts sonst.
         */
        details?: Invoice['details'];
        /** Durchgereicht an den Entwurf — siehe CreateInvoiceDraftInput. */
        customerTerms?: CustomerTerms;
        /** Ebenso durchgereicht: die Vorbelegungen des Projekts. */
        lastDefaults?: LastUsedInvoiceDefaults | null;
        /**
         * TEILRECHNUNG: nur die Vorrechnungen und Alt-Abschlaege DIESES
         * Auftraggebers abziehen.
         *
         * Ausdruecklich vom Aufrufer zu setzen und NICHT aus `customerId`
         * abgeleitet: ohne Aufteilung wechselt der Auftraggeber eines Projekts
         * gelegentlich (Verkauf, Umfirmierung), und dort gehoeren die
         * Vorrechnungen des Vorgaengers weiterhin abgezogen. Eine stille
         * Ableitung liesse in genau dem Fall Geld liegen.
         */
        splitScope?: PartyScope;
        /** Teilrechnung: Gruppe, Teilnehmer und Anteil, durchgereicht an den Entwurf. */
        splitGroupId?: string;
        splitPartyId?: string;
        splitShareBp?: number;
    },
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<{
    invoice: Invoice;
    issues: ValidationMessage[];
    /** Wie viele Belege abzugsfaehig waren und wie viele abgezogen wurden. */
    advances: { found: number; deductible: number };
}> => {
    const documentType = input.documentType ?? 'FINAL_INVOICE';

    // Zwei Abzugsquellen, je nach Modell. Kumulativ zaehlt jeder frueher
    // ausgestellte Beleg; im Abschlagsmodell nur die Abschlagsrechnungen, und
    // die auch nur, solange sie nicht schon abgeloest sind.
    const advances = input.cumulative
        ? await fetchProjectInvoices(input.primaryProjectId, input.splitScope)
        : await fetchAdvancesForProject(input.primaryProjectId, input.splitScope);
    // Externe Alt-Abschlaege (vor der App geschrieben) NUR kumulativ: dort
    // bleibt jeder Vorbeleg stehen und wird von jeder Rechnung erneut
    // abgezogen. Im Abschlagsmodell wuerde ein Abzug den Altbeleg abloesen
    // muessen — abloesen laesst sich ein externer Eintrag aber nicht, und die
    // naechste Schlussrechnung zoege ihn ein zweites Mal ab.
    const externalAdvances = input.cumulative
        ? await fetchExternalAdvances(input.primaryProjectId, input.splitScope)
        : [];
    const deductions = input.cumulative
        ? [
              ...deductionsFromPriorInvoices(advances),
              ...deductionsFromExternalAdvances(externalAdvances),
          ].sort(
              // Gemeinsame Ordnung ueber beide Quellen — die Einzelaufstellung
              // auf dem Beleg folgt der zeitlichen Reihenfolge; Eintraege ohne
              // Datum ('') stehen vorn.
              (a, b) =>
                  a.invoiceDate.localeCompare(b.invoiceDate) ||
                  a.invoiceNumber.localeCompare(b.invoiceNumber) ||
                  b.taxRateBp - a.taxRateBp,
          )
        : deductionsFromAdvances(advances);

    // Geprueft wird gegen die Zeilen, die der Beleg TATSAECHLICH bekommt —
    // einschliesslich des Nebenkostenzuschlags. Die abgezogenen Vorbelege
    // enthalten ihren eigenen Zuschlag; misst man sie an einer Grundlage ohne
    // Zuschlag, uebersteigen sie die Leistung rechnerisch, obwohl der Beleg
    // aufgeht. Kumulativ, wo alle Vorbelege abgezogen werden, schlaegt das
    // nahezu jedes Mal an und blockiert einen einwandfreien Beleg.
    let seq = 0;
    const validationLines = applyAncillaryCosts(
        // Auch der Nachlass gehoert dazu, und in derselben Reihenfolge wie im
        // Anlagepfad: er mindert die Grundlage, an der die abgezogenen Vorbelege
        // gemessen werden. Fehlte er hier, uebersteigen die Abzuege die Leistung
        // rechnerisch, obwohl der fertige Beleg aufgeht.
        applyDocumentDiscount(
            recalculateLines(input.lines),
            documentDiscountOf(input.details),
            () => `validate-discount-${(seq += 1)}`,
        ),
        cfg.defaultAncillaryPercent,
        () => `validate-${(seq += 1)}`,
    );
    const issues = validateAdvanceChain(validationLines, deductions, documentType, {
        cumulative: input.cumulative,
    });

    // Die Einbehalte der abgezogenen Vorbelege — hier gebildet und dem Entwurf
    // ausdruecklich mitgegeben, damit updateInvoice die Vorbelege nicht ein
    // zweites Mal laedt. Der Backfill (Alt-Einbehalte, Nr. 107) kommt nur bei
    // Abzuegen dazu: ohne Abzuege ist die Liste ohnehin leer.
    const backfill = deductions.length
        ? await fetchRetentionBackfill(input.primaryProjectId)
        : [];
    const priorRetentions = priorRetentionsFromDeductions(deductions, advances, backfill);

    // Offene Sicherheitseinbehalte, die diese Schlussrechnung NICHT freigibt.
    //
    // Warnung und kein Blocker: es gibt gute Gruende, den Einbehalt stehen zu
    // lassen (Gewaehrleistungsfrist laeuft noch, Abnahme fehlt). Aber es ist die
    // letzte Gelegenheit — nach der Schlussrechnung kommt kein Beleg mehr, der
    // die Einbehalte von selbst mitnimmt, und das Geld bliebe unbemerkt liegen.
    if (documentType === 'FINAL_INVOICE' && input.details?.releaseRetention === false) {
        const openRetention = priorRetentionTotal(priorRetentions);
        if (openRetention > 0) {
            issues.push({
                code: 'retention_not_released',
                level: 'warning',
                message: `Offene Sicherheitseinbehalte von ${formatCents(openRetention)} bleiben mit dieser Schlussrechnung stehen (Freigabe ausgeschaltet).`,
            });
        }
    }

    if (hasBlockingErrors(issues)) throw new InvoiceValidationError(issues);

    const draft = await createInvoiceDraft(
        {
            customerId: input.customerId,
            documentType,
            primaryProjectId: input.primaryProjectId,
            invoiceDate: input.invoiceDate,
            deliveryDateFrom: input.deliveryDateFrom,
            deliveryDateTo: input.deliveryDateTo,
            lines: input.lines,
            customerTerms: input.customerTerms,
            lastDefaults: input.lastDefaults,
            splitGroupId: input.splitGroupId,
            splitPartyId: input.splitPartyId,
            splitShareBp: input.splitShareBp,
            // Am Beleg vermerken: der Ausstellpfad darf die abgezogenen Belege
            // bei kumulativer Abrechnung nicht abloesen. Das Modell steht dabei
            // NACH dem Spread — es folgt aus `cumulative` und darf von einem
            // mitgegebenen details-Objekt nicht verstellt werden.
            details: {
                ...(input.details ?? {}),
                ...(input.cumulative ? { billingMode: 'CUMULATIVE' as const } : {}),
            },
        },
        cfg,
        todayIso,
    );

    // lines MUSS mit: updateInvoice rechnet die Summen nur dann neu, wenn
    // Zeilen dabei sind. Ohne sie blieben prepaid_cents auf 0 und
    // payable_cents auf dem vollen Bruttobetrag stehen — die Abzuege staenden
    // zwar im Beleg, der Zahlbetrag zeigte sie aber bis zur Ausstellung nicht.
    // Freigebende saehen im Entwurf und in der Vorschau den ungeminderten
    // Betrag.
    const withDeductions = await updateInvoice(draft.id, {
        lines: draft.lines,
        advanceDeductions: deductions,
        advanceDeductionMode: cfg.advanceDeductionMode,
        priorRetentions,
    });

    for (const advanceId of new Set(deductions.map((d) => d.advanceInvoiceId))) {
        // Externe Alt-Abschlaege existieren nicht in invoices — die
        // DEDUCTS_ADVANCE-Verknuepfung hat dort kein Ziel (FK).
        if (isExternalAdvanceId(advanceId)) continue;
        await linkInvoices(withDeductions.id, advanceId, 'DEDUCTS_ADVANCE');
    }

    return {
        invoice: withDeductions,
        issues,
        // Damit der Aufrufer erklaeren kann, warum NICHTS abgezogen wurde.
        // Abzugsfaehig ist ein Abschlag erst ab dem Ausstellen; ein Entwurf
        // zaehlt nicht, denn er fordert noch kein Geld. Ohne diese Zahlen
        // entstuende stillschweigend ein Beleg ohne Abzugsblock, und der
        // Unterschied faellt erst beim Kunden auf.
        advances: {
            found: advances.length + externalAdvances.length,
            deductible: new Set(deductions.map((d) => d.advanceInvoiceId)).size,
        },
    };
};

/**
 * Loest die abgezogenen Abschlaege ab.
 *
 * Wird beim Ausstellen einer Schlussrechnung aufgerufen. Der Filter auf die
 * abzugsfaehigen Status ist tragend: ohne ihn wuerde ein zweiter Aufruf einen
 * bereits stornierten Abschlag ueberschreiben.
 */
/**
 * Gibt abgeloeste Abschlaege wieder frei — die Umkehrung von supersedeAdvances.
 *
 * Ein Storno hebt den Abzug auf. Bliebe der Abschlag "verbraucht", koennte ihn
 * keine spaetere Schlussrechnung mehr abziehen: der Kunde haette ihn bezahlt,
 * und der Betrag verschwaende aus jeder weiteren Abrechnung.
 *
 * Der vorherige Status ist nirgends festgehalten, laesst sich aber aus den
 * Zahlungen wiederherstellen — dieselbe Ableitung, die auch der
 * Zahlungseingang verwendet. Nur wirklich abgeloeste Belege werden angefasst;
 * ein stornierter Abschlag bleibt storniert.
 */
export const restoreAdvances = async (advanceInvoiceIds: string[]): Promise<void> => {
    if (!advanceInvoiceIds.length) return;
    const { data, error } = await supabase
        .from('invoices')
        .select('id, paid_cents, gross_total_cents')
        .in('id', advanceInvoiceIds)
        .eq('status', 'SUPERSEDED_BY_FINAL_INVOICE');
    if (error) throw error;

    for (const row of (data ?? []) as Array<{
        id: string;
        paid_cents: number | null;
        gross_total_cents: number | null;
    }>) {
        const paid = Number(row.paid_cents ?? 0);
        const gross = Number(row.gross_total_cents ?? 0);
        const status = paid > 0 && paid >= gross ? 'PAID' : paid > 0 ? 'PARTIALLY_PAID' : 'ISSUED';
        const { error: updErr } = await supabase
            .from('invoices')
            .update({ status })
            .eq('id', row.id)
            // Nochmals gefiltert: zwischen Lesen und Schreiben koennte der
            // Beleg storniert worden sein, und den wollen wir nicht beleben.
            .eq('status', 'SUPERSEDED_BY_FINAL_INVOICE');
        if (updErr) throw updErr;
    }
};

export const supersedeAdvances = async (advanceInvoiceIds: string[]): Promise<void> => {
    if (!advanceInvoiceIds.length) return;
    const { data, error } = await supabase
        .from('invoices')
        .update({ status: 'SUPERSEDED_BY_FINAL_INVOICE' })
        .in('id', advanceInvoiceIds)
        // Dieselbe Statusliste wie isDeductibleAdvance. Frueher fehlte hier
        // DISPUTED: ein abzugsfaehiger Abschlag in diesem Status wurde
        // abgezogen, blieb aber unveraendert stehen — und konnte danach von
        // einer weiteren Schlussrechnung ein zweites Mal abgezogen werden.
        .in('status', DEDUCTIBLE_ADVANCE_STATUSES)
        .select('id');
    if (error) throw error;

    // Trefferzahl pruefen. PostgREST meldet null getroffene Zeilen als Erfolg;
    // ohne diesen Vergleich haette der Aufrufer den Vorgang fuer vollstaendig
    // gehalten, obwohl kein einziger Abschlag abgeloest wurde.
    const done = new Set((data ?? []).map((r: { id: string }) => r.id));
    const missed = advanceInvoiceIds.filter((id) => !done.has(id));
    if (missed.length) {
        throw new Error(
            `${missed.length} von ${advanceInvoiceIds.length} Abschlägen konnten nicht abgelöst werden (${missed.join(', ')}). Sie gelten weiterhin als offen und könnten ein zweites Mal abgezogen werden.`,
        );
    }
};

/**
 * Nimmt die Abloesung zurueck, wenn die Schlussrechnung storniert wird.
 *
 * Ohne das blieben die Abschlaege fuer immer auf
 * SUPERSEDED_BY_FINAL_INVOICE stehen: sie waeren weder offen noch erneut
 * abrechenbar, und die dafuer geleisteten Zahlungen haetten keinen gueltigen
 * Beleg mehr. Der Zielstatus wird aus dem Zahlungsstand hergeleitet, weil der
 * urspruengliche Status nicht mitgefuehrt wird — bezahlt bleibt bezahlt.
 *
 * Nur Belege im Status SUPERSEDED_BY_FINAL_INVOICE werden angefasst. Ein
 * Abschlag, der zwischenzeitlich selbst storniert wurde, bleibt storniert.
 */
export const restoreSupersededAdvances = async (advanceInvoiceIds: string[]): Promise<number> => {
    if (!advanceInvoiceIds.length) return 0;

    const { data, error } = await supabase
        .from('invoices')
        .select('id, payable_cents, paid_cents, credited_cents')
        .in('id', advanceInvoiceIds)
        .eq('status', 'SUPERSEDED_BY_FINAL_INVOICE');
    if (error) throw error;

    let restored = 0;
    for (const row of (data ?? []) as Array<{
        id: string;
        payable_cents: number | string;
        paid_cents: number | string;
        credited_cents: number | string;
    }>) {
        const open =
            Number(row.payable_cents) - Number(row.paid_cents) - Number(row.credited_cents);
        const target: InvoiceStatus = open <= 0 ? 'PAID' : 'ISSUED';
        const { data: done, error: updErr } = await supabase
            .from('invoices')
            .update({ status: target })
            .eq('id', row.id)
            .eq('status', 'SUPERSEDED_BY_FINAL_INVOICE')
            .select('id');
        if (updErr) throw updErr;
        if (done?.length) restored += 1;
    }
    return restored;
};

/**
 * Belegart und Exportvermerk zu einer Menge von Rechnungs-ids.
 *
 * Gebaut fuer den Buchungsstapel: der Export muss je Abzug wissen, WAS dort
 * abgezogen wird. Eine Abschlagsrechnung ist auf dem Konto der erhaltenen
 * Anzahlungen gebucht und wird beim Abzug dagegen aufgeloest; eine gewoehnliche
 * Vorrechnung ist bereits als Erloes gebucht, ihr Abzug mindert die
 * Erloeszeile. Ohne diese Auskunft bucht der Stapel den vollen Gesamtstand, und
 * beim kumulativen Abrechnungsmodell entsteht bereits gebuchter Umsatz ein
 * zweites Mal.
 *
 * exported_at kommt mit, weil die Belegart allein nicht sagt, WO ein Abschlag
 * liegt: wurde er exportiert, bevor das Anzahlungskonto eingerichtet war, hat
 * der Export ihn auf das Erloeskonto gebucht, und sein Abzug muss die
 * Erloeszeile mindern (deductedIsAdvance in datevExport.ts).
 *
 * Geholt werden nur id, document_type und exported_at. Der Beleg selbst wird
 * nicht gebraucht, und ein SELECT nach dem Muster der uebrigen Lesefunktionen
 * zoege bei einem Quartalsexport zu jedem abgezogenen Beleg das ganze
 * Positions-jsonb mit.
 *
 * Eine unbekannte id fehlt in der Map und ist hier KEIN Fehler: was ein
 * fehlender Eintrag bedeutet, entscheidet der Aufrufer — der Exporter wirft,
 * eine Vorschau nicht.
 *
 * In Bloecken, und das ist kein Schoenheitsgrund: PostgREST liefert hoechstens
 * 1000 Zeilen und meldet das Kappen nicht (in diesem Betrieb schon einmal
 * aufgefallen), und der `in`-Filter steht in der laengenbegrenzten URL, wo er
 * ebenso still abgeschnitten wird. Beides kaeme hier als "Belegart unbekannt"
 * heraus: der Exporter wuerde fuer die abgeschnittenen Belege werfen, mitten in
 * einem Export, dessen Auswahl vollkommen in Ordnung ist — und niemand
 * verstuende, warum ausgerechnet dieser Zeitraum nicht geht.
 */
export const fetchDeductionSources = async (
    ids: string[],
): Promise<Map<string, DeductionSource>> => {
    const out = new Map<string, DeductionSource>();
    // Doppelte kosten sonst Platz im URL-Filter: derselbe Abschlag wird von
    // mehreren Belegen desselben Projekts abgezogen.
    const unique = [...new Set(ids)];
    if (!unique.length) return out;

    for (const block of chunked(unique, ID_CHUNK)) {
        const { data, error } = await supabase
            .from('invoices')
            .select('id, document_type, exported_at')
            .in('id', block);
        if (error) throw error;
        for (const row of (data ?? []) as Array<{
            id: string;
            document_type: DocumentType;
            exported_at: string | null;
        }>) {
            out.set(row.id, {
                documentType: row.document_type,
                exportedAt: row.exported_at ?? undefined,
            });
        }
    }
    return out;
};

/**
 * Ergebnis des Exportvermerks — welche Belege ihn TATSAECHLICH tragen.
 *
 * Die Aufteilung ist der Kern der Sache und darf nicht auf eine Zahl
 * eingedampft werden: der Dialog bleibt nach dem Export offen und muss seinen
 * eigenen Bestand nachziehen. Wuesste er nur "nicht alle", zeigte er auch die
 * vermerkten Belege weiter als "neu" und warnte beim zweiten Export desselben
 * Zeitraums nicht — der Umsatz wuerde in der Kanzlei doppelt gebucht.
 */
export interface MarkExportedResult {
    /** Belege, an denen exported_at gesetzt wurde. */
    marked: string[];
    /** Belege, die der Filter nicht getroffen hat (z.B. nicht ausgestellt). */
    missed: string[];
    /**
     * Meldung der Datenbank, falls ein Block gar nicht erst durchlief. Die
     * bereits vermerkten Belege stehen dann trotzdem in `marked` — sie sind
     * geschrieben, und diese Auskunft darf ein Fehler nicht verschlucken.
     */
    error?: string;
}

/**
 * Vermerkt, dass Belege an die Buchhaltung uebergeben wurden.
 *
 * exportedAt ist eines der wenigen Felder, die der Festschreibungs-Trigger nach
 * der Ausstellung noch zulaesst — es entsteht ja erst danach. Der Vermerk ist
 * kein Selbstzweck: er speist die Warnung vor einem zweiten Export desselben
 * Zeitraums, und ein doppelt eingespielter Buchungsstapel faellt in der Kanzlei
 * erst beim Kontenabgleich auf.
 *
 * Wirft NICHT bei einem Teilerfolg, sondern meldet ihn: geworfen ginge die
 * Information verloren, WELCHE Belege den Vermerk tragen — und genau die
 * braucht der Aufrufer, um seine Anzeige auf den tatsaechlichen Stand zu
 * bringen. Ein Teilerfolg ist trotzdem ein Fehlerfall und muss dort auch als
 * solcher gemeldet werden.
 */
export const markInvoicesExported = async (
    invoiceIds: string[],
    exportedAt: string,
): Promise<MarkExportedResult> => {
    if (!invoiceIds.length) return { marked: [], missed: [] };

    // In Bloecken: ein Monats- oder Quartalsexport umfasst regelmaessig weit
    // ueber hundert Belege, und alle IDs zusammen sprengen den URL-Filter. Der
    // abgeschnittene Filter traefe zu wenig, die Trefferzahlpruefung unten
    // wuerde werfen — aber erst, nachdem ein Teil bereits vermerkt ist und die
    // Exportdatei beim Nutzer liegt.
    const data: Array<{ id: string }> = [];
    let failure: string | undefined;
    for (const block of chunked(invoiceIds, ID_CHUNK)) {
        const res = await supabase
            .from('invoices')
            .update({ exported_at: exportedAt })
            .in('id', block)
            // Nicht ausgestellte Belege gehen nie in einen Buchungsstapel. Die
            // Bedingung steht hier UND im Dialog: der Dialog kann sich aendern
            // oder umgangen werden, diese Funktion ist die letzte Instanz vor
            // der Datenbank.
            .not('status', 'in', '(DRAFT,READY_FOR_REVIEW,APPROVED,ISSUING)')
            .select('id');
        if (res.error) {
            // Abbrechen, aber die Bloecke davor NICHT vergessen: sie sind
            // geschrieben. Weiterzumachen hiesse, denselben Fehler noch
            // mehrfach zu kassieren.
            failure = res.error.message ?? String(res.error);
            break;
        }
        data.push(...((res.data ?? []) as Array<{ id: string }>));
    }

    // Trefferzahl pruefen, wie bei supersedeAdvances und recordDunning.
    // PostgREST meldet null getroffene Zeilen als Erfolg — und genau der
    // Exportvermerk ist die einzige Sicherung dagegen, denselben Zeitraum ein
    // zweites Mal an die Buchhaltung zu geben. Bleibt er bei einem Teil der
    // Belege aus, warnt der naechste Export dort nicht, und der Umsatz
    // verdoppelt sich in der Kanzlei.
    const done = new Set(data.map((r: { id: string }) => r.id));
    return {
        marked: invoiceIds.filter((id) => done.has(id)),
        missed: invoiceIds.filter((id) => !done.has(id)),
        error: failure,
    };
};

/**
 * Die Kostenstelle des Ursprungsbelegs fuer Storno, Korrektur und Gutschrift.
 *
 * Bezeichnung UND Wert, und beides nur zusammen mit einem Wert: eine
 * Bezeichnung ohne Wert ergaebe auf dem abgeleiteten Beleg eine leere Zeile,
 * und ohne Bezeichnung faellt der Andruck ohnehin auf "Kostenstelle" zurueck
 * (invoiceLogic: costCenterLine).
 *
 * Einmal hier statt dreimal an den Aufrufstellen: dass die drei Belegarten die
 * Stelle des Ursprungsbelegs tragen, ist EINE Regel — der Empfaenger bucht die
 * Aufhebung, die Berichtigung und die Minderung dorthin, wo die Rechnung
 * steht.
 */
const copiedCostCenter = (
    original: Invoice,
): { costCenter?: string; costCenterLabel?: string } => {
    const value = original.details?.costCenter?.trim();
    if (!value) return {};
    const label = original.details?.costCenterLabel?.trim();
    return { costCenter: value, ...(label ? { costCenterLabel: label } : {}) };
};

/**
 * Der abweichende Projektname des Ursprungsbelegs fuer Storno, Korrektur und
 * Gutschrift.
 *
 * Dieselbe Regel wie bei der Kostenstelle und aus demselben Grund: hiess die
 * Baustelle auf der Rechnung "SuedLink Los 5 und 6", muss die Aufhebung
 * denselben Namen tragen. Ein abgeleiteter Beleg, der dieselbe Leistung unter
 * einem anderen Projektnamen fuehrt, sieht beim Empfaenger nach einem anderen
 * Vorgang aus — und genau deshalb greift bei diesen Belegarten auch keine
 * Vorbelegung aus fremden Belegen (invoiceLogic: wantsStickyProjectName).
 */
const copiedProjectName = (original: Invoice): { projectNameOverride?: string } => {
    const name = original.details?.projectNameOverride?.trim();
    return name ? { projectNameOverride: name } : {};
};

/**
 * Die abweichenden Kundenangaben des Ursprungsbelegs fuer Storno, Korrektur
 * und Gutschrift.
 *
 * Dieselbe Regel wie bei Kostenstelle und Projektname: die Aufhebung geht an
 * denselben Empfaenger wie der Beleg, den sie aufhebt. Ohne die Uebernahme
 * fiele der abgeleitete Beleg auf den Kundenstamm zurueck und schriebe eine
 * andere Anschrift an als die Rechnung, die er zuruecknimmt — beim Empfaenger
 * zwei Vorgaenge statt einem. Aus demselben Grund greift bei diesen Belegarten
 * auch keine Vorbelegung aus fremden Belegen
 * (invoiceLogic: wantsStickyCustomerOverride).
 */
const copiedCustomerOverride = (
    original: Pick<Invoice, 'details'>,
): { customerOverride?: CustomerOverride } => {
    const o = original.details?.customerOverride;
    return hasCustomerOverride(o) ? { customerOverride: o } : {};
};

/**
 * Der ausgewiesene Leistungszeitraum des Ursprungsbelegs fuer Storno,
 * Korrektur und Gutschrift.
 *
 * Dieselbe Regel wie bei Kostenstelle und Projektname: die Aufhebung hebt
 * genau diese Leistung auf und muss deshalb denselben Zeitraum nennen. Ohne
 * die Uebernahme fiele der abgeleitete Beleg auf deliveryDateFrom/-To zurueck
 * — kumulativ also auf die erste Leistung des Projekts — und nennte einen
 * weiteren Zeitraum als die Rechnung, die er zuruecknimmt.
 */
export const copiedServicePeriod = (
    original: Pick<Invoice, 'details'>,
): {
    servicePeriodFrom?: string;
    servicePeriodTo?: string;
    performedFrom?: string;
    performedTo?: string;
} => {
    const from = original.details?.servicePeriodFrom?.trim();
    const to = original.details?.servicePeriodTo?.trim();
    // Und der eingefrorene Leistungszeitraum: der abgeleitete Beleg hebt genau
    // diese Leistung auf und muss denselben Leistungszeitpunkt nennen. Neu
    // berechnen laesst er sich nicht — das Protokoll ist inzwischen
    // weitergelaufen, und die Aufhebung betrifft den Stand von damals.
    const performedFrom = original.details?.performedFrom?.trim();
    const performedTo = original.details?.performedTo?.trim();
    return {
        ...(from ? { servicePeriodFrom: from } : {}),
        ...(to ? { servicePeriodTo: to } : {}),
        ...(performedFrom ? { performedFrom } : {}),
        ...(performedTo ? { performedTo } : {}),
    };
};

export const linkInvoices = async (
    fromInvoiceId: string,
    toInvoiceId: string,
    relationType: InvoiceRelationType,
): Promise<void> => {
    const { error } = await supabase.from('invoice_relations').insert({
        from_invoice_id: fromInvoiceId,
        to_invoice_id: toInvoiceId,
        relation_type: relationType,
    });
    if (error) throw error;
};

/**
 * Storniert eine ausgestellte Rechnung.
 *
 * Erzeugt einen eigenen Beleg mit eigener Nummer und eigenem PDF. Das Original
 * bleibt unveraendert erhalten und wird auf CANCELLED gesetzt — unter GoBD wird
 * nicht geloescht, sondern storniert.
 */
export const cancelInvoice = async (
    original: Invoice,
    reason: string,
    customer: Customer,
    ctx: IssueContext,
    todayIso: string,
): Promise<Invoice> => {
    const issues = validateCancellation(original);
    if (hasBlockingErrors(issues)) throw new InvoiceValidationError(issues);
    if (!reason.trim()) {
        throw new Error('Ein Storno braucht einen Grund — er steht auf dem Beleg und im Protokoll.');
    }

    // Anspruch auf das Storno VOR dem Erzeugen des Belegs, ueber einen echten
    // Zustandswechsel. Frueher wurde der Ursprungsbeleg erst am Ende auf
    // CANCELLED gesetzt, ohne Statusbedingung: zwei gleichzeitige Stornos
    // haetten beide durchlaufen, zwei Stornobelege mit eigenen Nummern erzeugt
    // und den Leistungsstand zweimal gemindert — die abgerechnete Menge waere
    // unter null gefallen.
    const { data: claimed, error: claimErr } = await supabase
        .from('invoices')
        .update({ status: 'CANCELLED' })
        .eq('id', original.id)
        .in('status', CANCEL_CLAIMABLE)
        .select('id');
    if (claimErr) throw claimErr;
    if (!claimed?.length) {
        throw new Error(
            'Der Beleg wird bereits storniert oder ist es schon. Bitte die Rechnung neu laden.',
        );
    }

    let stornoInvoice: Invoice;
    // Ausserhalb des try, damit das catch den angefangenen Beleg wieder
    // wegraeumen kann — siehe dort.
    let stornoDraftId: string | undefined;
    try {
        const draft = await createInvoiceDraft(
            {
                customerId: original.customerId,
                // Teilnehmer und Anteil des Ursprungsbelegs, aber NICHT seine Gruppe:
                // der Gegenbeleg hebt eine Forderung auf, er ist keine. In der Gruppe
                // mitgezaehlt kaeme ihre Pruefsumme nicht mehr auf 100 %. Ohne den
                // Teilnehmer waere seine Ruecknahme dagegen nicht zuordenbar und
                // sperrte die naechste Teilrechnung des Abschnitts.
                splitPartyId: original.splitPartyId,
                splitShareBp: original.splitShareBp,
                documentType: 'CANCELLATION_INVOICE',
                primaryProjectId: original.primaryProjectId,
                invoiceDate: todayIso,
                deliveryDateFrom: original.deliveryDateFrom,
                deliveryDateTo: original.deliveryDateTo,
                lines: mirrorLines(original.lines, () => crypto.randomUUID()),
                details: {
                    reasonText: reason,
                    headerText: `Stornorechnung zu Rechnung ${original.invoiceNumber}`,
                    // Der Satz des Ursprungsbelegs, nicht der heutige Standard:
                    // ein Storno muss den Beleg exakt aufheben. Steht in den
                    // Einstellungen inzwischen ein anderer Satz oder gar
                    // keiner, blieben sonst Nebenkosten offen oder wuerden zu
                    // viel erstattet.
                    ancillaryPercent: original.details?.ancillaryPercent ?? 0,
                    // Und aus demselben Grund der Nachlass, hier BEIDE Formen:
                    // das Storno spiegelt den ganzen Beleg, also auch einen
                    // festen Nachlassbetrag in voller Hoehe. Ohne die Angabe
                    // verwuerfe applyDocumentDiscount die gespiegelten
                    // Nachlasszeilen ersatzlos und erstattete den Nachlass mit.
                    discountPercent: original.details?.discountPercent ?? 0,
                    discountAmountCents: original.details?.discountAmountCents ?? 0,
                    // Und aus demselben Grund der Sicherheitseinbehalt: der
                    // Stornobeleg muss den Einbehalt des Originals gespiegelt
                    // tragen, sonst bliebe er als Minderung stehen, obwohl die
                    // Forderung aufgehoben ist — Original und Storno gingen
                    // nicht auf null auf. Siehe copiedRetention.
                    ...copiedRetention('CANCELLATION_INVOICE', original),
                    // Auch der Projektvermerk kommt vom Ursprungsbeleg: das
                    // Storno hebt genau ihn auf und muss dasselbe Projekt
                    // ausweisen, auch wenn es inzwischen umbenannt wurde.
                    ...(original.details?.projects?.length
                        ? { projects: original.details.projects }
                        : {}),
                    // Ebenso die Kostenstelle: der Empfaenger hat den
                    // Ursprungsbeleg dort gebucht und muss die Aufhebung auf
                    // derselben Stelle gegenbuchen. Fehlt sie, sucht seine
                    // Buchhaltung von Hand.
                    //
                    // Der Zusatztext (subjectNote) kommt bewusst NICHT mit: er
                    // ist eine Mitteilung zu genau diesem Vorgang ("Rueckfragen
                    // an die Bauleitung"), kein Merkmal des Belegs.
                    //
                    // Bezeichnung UND Wert: der Ursprungsbeleg nannte die
                    // Angabe vielleicht "PSP-Element" — die Aufhebung, die
                    // stattdessen "Kostenstelle" sagt, sieht nach einer anderen
                    // Angabe aus.
                    ...copiedCostCenter(original),
                    // Und der abweichende Projektname — siehe copiedProjectName.
                    ...copiedProjectName(original),
                    // Der Andruck gedeckter Positionen ebenso: das Storno hebt
                    // den Beleg auf, wie der Kunde ihn gelesen hat — mit
                    // derselben Summenzeile statt zwoelf alter Begehungen.
                    ...(original.details?.flatRateDisplay
                        ? { flatRateDisplay: original.details.flatRateDisplay }
                        : {}),
                    // Und der ausgewiesene Leistungszeitraum — siehe copiedServicePeriod.
                    ...copiedServicePeriod(original),
                    // Und die abweichenden Kundenangaben — siehe copiedCustomerOverride.
                    ...copiedCustomerOverride(original),
                    // Und das Abrechnungsmodell. Ohne es galt der Storno eines
                    // kumulativen Belegs als Abschlagsbeleg: die Pruefung vor
                    // dem Ausstellen legte die Abschlagsregel an, hielt jede
                    // gewoehnliche Vorrechnung fuer nicht abzugsfaehig und
                    // liess sich gar nicht mehr ausstellen.
                    ...(original.details?.billingMode
                        ? { billingMode: original.details.billingMode }
                        : {}),
                    // Die Standardeinleitung passt auf einem Storno nicht
                    // ("fuer die erbrachten Leistungen berechnen wir wie
                    // folgt"). Leer heisst: keine Einleitung.
                    introText: '',
                },
            },
            ctx.config,
            todayIso,
        );

        // Die Abschlagsabzuege muessen mitgespiegelt werden. Ohne sie stuende
        // auf dem Storno der volle Bruttobetrag als Zahlbetrag, waehrend die
        // Ursprungsrechnung nur den um die Abschlaege geminderten Betrag
        // gefordert hat — das Storno erstattete dem Kunden den Abzugsbetrag ein
        // zweites Mal.
        stornoDraftId = draft.id;
        let stornoDraft = draft;
        // Dasselbe gilt fuer den Sicherheitseinbehalt: das Storno traegt die
        // GESPIEGELTE Vorbelegliste des Originals und dessen NEGIERTE
        // Einbehalt-Felder (mirrorRetentionTotals) — es rechnet nicht neu.
        // Hat die stornierte Schlussrechnung freigegeben, hat sie damit ihren
        // Zahlbetrag erhoeht; ohne die Spiegelung erstattete das Storno dem
        // Kunden die Freigabe ein zweites Mal. Und ein Original aus dem alten
        // inkrementellen Modell trug einen Einbehalt, den die kumulative
        // Formel auf den gespiegelten Zeilen nicht reproduziert — gespiegelt
        // geht das Paar trotzdem auf null auf.
        //
        // Ausdruecklich mitgegeben und nicht in updateInvoice gebildet: dort
        // bleibt beim Stornobeleg die gespeicherte Liste stehen, und die ist
        // nach createInvoiceDraft leer.
        const mirroredRetention = mirrorRetentionTotals(original.totals);
        const carriesRetention = Object.values(mirroredRetention).some((v) => v !== 0);
        if (
            original.advanceDeductions.length > 0 ||
            (original.priorRetentions?.length ?? 0) > 0 ||
            carriesRetention
        ) {
            const mirrored: AdvanceDeduction[] = mirrorDeductions(original.advanceDeductions);
            // lines MUSS mit, sonst rechnet updateInvoice die Summen nicht neu
            // (dieselbe Falle wie in createFinalInvoice).
            stornoDraft = await updateInvoice(
                draft.id,
                {
                    lines: draft.lines,
                    advanceDeductions: mirrored,
                    advanceDeductionMode: original.advanceDeductionMode,
                    priorRetentions: mirrorPriorRetentions(original.priorRetentions ?? []),
                },
                { mirroredRetention },
            );
        }

        await linkInvoices(stornoDraft.id, original.id, 'CANCELS');
        await approveInvoice(stornoDraft, ctx.actorId);
        // Die Buchungsreferenz des ORIGINALS mitgeben: nur so nimmt die
        // Rueckschreibung genau dessen Buchung zurueck. Bevorzugt aus dem
        // Rueckschreibprotokoll des Belegs — dort steht, was beim Ausstellen
        // tatsaechlich verwendet wurde. Fehlt es (Altbelege), wird sie nach
        // derselben Regel neu gebildet wie damals beim Ausstellen.
        const issued = await issueInvoice(stornoDraft.id, customer, {
            ...ctx,
            reverseOf:
                original.details?.writeBack?.[0]?.bookingReference ??
                bookingRefFor(original.id, original.documentType),
        });

        stornoInvoice = issued;
    } catch (err) {
        // Der angefangene Stornobeleg muss weg. Blieb er liegen, sammelte
        // jeder weitere Versuch einen weiteren Entwurf an: die Uebersicht
        // zeigte dann mehrere Stornos zu einer Rechnung, von denen keiner
        // gueltig ist — und jeder von ihnen reservierte ueber
        // v_invoice_line_reservations weiterhin Mengen.
        //
        // Zuerst zurueck auf DRAFT: der Loesch-Trigger laesst ausschliesslich
        // Entwuerfe entfernen (invoices_delete_guard), und approveInvoice ist
        // oben in aller Regel schon gelaufen. Ohne diesen Schritt scheiterte das
        // Aufraeumen IMMER und der Entwurf blieb doch liegen — genau das, was es
        // verhindern soll. Dieselbe Reihenfolge wie in createCommercialCredit.
        //
        // Der Fehlschlag des Zurueckstufens wird verschluckt, weil er der
        // Normalfall "war noch gar nicht freigegeben" sein kann. Auf DISPUTED
        // steht der Entwurf, wenn issueInvoice seine Rueckschreibung nicht
        // zuruecknehmen konnte — dorthin fuehrt kein Weg nach DRAFT, und das ist
        // richtig so: im Projekt stehen dann gebuchte Mengen ohne Beleg, der
        // Entwurf muss stehen bleiben. Das Loeschen scheitert dann sichtbar am
        // Trigger und wird protokolliert. Der urspruengliche Fehler bleibt in
        // beiden Faellen der geworfene — er ist der wichtigere.
        if (stornoDraftId) {
            try {
                await setStatus(stornoDraftId, 'APPROVED', 'DRAFT');
            } catch {
                /* war noch nicht freigegeben oder steht auf DISPUTED */
            }
            try {
                await deleteInvoice(stornoDraftId);
            } catch (cleanupErr) {
                console.error(
                    `Das Storno von ${original.invoiceNumber} ist gescheitert; der angelegte Stornoentwurf konnte nicht entfernt werden und bleibt stehen. Steht er auf DISPUTED, konnte die Rückschreibung ins Projekt nicht zurückgenommen werden und muss von Hand geklärt werden.`,
                    cleanupErr,
                );
            }
        }

        // Der Anspruch war voreilig: es ist kein Stornobeleg entstanden, also
        // darf der Ursprungsbeleg nicht als storniert stehenbleiben. Die
        // Bedingung auf CANCELLED verhindert, dass ein zwischenzeitlich
        // erfolgreicher Vorgang ueberschrieben wird.
        const { error: revertErr } = await supabase
            .from('invoices')
            .update({ status: original.status })
            .eq('id', original.id)
            .eq('status', 'CANCELLED');
        if (revertErr) {
            console.error(
                `Das Storno von ${original.invoiceNumber} ist gescheitert, der Beleg konnte aber nicht auf ${original.status} zurückgesetzt werden. Er steht fälschlich auf CANCELLED.`,
                revertErr,
            );
        }
        throw err;
    }

    // AUSSERHALB des try: ab hier ist der Stornobeleg ausgestellt und
    // festgeschrieben. Ein Fehler darf den Ursprungsbeleg jetzt nicht mehr auf
    // seinen alten Status zurueckdrehen — es gaebe sonst einen gueltigen
    // Stornobeleg zu einer Rechnung, die sich fuer nicht storniert haelt.
    //
    // Die abgeloesten Abschlaege leben wieder auf: sie sind nicht mehr durch
    // eine Schlussrechnung ersetzt, denn die ist storniert. Ohne diesen Schritt
    // blieben sie fuer immer auf SUPERSEDED_BY_FINAL_INVOICE stehen — der
    // Zustandsautomat kennt von dort nur noch den Weg nach CANCELLED.
    const advanceIds = [
        ...new Set(original.advanceDeductions.map((d) => d.advanceInvoiceId)),
        // Externe Alt-Abschlaege (ext:…) sind nie abgeloest worden — es gibt
        // nichts zu reaktivieren, und ihre Id ist keine uuid.
    ].filter((id) => Boolean(id) && !isExternalAdvanceId(id));
    if (advanceIds.length) {
        try {
            await restoreSupersededAdvances(advanceIds);
        } catch (restoreErr) {
            console.error(
                'Abschläge konnten nach dem Storno nicht reaktiviert werden.',
                restoreErr,
            );
            throw new Error(
                `Der Stornobeleg ${stornoInvoice.invoiceNumber ?? ''} wurde ausgestellt und ist gültig. Die abgelösten Abschlagsrechnungen (${advanceIds.join(', ')}) konnten aber nicht wieder aktiviert werden: sie stehen weiterhin auf "Durch Schlussrechnung abgelöst" und müssen von Hand zurückgesetzt werden.`,
            );
        }
    }

    return stornoInvoice;
};

/** Status, aus denen heraus ein Storno beansprucht werden darf. */
const CANCEL_CLAIMABLE = [
    'ISSUED',
    'SENT',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'DISPUTED',
    'PARTIALLY_CREDITED',
];

// ─── Korrektur und Gutschriften ─────────────────────────────────────────────

/**
 * Korrekturrechnung: ein eigener Beleg ueber die DIFFERENZ.
 *
 * Der Ursprungsbeleg bleibt bestehen und behaelt seine Nummer. Erst beide
 * zusammen ergeben den richtigen Betrag — das ist der pruefbare Zustand und
 * der Grund, warum unter GoBD nicht ueberschrieben wird.
 */
export const correctInvoice = async (
    original: Invoice,
    correctedLines: InvoiceLine[],
    reason: string,
    customer: Customer,
    ctx: IssueContext,
    todayIso: string,
): Promise<Invoice> => {
    const diff = buildCorrectionLines(original.lines, correctedLines, () => crypto.randomUUID());
    const issues = validateCorrection(original, diff);
    if (hasBlockingErrors(issues)) throw new InvoiceValidationError(issues);
    if (!reason.trim()) throw new Error('Eine Korrektur braucht einen Grund.');

    const draft = await createInvoiceDraft(
        {
            customerId: original.customerId,
            // Teilnehmer und Anteil des Ursprungsbelegs, aber NICHT seine Gruppe:
            // der Gegenbeleg hebt eine Forderung auf, er ist keine. In der Gruppe
            // mitgezaehlt kaeme ihre Pruefsumme nicht mehr auf 100 %. Ohne den
            // Teilnehmer waere seine Ruecknahme dagegen nicht zuordenbar und
            // sperrte die naechste Teilrechnung des Abschnitts.
            splitPartyId: original.splitPartyId,
            splitShareBp: original.splitShareBp,
            documentType: 'CORRECTION_INVOICE',
            primaryProjectId: original.primaryProjectId,
            invoiceDate: todayIso,
            deliveryDateFrom: original.deliveryDateFrom,
            deliveryDateTo: original.deliveryDateTo,
            lines: diff,
            details: {
                reasonText: reason,
                headerText: `Korrekturrechnung zu Rechnung ${original.invoiceNumber}`,
                // Satz des Ursprungsbelegs: die Korrektur rechnet dessen
                // Differenz, und die traegt dieselben Nebenkosten. Mit dem
                // heutigen Standard ergaeben Ursprung plus Korrektur nicht den
                // berichtigten Gesamtbetrag.
                ancillaryPercent: original.details?.ancillaryPercent ?? 0,
                // Beim Nachlass gilt das NUR fuer den Prozentsatz: er wirkt auf
                // jede Bemessungsgrundlage und damit auch auf die Differenz.
                // Ein FESTER Nachlassbetrag ist bereits auf dem Ursprungsbeleg
                // vollstaendig abgezogen — auf der Differenz noch einmal
                // abgezogen, minderte er die Forderung ein zweites Mal.
                discountPercent: original.details?.discountPercent ?? 0,
                discountAmountCents: 0,
                // Beim Sicherheitseinbehalt gilt dasselbe wie beim
                // Nachlass-PROZENTSATZ: er wirkt auf jede Grundlage und damit
                // auch auf die Differenz. Ursprung plus Korrektur tragen danach
                // genau den Einbehalt, den der berichtigte Gesamtbetrag ergibt
                // — bei einer Korrektur nach unten vorzeichenrichtig negativ
                // (invoiceTotalsWithRetention). Der Satz ist der EFFEKTIVE des
                // Originals: eine freigebende Schlussrechnung hat mit 0
                // gerechnet, und ihre Korrektur haelt auf der Differenz nichts
                // neu ein.
                //
                // Die VORBELEGLISTE kommt bewusst NICHT mit: die Korrektur
                // traegt keine Abzuege, also prior 0. Sie nimmt den
                // Ursprungsbeleg nicht zurueck — er bleibt gueltig, und mit ihm
                // sein Einbehalt oder seine Freigabe. Nur das Storno spiegelt
                // die Liste.
                ...copiedRetention('CORRECTION_INVOICE', original),
                // Projektvermerk des Ursprungsbelegs — die Korrektur gehoert
                // zum selben Projekt, gleich wie es heute heisst.
                ...(original.details?.projects?.length
                    ? { projects: original.details.projects }
                    : {}),
                // Kostenstelle ebenso, Bezeichnung samt Wert: die Korrektur
                // wird beim Empfaenger auf dieselbe Stelle gebucht wie der
                // Beleg, den sie berichtigt. Der Zusatztext bleibt aussen vor —
                // er gehoerte zu jenem Vorgang, nicht zu dieser Berichtigung.
                ...copiedCostCenter(original),
                // Und der abweichende Projektname — siehe copiedProjectName.
                ...copiedProjectName(original),
                // Und der ausgewiesene Leistungszeitraum — siehe copiedServicePeriod.
                ...copiedServicePeriod(original),
                // Und die abweichenden Kundenangaben — siehe copiedCustomerOverride.
                ...copiedCustomerOverride(original),
                // billingMode wird BEWUSST nicht uebernommen — anders als beim
                // Storno (siehe cancelInvoice).
                //
                // Der Storno spiegelt die Zeilen des Originals: sie nennen
                // kumulativ weiterhin den Gesamtstand, also muss auch sein Beleg
                // kumulativ gelesen werden. Die Korrektur traegt dagegen
                // DIFFERENZEN, und eine Differenz ist immer fortschreibend.
                //
                // Kumulativ gelesen bucht planWriteBack nur
                // max(0, Zeilenmenge - bereits abgerechnet): eine
                // Aufwaertskorrektur um 2 auf einen Stand von 20 buchte damit 0,
                // und die zusaetzliche Menge waere berechnet, im Projekt aber nie
                // abgerechnet. Ebenso ordnete planAllocations ihr nichts zu.
                // Das Fehlen ist hier also die richtige Angabe, kein Versaeumnis.
            },
        },
        ctx.config,
        todayIso,
    );

    await linkInvoices(draft.id, original.id, 'CORRECTS');
    await approveInvoice(draft, ctx.actorId);
    return issueInvoice(draft.id, customer, ctx);
};

/**
 * Kaufmaennische Gutschrift: wir mindern nachtraeglich unsere eigene Forderung.
 *
 * Nicht zu verwechseln mit der Gutschrift im umsatzsteuerlichen Sinn (§14
 * Abs. 2 Satz 2 UStG), bei der der Leistungsempfaenger ueber UNSERE Leistung
 * abrechnet — dort ist der Umsatz positiv. Die beiden zu vertauschen dreht das
 * Vorzeichen in Buchhaltung und Umsatzsteuervoranmeldung.
 */
export const createCommercialCredit = async (
    original: Invoice,
    lines: InvoiceLine[],
    reason: string,
    customer: Customer,
    ctx: IssueContext,
    todayIso: string,
): Promise<Invoice> => {
    if (!reason.trim()) throw new Error('Eine Gutschrift braucht einen Grund.');
    if (!lines.length) throw new Error('Eine Gutschrift ohne Positionen ergibt keinen Beleg.');
    // Ein vollstaendig gutgeschriebener Beleg hat nichts mehr herzugeben. Ohne
    // diese Schranke entstuende eine zweite gueltige Gutschrift, und erst der
    // Statuswechsel am Ende schluege fehl — der Beleg waere dann schon in der
    // Welt.
    if (original.status === 'CREDITED') {
        throw new Error(
            `${original.invoiceNumber} ist bereits vollständig gutgeschrieben. Eine weitere Gutschrift würde den Betrag ein zweites Mal erstatten.`,
        );
    }

    // Die Betraege werden hier gespiegelt, damit der Aufrufer die zu
    // erstattenden Positionen POSITIV uebergeben kann — so, wie ein Mensch sie
    // denkt. Das Vorzeichen ist eine Eigenschaft der Belegart, keine der
    // Eingabe.
    //
    // buildCreditLines und NICHT mirrorLines: die Menge darf nur mit, wenn die
    // Position GANZ erstattet wird. mirrorLines spiegelte sie immer, und eine
    // Teilgutschrift gab damit die volle Position ins Projekt zurueck — die
    // Leistung stand wieder offen, obwohl der Rest zu Recht abgerechnet war.
    // Deshalb braucht die Funktion den Ursprungsbeleg: nur er sagt, was „ganz"
    // heisst.
    const credited = buildCreditLines(original.lines, lines, () => crypto.randomUUID());

    const draft = await createInvoiceDraft(
        {
            customerId: original.customerId,
            // Teilnehmer und Anteil des Ursprungsbelegs, aber NICHT seine Gruppe:
            // der Gegenbeleg hebt eine Forderung auf, er ist keine. In der Gruppe
            // mitgezaehlt kaeme ihre Pruefsumme nicht mehr auf 100 %. Ohne den
            // Teilnehmer waere seine Ruecknahme dagegen nicht zuordenbar und
            // sperrte die naechste Teilrechnung des Abschnitts.
            splitPartyId: original.splitPartyId,
            splitShareBp: original.splitShareBp,
            documentType: 'COMMERCIAL_CREDIT_INVOICE',
            primaryProjectId: original.primaryProjectId,
            invoiceDate: todayIso,
            // Leistungszeitraum vom Ursprungsbeleg uebernehmen. Er ist
            // Pflichtangabe (§14 Abs. 4 Nr. 6 UStG), und eine Gutschrift
            // bezieht sich auf dieselbe Leistung wie die Rechnung, die sie
            // mindert — ein eigener Zeitraum waere eine andere Aussage.
            //
            // Ohne das scheiterte das Ausstellen unten an der Validierung,
            // waehrend der Entwurf schon angelegt war: jeder Klick auf
            // "Gutschrift" hinterliess einen weiteren Entwurf in der Liste.
            deliveryDateFrom: original.deliveryDateFrom,
            deliveryDateTo: original.deliveryDateTo ?? original.invoiceDate,
            lines: credited,
            details: {
                reasonText: reason,
                headerText: `Gutschrift zu Rechnung ${original.invoiceNumber}`,
                // Satz des Ursprungsbelegs — die Gutschrift mindert genau
                // dessen Forderung, Nebenkosten eingeschlossen.
                ancillaryPercent: original.details?.ancillaryPercent ?? 0,
                // Der Nachlass wie bei der Korrektur nur als Prozentsatz: die
                // Gutschrift erstattet einen TEIL der Leistung, und ein fester
                // Nachlassbetrag gehoert zum ganzen Beleg. Auf dem Teil noch
                // einmal abgezogen, erstattete sie zu wenig.
                discountPercent: original.details?.discountPercent ?? 0,
                discountAmountCents: 0,
                // Der Sicherheitseinbehalt kommt ausdruecklich NICHT mit
                // (Plan §5.4: "Gutschrift traegt 0"). Sie ist eine
                // nachtraegliche Preisminderung und keine Ruecknahme des
                // Belegs: der Einbehalt des Ursprungsbelegs bleibt bestehen und
                // wird mit der Schlussrechnung ausgezahlt wie vereinbart. Die
                // ausdrueckliche Null steht hier, damit keine spaetere
                // Vorbelegung sie nachtraeglich fuellt.
                ...copiedRetention('COMMERCIAL_CREDIT_INVOICE', original),
                // Projektvermerk des Ursprungsbelegs — dieselbe Begruendung.
                ...(original.details?.projects?.length
                    ? { projects: original.details.projects }
                    : {}),
                // Kostenstelle ebenso, Bezeichnung samt Wert — die Gutschrift
                // mindert eine Forderung, die beim Empfaenger auf dieser Stelle
                // steht. Der Zusatztext bleibt aussen vor (dokumentspezifisch).
                ...copiedCostCenter(original),
                // Und der abweichende Projektname — siehe copiedProjectName.
                ...copiedProjectName(original),
                // Und der ausgewiesene Leistungszeitraum — siehe copiedServicePeriod.
                ...copiedServicePeriod(original),
                // Und die abweichenden Kundenangaben — siehe copiedCustomerOverride.
                ...copiedCustomerOverride(original),
            },
        },
        ctx.config,
        todayIso,
    );

    let issued: Invoice;
    try {
        await linkInvoices(draft.id, original.id, 'CREDITS');
        await approveInvoice(draft, ctx.actorId);
        issued = await issueInvoice(draft.id, customer, ctx);
    } catch (e) {
        // Aufraeumen, statt einen halben Beleg stehen zu lassen. Scheitert das
        // Ausstellen, ist der Entwurf wertlos — er traegt keine Nummer, kein
        // PDF und keinen Archiveintrag, steht aber in jeder Liste und laedt
        // dazu ein, es einfach noch einmal zu versuchen.
        //
        // Best effort: gelingt das Aufraeumen nicht, zaehlt der urspruengliche
        // Fehler. Ihn durch einen Aufraeumfehler zu ersetzen verstellt den Blick
        // auf die eigentliche Ursache.
        try {
            await setStatus(draft.id, 'APPROVED', 'DRAFT');
        } catch {
            /* war noch gar nicht freigegeben */
        }
        await deleteInvoice(draft.id).catch(() => undefined);
        throw e;
    }

    // Der Ursprungsbeleg bleibt bestehen, sein Status vermerkt aber die
    // Gutschrift. Vollstaendig gutgeschrieben heisst CREDITED.
    const creditedGross = Math.abs(issued.totals.grossTotalCents);
    const fully = creditedGross >= original.totals.payableCents;
    const { data: advanced, error } = await supabase
        .from('invoices')
        .update({ status: fully ? 'CREDITED' : 'PARTIALLY_CREDITED' })
        .eq('id', original.id)
        .in('status', ['ISSUED', 'SENT', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'PARTIALLY_CREDITED'])
        .select('id');
    // Trefferzahl UND Fehler pruefen. Steht der Ursprungsbeleg inzwischen auf
    // CANCELLED oder DISPUTED, trifft der Filter null Zeilen — und PostgREST
    // meldet das als Erfolg, der Fehlerzweig lief also nie. Die Gutschrift war
    // ausgestellt und gueltig, der Ursprungsbeleg trug aber weiter die volle
    // offene Forderung: das Mahnwesen mahnte einen bereits gutgeschriebenen
    // Betrag, und die Offene-Posten-Liste war falsch.
    if (error || !advanced?.length) {
        console.error(
            `Die Gutschrift ${issued.invoiceNumber} wurde ausgestellt, der Status von ${original.invoiceNumber} konnte aber nicht fortgeschrieben werden.`,
            error,
        );
        throw new Error(
            `Die Gutschrift ${issued.invoiceNumber} wurde ausgestellt und ist gültig. Der Status von ${original.invoiceNumber} konnte aber nicht auf "${fully ? 'gutgeschrieben' : 'teilweise gutgeschrieben'}" gesetzt werden — der Beleg steht dort weiterhin mit der vollen offenen Forderung und muss von Hand nachgezogen werden.`,
        );
    }

    return issued;
};

/**
 * Gutschrift im umsatzsteuerlichen Sinn — der Kunde rechnet ueber unsere
 * Leistung ab (§14 Abs. 2 Satz 2 UStG).
 *
 * Der Umsatz ist POSITIV; der Beleg unterscheidet sich von einer Rechnung nur
 * darin, wer ihn ausstellt. Eine Vereinbarung darueber muss vorliegen — sie
 * gehoert in den Belegtext, nicht in eine Voreinstellung.
 */
export const createSelfBillingCreditNote = async (
    input: {
        customerId: string;
        primaryProjectId?: string;
        lines: InvoiceLine[];
        agreementText: string;
        deliveryDateFrom?: string;
        deliveryDateTo?: string;
    },
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<Invoice> => {
    if (!input.agreementText.trim()) {
        throw new Error(
            'Eine Gutschrift im umsatzsteuerlichen Sinn setzt eine Vereinbarung mit dem Leistungsempfänger voraus. Sie gehört auf den Beleg.',
        );
    }
    return createInvoiceDraft(
        {
            customerId: input.customerId,
            documentType: 'SELF_BILLING_CREDIT_NOTE',
            primaryProjectId: input.primaryProjectId,
            invoiceDate: todayIso,
            deliveryDateFrom: input.deliveryDateFrom,
            deliveryDateTo: input.deliveryDateTo,
            lines: input.lines,
            details: { reasonText: input.agreementText, headerText: 'Gutschrift' },
        },
        cfg,
        todayIso,
    );
};

// ─── Archiv ─────────────────────────────────────────────────────────────────

/**
 * Prueft, ob die archivierte PDF noch dem gespeicherten Hash entspricht.
 *
 * Der Hash beweist von sich aus nichts — er macht eine Veraenderung nur
 * feststellbar. Zusammen mit dem Append-only-Audit-Log und dem Rechtekonzept
 * ergibt das die Beweiskette.
 */
export const verifySnapshotHash = async (
    snapshotId: string,
): Promise<{ ok: boolean; expected: string; actual: string }> => {
    const { data: snap, error } = await supabase
        .from('invoice_snapshots')
        .select('pdf_path, pdf_sha256')
        .eq('id', snapshotId)
        .single();
    if (error) throw error;

    const { data: file, error: dlErr } = await supabase.storage.from(BUCKET).download(snap.pdf_path);
    if (dlErr) throw dlErr;

    const actual = await sha256Hex(await file.arrayBuffer());
    return { ok: actual === snap.pdf_sha256, expected: snap.pdf_sha256, actual };
};

export const getInvoicePdfUrl = async (path: string, expiresIn = 3600): Promise<string> => {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresIn);
    if (error) throw error;
    return data.signedUrl;
};
